import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import {
  AreaChart, Area, BarChart, Bar, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from "recharts";

const SESSIONS = ["Asia", "London", "NY AM", "NY PM"];
const EMOTIONS = ["Confident", "Neutral", "Fear", "FOMO", "Revenge", "Disciplined", "Anxious", "Excited"];
const ASSETS = ["MNQ", "NQ", "ES", "XAUUSD", "MGC", "CrudeOil", "Silver", "EUR/USD", "GBP/USD", "USD/JPY", "AUD/USD", "USD/CAD", "EUR/GBP", "NZD/USD", "USD/CHF", "BTC/USD", "ETH/USD"];
const MISTAKE_TAGS = ["Overtrading", "Early Exit", "No Stop Loss", "Revenge Trade", "FOMO Entry", "Sized Too Big", "Sized Too Low", "Missed Entry", "Moved Stop", "Chased Entry", "Ignored Rules", "Bad Timing", "Over Leveraged"];

function useColors() {
  const [light, setLight] = useState(() => document.body.classList.contains("light"));
  useEffect(() => {
    const obs = new MutationObserver(() => setLight(document.body.classList.contains("light")));
    obs.observe(document.body, { attributes: true, attributeFilter: ["class"] });
    return () => obs.disconnect();
  }, []);
  return light ? {
    bg: "#f1f5f9", card: "#ffffff", border: "#e2e8f0", text: "#111827",
    subtext: "#6b7280", tableBg: "#f8fafc", rowHover: "#f1f5f9",
    inputBg: "#f1f5f9", chartGrid: "rgba(0,0,0,0.06)", chartAxis: "#94a3b8",
    insetBg: "#f8fafc", tagBg: "#f1f5f9", isLight: true,
  } : {
    bg: "#0A0B0E", card: "#111217", border: "#1E2028", text: "#ffffff",
    subtext: "#4B5563", tableBg: "#0A0B0E", rowHover: "#14151A",
    inputBg: "#0A0B0E", chartGrid: "rgba(255,255,255,.04)", chartAxis: "#4B5563",
    insetBg: "#0A0B0E", tagBg: "#0A0B0E", isLight: false,
  };
}

const IC = {
  dashboard: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7" rx="1.5"/><rect x="14" y="3" width="7" height="7" rx="1.5"/><rect x="3" y="14" width="7" height="7" rx="1.5"/><rect x="14" y="14" width="7" height="7" rx="1.5"/></svg>,
  trades: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>,
  analytics: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>,
  calendar: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>,
  psychology: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z"/><path d="M12 16v-4"/><path d="M12 8h.01"/><circle cx="12" cy="12" r="3"/></svg>,
  insights: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l.548 2.194a.5.5 0 01-.485.626H9.899a.5.5 0 01-.485-.626l.548-2.194z"/></svg>,
  calendar2: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/><circle cx="12" cy="15" r="2"/></svg>,
  menu: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="15" y2="12"/><line x1="3" y1="18" x2="18" y2="18"/></svg>,
  rules: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/></svg>,
};

function StreakWidget({ trades, dayWR, C }) {
  const [face, setFace] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => setFace(f => 1 - f), 3500);
    return () => clearInterval(timer);
  }, []);
  const streakData = useMemo(() => {
    const sorted = [...trades].sort((a, b) => new Date(a.date) - new Date(b.date));
    let cur = 0, curType = null, best = 0;
    sorted.forEach(t => {
      if (t.isBE) return;
      const type = t.win ? 'W' : 'L';
      if (type === curType) cur++;
      else { cur = 1; curType = type; }
      if (type === 'W' && cur > best) best = cur;
    });
    return { current: cur, isWin: curType === 'W', currentType: curType, best };
  }, [trades]);
  const wr = (dayWR * 100).toFixed(1);
  const wrColor = dayWR === 0 ? C.subtext : dayWR >= 0.5 ? "#22C55E" : "#EF4444";
  const streakColor = streakData.isWin ? "#22C55E" : "#EF4444";
  const fireIntensity = streakData.isWin && streakData.current >= 3;
  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 68 }}>
      <style>{`
        @keyframes swIn { from { opacity:0; transform:translateY(6px); } to { opacity:1; transform:translateY(0); } }
        @keyframes fireFlicker { 0%,100% { transform:scaleY(1) rotate(-2deg); } 50% { transform:scaleY(1.1) rotate(2deg); } }
        .sw-face { animation: swIn 0.28s ease both; }
        .fire-anim { display:inline-block; animation:fireFlicker 0.7s ease-in-out infinite; transform-origin:bottom center; }
      `}</style>
      <div style={{ fontSize: 10, fontWeight: 500, color: C.subtext, textTransform: "uppercase", letterSpacing: ".08em", marginBottom: 5 }}>
        {face === 0 ? "Day Win %" : "Win Streak"}
      </div>
      <div className="sw-face" key={face} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
        {face === 0 ? (
          <>
            <div style={{ fontSize: 20, fontWeight: 700, color: wrColor, lineHeight: 1 }}>{wr}%</div>
            <div style={{ display: "flex", alignItems: "flex-end", gap: 2 }}>
              {[...Array(5)].map((_, i) => (
                <div key={i} style={{ width: 4, height: 10 + i * 4, borderRadius: 2, background: i / 5 < dayWR ? wrColor : C.border, opacity: i / 5 < dayWR ? 1 : 0.35 }} />
              ))}
            </div>
          </>
        ) : (
          <>
            <div style={{ fontSize: 20, fontWeight: 700, color: streakData.currentType ? streakColor : C.subtext, lineHeight: 1 }}>
              {streakData.currentType ? `${streakData.isWin ? "+" : "-"}${streakData.current}` : "—"}
            </div>
            {fireIntensity
              ? <span className="fire-anim" style={{ fontSize: 18 }}>🔥</span>
              : streakData.currentType && <span style={{ fontSize: 14 }}>{streakData.isWin ? "⚡" : "❄️"}</span>
            }
            <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <div style={{ fontSize: 9, color: C.subtext }}>Best: <span style={{ color: "#22C55E", fontWeight: 700 }}>{streakData.best}W</span></div>
              <div style={{ fontSize: 9, color: C.subtext }}>{streakData.isWin ? "Win streak" : "Loss streak"}</div>
            </div>
          </>
        )}
      </div>
      <div style={{ display: "flex", gap: 4 }}>
        {[0, 1].map(i => (
          <div key={i} onClick={() => setFace(i)} style={{ width: 6, height: 6, borderRadius: "50%", background: face === i ? "#22C55E" : C.border, cursor: "pointer", transition: "background 0.2s" }} />
        ))}
      </div>
    </div>
  );
}

function GradeRating({ value = 0, onChange, size = 14 }) {
  const g = ["C-", "C", "B", "A", "A+"];
  const c = ["#EF4444", "#F59E0B", "#3B82F6", "#22C55E", "#A78BFA"];
  return (
    <div style={{ display: "flex", gap: 3 }}>
      {g.map((gr, i) => {
        const idx = i + 1, on = idx <= value;
        return <span key={gr} onClick={() => onChange?.(idx === value ? 0 : idx)}
          style={{ fontSize: size, fontWeight: 700, cursor: onChange ? "pointer" : "default", color: on ? c[i] : "#9CA3AF", background: on ? c[i] + "18" : "transparent", border: `1px solid ${on ? c[i] + "40" : "transparent"}`, borderRadius: 4, padding: "1px 5px", userSelect: "none", lineHeight: 1.3 }}>{gr}</span>;
      })}
    </div>
  );
}

function StarRating({ value = 0, onChange, size = 16 }) {
  const [h, setH] = useState(0);
  return (
    <div style={{ display: "flex", gap: 1 }}>
      {[1, 2, 3, 4, 5].map(s => (
        <span key={s} onClick={() => onChange && onChange(s === value ? 0 : s)}
          onMouseEnter={() => onChange && setH(s)} onMouseLeave={() => setH(0)}
          style={{ fontSize: size, cursor: onChange ? "pointer" : "default", color: s <= (h || value) ? "#F59E0B" : "#9CA3AF", transition: "color .1s", userSelect: "none" }}>★</span>
      ))}
    </div>
  );
}

function RatingWidget({ value = 0, onChange, type = "stars", size = 16 }) {
  if (type === "grades") return <GradeRating value={value} onChange={onChange} size={size} />;
  return <StarRating value={value} onChange={onChange} size={size} />;
}

const RATING_LABELS = { stars: ["Not rated", "Poor", "Below avg", "Average", "Great", "Perfect"], grades: ["Not rated", "C-", "C", "B", "A", "A+"] };

function ImageLightbox({ images, startIdx = 0, onClose }) {
  const [idx, setIdx] = useState(startIdx);
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.93)", zIndex: 500, display: "flex", alignItems: "center", justifyContent: "center", backdropFilter: "blur(10px)" }} onClick={onClose}>
      <div style={{ position: "relative", maxWidth: "92vw", maxHeight: "92vh" }} onClick={e => e.stopPropagation()}>
        <img src={images[idx]} alt="" style={{ maxWidth: "90vw", maxHeight: "85vh", borderRadius: 12, objectFit: "contain", display: "block" }} />
        <button onClick={onClose} style={{ position: "absolute", top: -12, right: -12, background: "#111217", border: "1px solid #1E2028", borderRadius: "50%", width: 28, height: 28, color: "#fff", fontSize: 12, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>✕</button>
        {images.length > 1 && (
          <>
            <button onClick={() => setIdx(p => (p - 1 + images.length) % images.length)} style={{ position: "absolute", left: -18, top: "50%", transform: "translateY(-50%)", background: "#111217ee", border: "1px solid #1E2028", borderRadius: "50%", width: 34, height: 34, color: "#fff", fontSize: 16, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>‹</button>
            <button onClick={() => setIdx(p => (p + 1) % images.length)} style={{ position: "absolute", right: -18, top: "50%", transform: "translateY(-50%)", background: "#111217ee", border: "1px solid #1E2028", borderRadius: "50%", width: 34, height: 34, color: "#fff", fontSize: 16, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>›</button>
            <div style={{ textAlign: "center", marginTop: 10, fontSize: 12, color: "#6B7280" }}>{idx + 1} / {images.length}</div>
          </>
        )}
      </div>
    </div>
  );
}

function enrich(t, beCap = 35) {
  const pnl = parseFloat(t.pnl) || 0;
  const isBE = Math.abs(pnl) <= beCap;
  return { ...t, pnl, rr: parseFloat(t.rr) || 0, win: !isBE && pnl > 0, isBE, rating: t.rating || 0 };
}

function calc(trades) {
  if (!trades.length) return { netPnl: 0, winRate: 0, avgWin: 0, avgLoss: 0, totalProfit: 0, totalLoss: 0, expectancy: 0, profitFactor: 0, avgRR: 0, maxDD: 0, maxCW: 0, maxCL: 0, sessions: {}, tagMap: {}, emotionMap: {}, wc: 0, lc: 0, be: 0 };
  const w = trades.filter(t => t.win), l = trades.filter(t => !t.win && !t.isBE), be = trades.filter(t => t.isBE);
  const countable = w.length + l.length, wr = countable ? w.length / countable : 0;
  const aw = w.length ? w.reduce((a, t) => a + t.pnl, 0) / w.length : 0;
  const al = l.length ? Math.abs(l.reduce((a, t) => a + t.pnl, 0) / l.length) : 0;
  const tp = w.reduce((a, t) => a + t.pnl, 0), tl = Math.abs(l.reduce((a, t) => a + t.pnl, 0));
  const exp = countable ? wr * aw - (1 - wr) * al : 0, pf = tl > 0 ? tp / tl : tp > 0 ? 999 : 0;
  const net = trades.reduce((a, t) => a + t.pnl, 0), ar = trades.length ? trades.reduce((a, t) => a + t.rr, 0) / trades.length : 0;
  let pk = 0, bal = 0, dd = 0; trades.forEach(t => { bal += t.pnl; if (bal > pk) pk = bal; const d = pk - bal; if (d > dd) dd = d; });
  let mcw = 0, mcl = 0, cc = 0, ct = null; trades.forEach(t => { if (t.isBE) return; if (t.win === ct) cc++; else { cc = 1; ct = t.win; } if (t.win) mcw = Math.max(mcw, cc); else mcl = Math.max(mcl, cc); });
  const sess = {}; trades.forEach(t => { if (!sess[t.session]) sess[t.session] = { pnl: 0, wins: 0, count: 0 }; sess[t.session].pnl += t.pnl; sess[t.session].count++; if (t.win) sess[t.session].wins++; });
  const tm = {}; trades.forEach(t => { (t.tags || []).forEach(tag => { if (!tm[tag]) tm[tag] = { pnl: 0, wins: 0, count: 0 }; tm[tag].pnl += t.pnl; tm[tag].count++; if (t.win) tm[tag].wins++; }); (t.mistakes || []).forEach(tag => { if (!tm[tag]) tm[tag] = { pnl: 0, wins: 0, count: 0 }; tm[tag].pnl += t.pnl; tm[tag].count++; if (t.win) tm[tag].wins++; }); });
  const em = {}; trades.forEach(t => { const e = t.emotionBefore; if (!em[e]) em[e] = { wins: 0, count: 0 }; em[e].count++; if (t.win) em[e].wins++; });
  return { netPnl: net, winRate: wr, avgWin: aw, avgLoss: al, totalProfit: tp, totalLoss: tl, expectancy: exp, profitFactor: pf, avgRR: ar, maxDD: dd, maxCW: mcw, maxCL: mcl, sessions: sess, tagMap: tm, emotionMap: em, wc: w.length, lc: l.length, be: be.length };
}

function generateInsights(trades, A, startBal) {
  if (trades.length < 3) return [];
  const insights = [];
  const sorted = [...trades].sort((a, b) => new Date(a.date) - new Date(b.date));
  const withDuration = sorted.filter(t => t.entryTime && t.exitTime);
  if (withDuration.length >= 4) {
    const wins = withDuration.filter(t => t.win), losses = withDuration.filter(t => !t.win && !t.isBE);
    if (wins.length && losses.length) {
      const avgWinDur = wins.reduce((a, t) => a + (new Date(t.exitTime) - new Date(t.entryTime)), 0) / wins.length;
      const avgLossDur = losses.reduce((a, t) => a + (new Date(t.exitTime) - new Date(t.entryTime)), 0) / losses.length;
      if (avgLossDur > avgWinDur * 1.3) {
        insights.push({ icon: "⏱️", severity: "warning", title: "Holding losers too long", body: `You hold losing trades ${Math.round((avgLossDur / avgWinDur - 1) * 100)}% longer than winners. Cut losses faster.` });
      }
    }
  }
  const sessionWR = Object.entries(A.sessions || {}).map(([s, d]) => ({ s, wr: d.count ? d.wins / d.count : 0, pnl: d.pnl, count: d.count })).filter(x => x.count >= 2);
  if (sessionWR.length) {
    const best = [...sessionWR].sort((a, b) => b.pnl - a.pnl)[0];
    const worst = [...sessionWR].sort((a, b) => a.pnl - b.pnl)[0];
    insights.push({ icon: "🏆", severity: "success", title: `Best session: ${best.s}`, body: `Your highest P&L comes from ${best.s} (${(best.wr * 100).toFixed(0)}% WR, $${best.pnl.toFixed(0)}). Prioritize this window.` });
    if (worst.pnl < 0 && worst.s !== best.s) {
      insights.push({ icon: "⚠️", severity: "danger", title: `Avoid ${worst.s} session`, body: `You lose $${Math.abs(worst.pnl).toFixed(0)} on average in ${worst.s}. Consider skipping it.` });
    }
  }
  let postLoss2Wins = 0, postLoss2Total = 0;
  for (let i = 2; i < sorted.length; i++) {
    const prev2 = sorted[i - 2], prev1 = sorted[i - 1], cur = sorted[i];
    if (!prev2.isBE && !prev1.isBE && !prev2.win && !prev1.win) {
      postLoss2Total++;
      if (!cur.isBE && !cur.win) postLoss2Wins++;
    }
  }
  if (postLoss2Total >= 3) {
    const lossRate = postLoss2Wins / postLoss2Total;
    if (lossRate > 0.5) {
      insights.push({ icon: "🔴", severity: "danger", title: "Revenge trading detected", body: `After 2 consecutive losses you lose ${(lossRate * 100).toFixed(0)}% of the time. Stop after 2 red trades.` });
    } else if (lossRate < 0.35) {
      insights.push({ icon: "💪", severity: "success", title: "Strong recovery after losses", body: `After 2 losses you bounce back ${(100 - lossRate * 100).toFixed(0)}% of the time. Good discipline.` });
    }
  }
  const buys = sorted.filter(t => t.direction === "buy"), sells = sorted.filter(t => t.direction === "sell");
  if (buys.length >= 3 && sells.length >= 3) {
    const buyWR = buys.filter(t => t.win).length / buys.length;
    const sellWR = sells.filter(t => t.win).length / sells.length;
    if (Math.abs(buyWR - sellWR) > 0.2) {
      const better = buyWR > sellWR ? "Long" : "Short";
      const worse = buyWR > sellWR ? "Short" : "Long";
      insights.push({ icon: "📈", severity: "info", title: `${better} bias suits you`, body: `${better} WR: ${(Math.max(buyWR, sellWR) * 100).toFixed(0)}% vs ${worse} WR: ${(Math.min(buyWR, sellWR) * 100).toFixed(0)}%. Trade with the bias.` });
    }
  }
  const assetMap = {};
  sorted.forEach(t => {
    if (!assetMap[t.asset]) assetMap[t.asset] = { pnl: 0, wins: 0, count: 0 };
    assetMap[t.asset].pnl += t.pnl; assetMap[t.asset].count++;
    if (t.win) assetMap[t.asset].wins++;
  });
  const bestAsset = Object.entries(assetMap).filter(([, d]) => d.count >= 2).sort((a, b) => b[1].pnl - a[1].pnl)[0];
  const worstAsset = Object.entries(assetMap).filter(([, d]) => d.count >= 2).sort((a, b) => a[1].pnl - b[1].pnl)[0];
  if (bestAsset) insights.push({ icon: "⭐", severity: "success", title: `Best instrument: ${bestAsset[0]}`, body: `${bestAsset[0]} yields $${bestAsset[1].pnl.toFixed(0)} with ${(bestAsset[1].wins / bestAsset[1].count * 100).toFixed(0)}% WR across ${bestAsset[1].count} trades.` });
  if (worstAsset && worstAsset[0] !== bestAsset?.[0] && worstAsset[1].pnl < 0) {
    insights.push({ icon: "🚫", severity: "danger", title: `Avoid ${worstAsset[0]}`, body: `You've lost $${Math.abs(worstAsset[1].pnl).toFixed(0)} on ${worstAsset[0]}. Consider removing it from your watchlist.` });
  }
  const goodTags = Object.entries(A.tagMap || {}).filter(([t]) => !MISTAKE_TAGS.includes(t) && A.tagMap[t].count >= 2).sort((a, b) => b[1].pnl - a[1].pnl);
  if (goodTags.length) {
    const [tag, d] = goodTags[0];
    insights.push({ icon: "🔑", severity: "success", title: `Best confluence: "${tag}"`, body: `Trades tagged "${tag}" produce $${d.pnl.toFixed(0)} with ${(d.wins / d.count * 100).toFixed(0)}% WR. This is your key level/setup.` });
  }
  const dayMap = {};
  sorted.forEach(t => {
    const k = t.date?.slice(0, 10);
    if (!k) return;
    if (!dayMap[k]) dayMap[k] = { trades: 0, pnl: 0 };
    dayMap[k].trades++; dayMap[k].pnl += t.pnl;
  });
  const heavyDays = Object.values(dayMap).filter(d => d.trades >= 5);
  if (heavyDays.length >= 2) {
    const avgPnlHeavy = heavyDays.reduce((a, d) => a + d.pnl, 0) / heavyDays.length;
    if (avgPnlHeavy < 0) insights.push({ icon: "📉", severity: "warning", title: "Overtrading on bad days", body: `Days with 5+ trades average $${avgPnlHeavy.toFixed(0)} P&L. Quality over quantity.` });
  }
  const ratedTrades = sorted.filter(t => t.rating > 0);
  if (ratedTrades.length >= 5) {
    const high = ratedTrades.filter(t => t.rating >= 4);
    const low = ratedTrades.filter(t => t.rating <= 2);
    if (high.length && low.length) {
      const highAvg = high.reduce((a, t) => a + t.pnl, 0) / high.length;
      const lowAvg = low.reduce((a, t) => a + t.pnl, 0) / low.length;
      if (highAvg > lowAvg * 2) {
        insights.push({ icon: "⭐", severity: "info", title: "Your A+ setups are worth waiting for", body: `High-rated trades avg $${highAvg.toFixed(0)} vs $${lowAvg.toFixed(0)} for low-rated. Only take A+ setups.` });
      }
    }
  }
  if (A.expectancy > 50) insights.push({ icon: "💰", severity: "success", title: "Strong positive expectancy", body: `Per-trade expectancy: +$${A.expectancy.toFixed(0)}. Your edge is real — stay consistent and scale up.` });
  else if (A.expectancy < -20) insights.push({ icon: "🆘", severity: "danger", title: "Negative expectancy system", body: `Per-trade expectancy: $${A.expectancy.toFixed(0)}. Fix the system before scaling size.` });
  return insights;
}

// ─── Tag Bubble Chart — standalone component so it can use hooks ───
function TagBubbleChart({ tagEntries, trades, C }) {
  const [tooltip, setTooltip] = useState(null);
  const maxCount = Math.max(...tagEntries.map(t => t.count), 1);
  const maxAbsPnl = Math.max(...tagEntries.map(t => Math.abs(t.pnl)), 1);
  const W = 600, H = 260, PAD = 40;
  const toX = pnl => PAD + ((pnl + maxAbsPnl) / (2 * maxAbsPnl)) * (W - PAD * 2);
  const toY = wr => PAD + (1 - wr / 100) * (H - PAD * 2);
  const bubbleR = count => 8 + (count / maxCount) * 22;
  const wrCol = wr => wr >= 60 ? "#22C55E" : wr >= 45 ? "#F59E0B" : "#EF4444";

  return (
    <div style={{ padding: "16px", position: "relative" }}>
      <div style={{ overflowX: "auto" }}>
        <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ display: "block", margin: "0 auto", maxWidth: W }}>
          <line x1={PAD} y1={H / 2} x2={W - PAD} y2={H / 2} stroke={C.chartGrid} strokeWidth={1} strokeDasharray="4 4" />
          <line x1={W / 2} y1={PAD} x2={W / 2} y2={H - PAD} stroke={C.chartGrid} strokeWidth={1} strokeDasharray="4 4" />
          <text x={PAD + 2} y={H - PAD + 14} fill={C.subtext} fontSize={9}>Loss</text>
          <text x={W - PAD - 22} y={H - PAD + 14} fill={C.subtext} fontSize={9}>Profit</text>
          <text x={PAD - 36} y={PAD + 4} fill={C.subtext} fontSize={9}>High WR</text>
          <text x={PAD - 36} y={H - PAD} fill={C.subtext} fontSize={9}>Low WR</text>
          <text x={W / 2 - 12} y={H - PAD + 14} fill={C.subtext} fontSize={8}>$0</text>
          <text x={PAD - 6} y={H / 2 + 4} fill={C.subtext} fontSize={8}>50%</text>
          <rect x={PAD} y={PAD} width={(W - PAD * 2) / 2} height={(H - PAD * 2) / 2} fill="rgba(34,197,94,.03)" rx={4} />
          <rect x={W / 2} y={PAD} width={(W - PAD * 2) / 2} height={(H - PAD * 2) / 2} fill="rgba(34,197,94,.06)" rx={4} />
          <rect x={PAD} y={H / 2} width={(W - PAD * 2) / 2} height={(H - PAD * 2) / 2} fill="rgba(239,68,68,.04)" rx={4} />
          <rect x={W / 2} y={H / 2} width={(W - PAD * 2) / 2} height={(H - PAD * 2) / 2} fill="rgba(239,68,68,.02)" rx={4} />
          <text x={PAD + 6} y={PAD + 14} fill="rgba(34,197,94,.3)" fontSize={8} fontWeight="600">HIGH WR · LOSING</text>
          <text x={W / 2 + 6} y={PAD + 14} fill="rgba(34,197,94,.5)" fontSize={8} fontWeight="600">HIGH WR · PROFIT ⭐</text>
          <text x={PAD + 6} y={H - PAD - 6} fill="rgba(239,68,68,.4)" fontSize={8} fontWeight="600">LOW WR · LOSING ⚠</text>
          <text x={W / 2 + 6} y={H - PAD - 6} fill="rgba(245,158,11,.4)" fontSize={8} fontWeight="600">LOW WR · PROFIT</text>
          {tagEntries.map((t) => {
            const cx = toX(t.pnl), cy = toY(t.wr), r = bubbleR(t.count);
            const col = t.isMistake ? "#EF4444" : wrCol(t.wr);
            return (
              <g key={t.tag}
                onMouseEnter={() => setTooltip({ tag: t.tag, wr: t.wr, pnl: t.pnl, count: t.count, wins: t.wins, avgRR: t.avgRR, isMistake: t.isMistake, cx, cy })}
                onMouseLeave={() => setTooltip(null)}
                style={{ cursor: "default" }}>
                <circle cx={cx} cy={cy} r={r} fill={col} fillOpacity={0.18} stroke={col} strokeWidth={1.5} />
                {r > 14 && (
                  <text x={cx} y={cy + 4} textAnchor="middle" fill={col} fontSize={Math.min(r * 0.55, 10)} fontWeight="700">
                    {t.tag.length > 8 ? t.tag.slice(0, 7) + "…" : t.tag}
                  </text>
                )}
              </g>
            );
          })}
        </svg>
      </div>
      {tooltip && (
        <div style={{ position: "absolute", top: 20, right: 20, background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: "12px 14px", minWidth: 160, boxShadow: "0 8px 24px rgba(0,0,0,.4)", zIndex: 10, pointerEvents: "none" }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 8 }}>{tooltip.tag}</div>
          {[
            ["Net P&L", `${tooltip.pnl >= 0 ? "+" : ""}$${tooltip.pnl.toFixed(0)}`, tooltip.pnl >= 0 ? "#22C55E" : "#EF4444"],
            ["Win Rate", `${tooltip.wr.toFixed(1)}%`, tooltip.wr >= 60 ? "#22C55E" : tooltip.wr >= 45 ? "#F59E0B" : "#EF4444"],
            ["Trades", `${tooltip.count} (${tooltip.wins}W/${tooltip.count - tooltip.wins}L)`, "#818CF8"],
            ["Avg RR", tooltip.avgRR.toFixed(2), "#A78BFA"],
            ["Type", tooltip.isMistake ? "Mistake" : "Setup", tooltip.isMistake ? "#EF4444" : "#A78BFA"],
          ].map(([l, v, c]) => (
            <div key={l} style={{ display: "flex", justifyContent: "space-between", fontSize: 11, marginBottom: 4 }}>
              <span style={{ color: C.subtext }}>{l}</span>
              <span style={{ color: c, fontWeight: 600 }}>{v}</span>
            </div>
          ))}
        </div>
      )}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginTop: 10 }}>
        {tagEntries.filter(t => bubbleR(t.count) <= 14).map(t => (
          <span key={t.tag} style={{ fontSize: 9, padding: "2px 7px", borderRadius: 10,
            background: (t.isMistake ? "#EF4444" : wrCol(t.wr)) + "18",
            color: t.isMistake ? "#EF4444" : wrCol(t.wr),
            border: `1px solid ${(t.isMistake ? "#EF4444" : wrCol(t.wr))}30` }}>
            {t.tag} · {t.wr.toFixed(0)}% · {t.pnl >= 0 ? "+" : ""}${t.pnl.toFixed(0)}
          </span>
        ))}
      </div>
    </div>
  );
}

function RulesPage({ C, propRules, propHistory, onSaveRules, onSaveRulesHistory }) {
  const RULE_CATEGORIES = ["Entry", "Exit", "Risk Management", "Psychology", "Session", "General"];
  const CAT_COLOR = { Entry: "#22C55E", Exit: "#F59E0B", "Risk Management": "#EF4444", Psychology: "#A78BFA", Session: "#3B82F6", General: "#6B7280" };
  const todayKey = new Date().toISOString().slice(0, 10);
  const [mode, setMode] = useState("checkin");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", category: "Entry" });
  const [editId, setEditId] = useState(null);
  const rules = propRules || [];
  const history = propHistory || {};
  const todayLog = history[todayKey] || { followed: [], broken: [], note: "", submitted: false };
  const [todayNote, setTodayNote] = useState(todayLog.note || "");
  useEffect(() => { setTodayNote(todayLog.note || ""); }, [todayKey]);
  const saveRules = async (r) => { if (onSaveRules) await onSaveRules(r); };
  const saveHistory = async (h) => { if (onSaveRulesHistory) await onSaveRulesHistory(h); };
  const submitRule = () => {
    if (!form.title.trim()) return;
    const id = editId || Date.now().toString();
    const updated = editId ? rules.map(r => r.id === editId ? { ...form, id } : r) : [...rules, { ...form, id }];
    saveRules(updated);
    setForm({ title: "", description: "", category: "Entry" });
    setEditId(null); setShowForm(false);
  };
  const delRule = id => saveRules(rules.filter(r => r.id !== id));
  const toggleFollow = (id, status) => {
    const log = { ...todayLog };
    if (status === "followed") {
      log.followed = log.followed.includes(id) ? log.followed.filter(x => x !== id) : [...log.followed.filter(x => x !== id), id];
      log.broken = log.broken.filter(x => x !== id);
    } else {
      log.broken = log.broken.includes(id) ? log.broken.filter(x => x !== id) : [...log.broken.filter(x => x !== id), id];
      log.followed = log.followed.filter(x => x !== id);
    }
    saveHistory({ ...history, [todayKey]: log });
  };
  const submitCheckin = () => {
    const log = { ...todayLog, note: todayNote, submitted: true, ts: Date.now() };
    saveHistory({ ...history, [todayKey]: log });
  };
  const resetToday = () => { const h = { ...history }; delete h[todayKey]; saveHistory(h); setTodayNote(""); };
  const checkedCount = todayLog.followed.length + todayLog.broken.length;
  const followPct = rules.length ? Math.round(todayLog.followed.length / rules.length * 100) : 0;
  const histEntries = Object.entries(history).sort((a, b) => b[0].localeCompare(a[0])).slice(0, 30);
  const iS = { width: "100%", background: C.inputBg, border: `1px solid ${C.border}`, borderRadius: 7, padding: "8px 10px", color: C.text, fontSize: 12, outline: "none", fontFamily: "inherit" };
  const lS = { fontSize: 10, color: C.subtext, textTransform: "uppercase", letterSpacing: ".1em", display: "block", marginBottom: 4 };
  const cS = { background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, overflow: "hidden" };
  const cP = { ...cS, padding: "14px 16px" };
  const tabBtn = (id, lb) => (
    <button onClick={() => setMode(id)} style={{ background: mode === id ? "rgba(34,197,94,.12)" : "transparent", border: `1px solid ${mode === id ? "rgba(34,197,94,.35)" : C.border}`, borderRadius: 6, padding: "6px 16px", fontSize: 11, fontWeight: 600, color: mode === id ? "#22C55E" : C.subtext, cursor: "pointer", fontFamily: "inherit" }}>{lb}</button>
  );
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, color: C.text }}>Trading Rules</div>
          <div style={{ fontSize: 11, color: C.subtext, marginTop: 2 }}>{rules.length} rules · {new Date().toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" })}</div>
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          {tabBtn("checkin", "📋 Today")}
          {tabBtn("history", "📅 History")}
          {tabBtn("manage", "⚙️ Manage")}
        </div>
      </div>
      {mode === "checkin" && (
        <>
          {rules.length === 0 ? (
            <div style={{ ...cP, textAlign: "center", padding: "50px 20px" }}>
              <div style={{ fontSize: 32, marginBottom: 10 }}>📋</div>
              <div style={{ fontSize: 14, fontWeight: 600, color: C.text, marginBottom: 6 }}>No rules yet</div>
              <div style={{ fontSize: 12, color: C.subtext, marginBottom: 16 }}>Add your trading rules first, then use this page for your daily check-in.</div>
              <button onClick={() => setMode("manage")} style={{ background: "#22C55E", border: "none", borderRadius: 7, padding: "8px 18px", color: "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>+ Add Rules</button>
            </div>
          ) : (
            <>
              <div style={cP}>
                <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 12 }}>
                  <div style={{ position: "relative", width: 72, height: 72, flexShrink: 0 }}>
                    <svg width="72" height="72" style={{ transform: "rotate(-90deg)" }}>
                      <circle cx="36" cy="36" r="30" fill="none" stroke={C.border} strokeWidth="6" />
                      <circle cx="36" cy="36" r="30" fill="none"
                        stroke={followPct >= 80 ? "#22C55E" : followPct >= 50 ? "#F59E0B" : "#EF4444"}
                        strokeWidth="6" strokeLinecap="round"
                        strokeDasharray={`${2 * Math.PI * 30}`}
                        strokeDashoffset={`${2 * Math.PI * 30 * (1 - followPct / 100)}`}
                        style={{ transition: "stroke-dashoffset 0.6s" }} />
                    </svg>
                    <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                      <div style={{ fontSize: 16, fontWeight: 700, color: followPct >= 80 ? "#22C55E" : followPct >= 50 ? "#F59E0B" : "#EF4444", lineHeight: 1 }}>{followPct}%</div>
                    </div>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 4 }}>
                      {todayLog.submitted ? "✅ Check-in submitted" : checkedCount === 0 ? "Start your daily check-in" : `${checkedCount}/${rules.length} rules reviewed`}
                    </div>
                    <div style={{ display: "flex", gap: 12, fontSize: 11 }}>
                      <span style={{ color: "#22C55E" }}>✓ {todayLog.followed.length} followed</span>
                      <span style={{ color: "#EF4444" }}>✗ {todayLog.broken.length} broken</span>
                      <span style={{ color: C.subtext }}>{rules.length - checkedCount} pending</span>
                    </div>
                    <div style={{ height: 4, background: C.border, borderRadius: 2, marginTop: 8, overflow: "hidden" }}>
                      <div style={{ display: "flex", height: "100%" }}>
                        <div style={{ width: `${todayLog.followed.length / rules.length * 100}%`, background: "#22C55E", transition: "width 0.4s" }} />
                        <div style={{ width: `${todayLog.broken.length / rules.length * 100}%`, background: "#EF4444", transition: "width 0.4s" }} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {RULE_CATEGORIES.filter(cat => rules.some(r => r.category === cat)).map(cat => (
                <div key={cat} style={cS}>
                  <div style={{ padding: "9px 14px", borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", gap: 7 }}>
                    <div style={{ width: 7, height: 7, borderRadius: "50%", background: CAT_COLOR[cat], flexShrink: 0 }} />
                    <span style={{ fontSize: 11, fontWeight: 600, color: C.text }}>{cat}</span>
                  </div>
                  <div style={{ padding: "6px 8px" }}>
                    {rules.filter(r => r.category === cat).map(rule => {
                      const isFollowed = todayLog.followed.includes(rule.id);
                      const isBroken = todayLog.broken.includes(rule.id);
                      return (
                        <div key={rule.id} style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "10px 8px", borderRadius: 8, marginBottom: 2, background: isFollowed ? "rgba(34,197,94,.04)" : isBroken ? "rgba(239,68,68,.04)" : "transparent" }}>
                          <div style={{ flex: 1, paddingTop: 2 }}>
                            <div style={{ fontSize: 13, fontWeight: 500, color: C.text }}>{rule.title}</div>
                            {rule.description && <div style={{ fontSize: 11, color: C.subtext, marginTop: 2, lineHeight: 1.5 }}>{rule.description}</div>}
                          </div>
                          <div style={{ display: "flex", gap: 5, flexShrink: 0 }}>
                            <button onClick={() => toggleFollow(rule.id, "followed")} style={{ padding: "4px 10px", borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", border: isFollowed ? "1.5px solid #22C55E" : `1px solid ${C.border}`, background: isFollowed ? "rgba(34,197,94,.15)" : C.inputBg, color: isFollowed ? "#22C55E" : C.subtext }}>✓ Yes</button>
                            <button onClick={() => toggleFollow(rule.id, "broken")} style={{ padding: "4px 10px", borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", border: isBroken ? "1.5px solid #EF4444" : `1px solid ${C.border}`, background: isBroken ? "rgba(239,68,68,.12)" : C.inputBg, color: isBroken ? "#EF4444" : C.subtext }}>✗ No</button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
              <div style={cP}>
                <div style={{ fontSize: 11, fontWeight: 600, color: C.subtext, textTransform: "uppercase", letterSpacing: ".08em", marginBottom: 6 }}>Session Notes (optional)</div>
                <textarea value={todayNote} onChange={e => setTodayNote(e.target.value)} placeholder="Why did you break a rule? What can you do better tomorrow?" style={{ ...iS, resize: "vertical", minHeight: 64, marginBottom: 12 }} />
                <div style={{ display: "flex", gap: 8, justifyContent: "space-between", alignItems: "center" }}>
                  <button onClick={resetToday} style={{ background: "transparent", border: `1px solid ${C.border}`, borderRadius: 7, padding: "7px 14px", color: C.subtext, fontSize: 11, cursor: "pointer", fontFamily: "inherit" }}>Reset Today</button>
                  <button onClick={submitCheckin} disabled={todayLog.submitted} style={{ background: todayLog.submitted ? C.border : "#22C55E", border: "none", borderRadius: 7, padding: "8px 20px", color: todayLog.submitted ? C.subtext : "#fff", fontSize: 12, fontWeight: 600, cursor: todayLog.submitted ? "default" : "pointer", fontFamily: "inherit", opacity: todayLog.submitted ? 0.6 : 1 }}>
                    {todayLog.submitted ? "✅ Submitted" : "Submit Check-in"}
                  </button>
                </div>
              </div>
            </>
          )}
        </>
      )}
      {mode === "history" && (
        histEntries.length === 0 ? (
          <div style={{ ...cP, textAlign: "center", padding: "50px 20px" }}>
            <div style={{ fontSize: 32, marginBottom: 10 }}>📅</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: C.text, marginBottom: 6 }}>No history yet</div>
            <div style={{ fontSize: 12, color: C.subtext }}>Submit your first daily check-in and it will appear here.</div>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <div style={cP}>
              <div style={{ fontSize: 12, fontWeight: 600, color: C.text, marginBottom: 10 }}>Compliance over time</div>
              <div style={{ display: "flex", gap: 3, alignItems: "flex-end", height: 40 }}>
                {histEntries.slice(0, 20).reverse().map(([date, log]) => {
                  const tot = (log.followed?.length || 0) + (log.broken?.length || 0);
                  const pct = tot > 0 ? (log.followed?.length || 0) / tot : 0;
                  const col = pct >= 0.8 ? "#22C55E" : pct >= 0.5 ? "#F59E0B" : "#EF4444";
                  return <div key={date} title={`${date}: ${Math.round(pct * 100)}%`} style={{ flex: 1, minWidth: 8, height: `${Math.max(pct * 100, 8)}%`, background: col, borderRadius: 2, opacity: 0.85 }} />;
                })}
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 9, color: C.subtext, marginTop: 4 }}>
                <span>{histEntries.slice(0, 20).reverse()[0]?.[0]}</span>
                <span>Today ←</span>
              </div>
            </div>
            {histEntries.map(([date, log]) => {
              const followed = log.followed?.length || 0;
              const broken = log.broken?.length || 0;
              const total = followed + broken;
              const pct = total > 0 ? Math.round(followed / total * 100) : null;
              const col = pct === null ? C.subtext : pct >= 80 ? "#22C55E" : pct >= 50 ? "#F59E0B" : "#EF4444";
              const label = new Date(date + "T12:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
              return (
                <div key={date} style={cS}>
                  <div style={{ padding: "10px 14px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div style={{ width: 36, height: 36, borderRadius: "50%", border: `2px solid ${col}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <span style={{ fontSize: 11, fontWeight: 700, color: col }}>{pct !== null ? `${pct}%` : "—"}</span>
                      </div>
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 600, color: C.text }}>{label}</div>
                        <div style={{ fontSize: 10, color: C.subtext, marginTop: 1 }}>
                          <span style={{ color: "#22C55E" }}>{followed} followed</span>
                          {broken > 0 && <span style={{ color: "#EF4444" }}> · {broken} broken</span>}
                          {!log.submitted && <span style={{ color: C.subtext }}> · not submitted</span>}
                        </div>
                      </div>
                    </div>
                    {broken > 0 && (
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 3, justifyContent: "flex-end", maxWidth: "55%" }}>
                        {(log.broken || []).map(id => {
                          const rule = rules.find(r => r.id === id);
                          return rule ? <span key={id} style={{ fontSize: 9, padding: "2px 6px", borderRadius: 10, background: "rgba(239,68,68,.1)", color: "#EF4444", border: "1px solid rgba(239,68,68,.2)" }}>{rule.title}</span> : null;
                        })}
                      </div>
                    )}
                  </div>
                  {log.note && <div style={{ padding: "0 14px 10px", fontSize: 11, color: C.subtext, borderTop: `1px solid ${C.border}`, paddingTop: 8, lineHeight: 1.5, fontStyle: "italic" }}>"{log.note}"</div>}
                </div>
              );
            })}
          </div>
        )
      )}
      {mode === "manage" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <button onClick={() => { setShowForm(true); setEditId(null); setForm({ title: "", description: "", category: "Entry" }); }} style={{ background: "#22C55E", border: "none", borderRadius: 7, padding: "7px 16px", color: "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>+ Add Rule</button>
          </div>
          {showForm && (
            <div style={{ ...cS, padding: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 12 }}>{editId ? "Edit Rule" : "New Rule"}</div>
              <div style={{ display: "grid", gap: 10, marginBottom: 12 }}>
                <div><label style={lS}>Rule Title *</label><input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="e.g. Only trade A+ setups" style={iS} /></div>
                <div><label style={lS}>Category</label><select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} style={iS}>{RULE_CATEGORIES.map(c => <option key={c}>{c}</option>)}</select></div>
                <div><label style={lS}>Description</label><textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Details, context, or why this rule matters..." style={{ ...iS, resize: "vertical", minHeight: 60 }} /></div>
              </div>
              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                <button onClick={() => { setShowForm(false); setEditId(null); }} style={{ background: "transparent", border: `1px solid ${C.border}`, borderRadius: 7, padding: "7px 14px", color: C.subtext, fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
                <button onClick={submitRule} style={{ background: "#22C55E", border: "none", borderRadius: 7, padding: "7px 14px", color: "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>Save</button>
              </div>
            </div>
          )}
          {rules.length === 0 && !showForm && (
            <div style={{ ...cP, textAlign: "center", padding: "40px 20px" }}>
              <div style={{ fontSize: 32, marginBottom: 10 }}>📋</div>
              <div style={{ fontSize: 13, color: C.subtext }}>No rules yet. Add your first trading rule above.</div>
            </div>
          )}
          {RULE_CATEGORIES.filter(cat => rules.some(r => r.category === cat)).map(cat => (
            <div key={cat} style={cS}>
              <div style={{ padding: "9px 14px", borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", gap: 7 }}>
                <div style={{ width: 7, height: 7, borderRadius: "50%", background: CAT_COLOR[cat] }} />
                <span style={{ fontSize: 11, fontWeight: 600, color: C.text }}>{cat}</span>
                <span style={{ fontSize: 10, color: C.subtext }}>({rules.filter(r => r.category === cat).length})</span>
              </div>
              <div style={{ padding: "6px 8px" }}>
                {rules.filter(r => r.category === cat).map(rule => (
                  <div key={rule.id} style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "10px 8px", borderRadius: 8, marginBottom: 2 }}>
                    <div style={{ width: 7, height: 7, borderRadius: "50%", background: CAT_COLOR[cat], flexShrink: 0, marginTop: 5 }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 500, color: C.text }}>{rule.title}</div>
                      {rule.description && <div style={{ fontSize: 11, color: C.subtext, marginTop: 2, lineHeight: 1.5 }}>{rule.description}</div>}
                    </div>
                    <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
                      <button onClick={() => { setEditId(rule.id); setForm({ title: rule.title, description: rule.description, category: rule.category }); setShowForm(true); }} style={{ background: "none", border: `1px solid ${C.border}`, borderRadius: 5, padding: "2px 7px", color: C.subtext, fontSize: 10, cursor: "pointer" }}>Edit</button>
                      <button onClick={() => delRule(rule.id)} style={{ background: "rgba(239,68,68,.08)", border: "1px solid rgba(239,68,68,.2)", borderRadius: 5, padding: "2px 7px", color: "#EF4444", fontSize: 10, cursor: "pointer" }}>×</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CircleGauge({ value, max, size = 52, strokeW = 5, trackColor = "#1E2028" }) {
  const r = (size - strokeW) / 2, circ = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(Math.min(value, max) / max, 1));
  const col = pct === 0 ? "#374151" : pct >= 0.5 ? "#22C55E" : pct >= 0.25 ? "#F59E0B" : "#EF4444";
  return (
    <svg width={size} height={size} style={{ transform: "rotate(-90deg)", flexShrink: 0 }}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={trackColor} strokeWidth={strokeW} strokeOpacity={0.8} />
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={col} strokeWidth={strokeW} strokeDasharray={circ} strokeDashoffset={circ * (1 - pct)} strokeLinecap="round" style={{ transition: "stroke-dashoffset .8s" }} />
    </svg>
  );
}

function SemiGauge({ wc = 0, lc = 0, be = 0, size = 64 }) {
  const total = wc + lc + be || 1;
  const strokeW = 6, r = (size - strokeW) / 2, half = Math.PI * r;
  const wA = half * (wc / total), bA = half * (be / total), lA = half * (lc / total);
  const W = size, H = size / 2 + strokeW, cy = H - 2;
  return (
    <div style={{ position: "relative", width: W + 36, height: H + 24, flexShrink: 0 }}>
      <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ display: "block", margin: "0 18px" }}>
        <path d={`M ${strokeW / 2} ${cy} A ${r} ${r} 0 0 1 ${W - strokeW / 2} ${cy}`} fill="none" stroke="#181920" strokeWidth={strokeW} />
        {wA > .1 && <path d={`M ${strokeW / 2} ${cy} A ${r} ${r} 0 0 1 ${W - strokeW / 2} ${cy}`} fill="none" stroke="#22C55E" strokeWidth={strokeW} strokeLinecap="round" strokeDasharray={`${wA} ${half}`} strokeDashoffset={0} />}
        {bA > .1 && <path d={`M ${strokeW / 2} ${cy} A ${r} ${r} 0 0 1 ${W - strokeW / 2} ${cy}`} fill="none" stroke="#3B82F6" strokeWidth={strokeW} strokeLinecap="round" strokeDasharray={`${bA} ${half}`} strokeDashoffset={`${-wA}`} />}
        {lA > .1 && <path d={`M ${strokeW / 2} ${cy} A ${r} ${r} 0 0 1 ${W - strokeW / 2} ${cy}`} fill="none" stroke="#EF4444" strokeWidth={strokeW} strokeLinecap="round" strokeDasharray={`${lA} ${half}`} strokeDashoffset={`${-(wA + bA)}`} />}
      </svg>
      <div style={{ position: "absolute", bottom: 2, left: 8, display: "flex", alignItems: "center", justifyContent: "center", width: 20, height: 20, borderRadius: "50%", background: "rgba(34,197,94,.12)", color: "#22C55E", fontSize: 9, fontWeight: 700, border: "1px solid rgba(34,197,94,.35)" }}>{wc}</div>
      {be > 0 && <div style={{ position: "absolute", bottom: 2, left: "50%", transform: "translateX(-50%)", display: "flex", alignItems: "center", justifyContent: "center", width: 20, height: 20, borderRadius: "50%", background: "rgba(59,130,246,.12)", color: "#3B82F6", fontSize: 9, fontWeight: 700, border: "1px solid rgba(59,130,246,.35)" }}>{be}</div>}
      <div style={{ position: "absolute", bottom: 2, right: 8, display: "flex", alignItems: "center", justifyContent: "center", width: 20, height: 20, borderRadius: "50%", background: "rgba(239,68,68,.12)", color: "#EF4444", fontSize: 9, fontWeight: 700, border: "1px solid rgba(239,68,68,.35)" }}>{lc}</div>
    </div>
  );
}

function ThunderScore({ data }) {
  const { winRate = 0, profitFactor = 0, avgRR = 0, maxCL = 0, expectancy = 0 } = data;
  const axes = [
    { label: "Win %", score: Math.min(winRate * 140, 100) },
    { label: "PF", score: Math.min((profitFactor / 3) * 100, 100) },
    { label: "AVG W/L", score: Math.min((avgRR / 4) * 100, 100) },
    { label: "Avg RR", score: Math.min((avgRR / 3) * 100, 100) },
    { label: "Consist.", score: Math.max(0, 100 - maxCL * 15) },
    { label: "Recovery", score: Math.min(Math.max((expectancy + 100) / 300 * 100, 0), 100) },
  ];
  const total = Math.round(axes.reduce((a, x) => a + x.score, 0) / axes.length);
  const n = 6, cx = 150, cy = 140, mR = 80;
  const pt = (i, r) => { const a = -Math.PI / 2 + (2 * Math.PI * i) / n; return { x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) }; };
  const grids = [.2, .4, .6, .8, 1].map(s => Array.from({ length: n }, (_, i) => pt(i, mR * s)).map(p => `${p.x},${p.y}`).join(" "));
  const dp = axes.map((a, i) => pt(i, (a.score / 100) * mR));
  const col = total >= 70 ? "#22C55E" : total >= 50 ? "#F59E0B" : total >= 30 ? "#E85D2A" : "#8B1A1A";
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, minHeight: 260, flex: 1, justifyContent: "center" }}>
      <svg viewBox="0 0 300 260" width="100%" style={{ maxWidth: 300 }} preserveAspectRatio="xMidYMid meet">
        <defs><linearGradient id="hF" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#8B5CF6" stopOpacity=".45" /><stop offset="100%" stopColor="#6366F1" stopOpacity=".1" /></linearGradient></defs>
        {grids.map((g, i) => <polygon key={i} points={g} fill="none" stroke="rgba(128,128,128,.15)" strokeWidth={i === 4 ? 1 : .4} />)}
        {Array.from({ length: n }, (_, i) => { const o = pt(i, mR); return <line key={i} x1={cx} y1={cy} x2={o.x} y2={o.y} stroke="rgba(128,128,128,.15)" strokeWidth=".4" />; })}
        <polygon points={dp.map(p => `${p.x},${p.y}`).join(" ")} fill="url(#hF)" stroke="#8B5CF6" strokeWidth="1.8" strokeLinejoin="round" />
        {dp.map((p, i) => <circle key={i} cx={p.x} cy={p.y} r="3.5" fill="#A78BFA" stroke="#fff" strokeWidth="1.5" strokeOpacity=".5" />)}
        {axes.map((a, i) => { const lp = pt(i, mR + 30); const anc = lp.x < cx - 12 ? "end" : lp.x > cx + 12 ? "start" : "middle"; return <text key={i} x={lp.x} y={lp.y + 4} textAnchor={anc} fill="#6B7280" fontSize="10.5" fontFamily="Inter" fontWeight="600">{a.label}</text>; })}
      </svg>
      <div style={{ width: "100%", maxWidth: 200, marginTop: -4 }}>
        <div style={{ height: 5, background: "linear-gradient(90deg,#8B1A1A,#E85D2A 30%,#F59E0B 50%,#22C55E)", borderRadius: 3, position: "relative", marginBottom: 5 }}>
          <div style={{ position: "absolute", top: "50%", left: `${total}%`, transform: "translate(-50%,-50%)", width: 11, height: 11, borderRadius: "50%", background: col, border: "2px solid #fff", boxShadow: `0 0 8px ${col}` }} />
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 7, color: "#6B7280" }}><span>0</span><span>25</span><span>50</span><span>75</span><span>100</span></div>
      </div>
      <div style={{ textAlign: "center", marginTop: 2, paddingBottom: 6 }}>
        <div style={{ fontSize: 30, fontWeight: 700, color: col, lineHeight: 1 }}>{total}</div>
        <div style={{ fontSize: 8, color: "#6B7280", letterSpacing: 2, marginTop: 3, textTransform: "uppercase" }}>Thunder Score</div>
      </div>
    </div>
  );
}

function WinLossBar({ avgWin, avgLoss }) {
  const total = avgWin + avgLoss || 1, wp = (avgWin / total) * 100;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 3, width: "100%" }}>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 9, fontWeight: 600 }}>
        <span style={{ color: "#22C55E" }}>+${avgWin.toFixed(0)}</span>
        <span style={{ color: "#EF4444" }}>-${avgLoss.toFixed(0)}</span>
      </div>
      <div style={{ display: "flex", height: 5, borderRadius: 3, overflow: "hidden" }}>
        <div style={{ width: `${wp}%`, background: "#22C55E", borderRadius: "3px 0 0 3px" }} />
        <div style={{ width: `${100 - wp}%`, background: "#EF4444", borderRadius: "0 3px 3px 0" }} />
      </div>
    </div>
  );
}

function MiniCal({ value, onPick, onClose }) {
  const [vm, setVm] = useState(() => { const d = value ? new Date(value) : new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const yr = vm.getFullYear(), mo = vm.getMonth(), fd = new Date(yr, mo, 1).getDay(), dim = new Date(yr, mo + 1, 0).getDate(), today = new Date();
  const cells = [];
  for (let i = 0; i < fd; i++) cells.push(null);
  for (let d = 1; d <= dim; d++) cells.push(d);
  const selD = value ? new Date(value).toDateString() : "";
  return (
    <div style={{ position: "absolute", top: "100%", left: 0, zIndex: 999, background: "#141519", border: "1px solid #1E2028", borderRadius: 10, padding: 10, width: 260, boxShadow: "0 12px 40px rgba(0,0,0,.7)", marginTop: 4 }} onClick={e => e.stopPropagation()}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <button onClick={() => setVm(new Date(yr, mo - 1, 1))} style={{ background: "none", border: "1px solid #1E2028", borderRadius: 5, padding: "3px 8px", color: "#6B7280", cursor: "pointer", fontSize: 11 }}>‹</button>
        <span style={{ fontSize: 12, fontWeight: 600, color: "#fff" }}>{vm.toLocaleDateString("en-US", { month: "long", year: "numeric" })}</span>
        <button onClick={() => setVm(new Date(yr, mo + 1, 1))} style={{ background: "none", border: "1px solid #1E2028", borderRadius: 5, padding: "3px 8px", color: "#6B7280", cursor: "pointer", fontSize: 11 }}>›</button>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 1 }}>
        {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => <div key={i} style={{ textAlign: "center", fontSize: 9, color: "#4B5563", padding: "3px 0" }}>{d}</div>)}
        {cells.map((day, i) => {
          if (!day) return <div key={`e${i}`} />;
          const dt = new Date(yr, mo, day), isT = dt.toDateString() === today.toDateString(), isS = dt.toDateString() === selD;
          return <button key={day} onClick={() => { onPick(`${yr}-${String(mo + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}T${new Date().toTimeString().slice(0, 5)}`); onClose(); }}
            style={{ background: isS ? "#22C55E" : isT ? "rgba(34,197,94,.12)" : "transparent", color: isS ? "#fff" : isT ? "#22C55E" : "#fff", border: isT && !isS ? "1px solid rgba(34,197,94,.35)" : "1px solid transparent", borderRadius: 5, padding: "5px 0", fontSize: 11, fontWeight: isS || isT ? 600 : 400, cursor: "pointer", fontFamily: "inherit" }}>{day}</button>;
        })}
      </div>
      <div style={{ display: "flex", gap: 5, marginTop: 8 }}>
        <button onClick={() => { const n = new Date(); onPick(`${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, "0")}-${String(n.getDate()).padStart(2, "0")}T${n.toTimeString().slice(0, 5)}`); onClose(); }} style={{ flex: 1, background: "rgba(34,197,94,.12)", border: "1px solid rgba(34,197,94,.35)", borderRadius: 5, padding: "5px 0", color: "#22C55E", fontSize: 10, fontWeight: 600, cursor: "pointer" }}>Today</button>
        <button onClick={onClose} style={{ flex: 1, background: "transparent", border: "1px solid #1E2028", borderRadius: 5, padding: "5px 0", color: "#4B5563", fontSize: 10, cursor: "pointer" }}>Close</button>
      </div>
    </div>
  );
}

const CTip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: "#141519", border: "1px solid #1E2028", borderRadius: 8, padding: "8px 12px", fontSize: 11, boxShadow: "0 8px 20px rgba(0,0,0,.5)" }}>
      <div style={{ color: "#4B5563", fontSize: 9, marginBottom: 4 }}>{label}</div>
      {payload.map((p, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 2 }}>
          <div style={{ width: 5, height: 5, borderRadius: "50%", background: typeof p.value === "number" && p.value < 0 ? "#EF4444" : "#22C55E" }} />
          <span style={{ color: typeof p.value === "number" && p.value < 0 ? "#EF4444" : "#22C55E", fontWeight: 600 }}>{p.name}: ${typeof p.value === "number" ? p.value.toFixed(2) : p.value}</span>
        </div>
      ))}
    </div>
  );
};

function SettingsModal({ startBal, userName, beCap, ratingType, theme: currentTheme, onSave, onClose }) {
  const C = useColors();
  const [v, setV] = useState(String(startBal));
  const [theme, setTheme] = useState(currentTheme || "dark");
  const [name, setName] = useState(userName);
  const [beVal, setBeVal] = useState(String(beCap));
  const [rt, setRt] = useState(ratingType);
  const iS = { width: "100%", background: C.inputBg, border: `1px solid ${C.border}`, borderRadius: 7, padding: "10px 12px", color: C.text, fontSize: 14, outline: "none", fontFamily: "inherit" };
  const lS = { fontSize: 10, color: C.subtext, textTransform: "uppercase", letterSpacing: ".1em", display: "block", marginBottom: 5 };
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.75)", zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, backdropFilter: "blur(4px)" }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, width: "100%", maxWidth: 420, padding: 24 }}>
        <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 16, color: C.text }}>⚙️ Account Settings</div>
        <div style={{ marginBottom: 14 }}><label style={lS}>Display Name</label><input value={name} onChange={e => setName(e.target.value)} placeholder="Tag" style={iS} /></div>
        <div style={{ marginBottom: 14 }}><label style={lS}>Starting Balance ($)</label><input type="number" value={v} onChange={e => setV(e.target.value)} style={iS} /></div>
        <div style={{ marginBottom: 14 }}>
          <label style={lS}>Breakeven Cap ($)</label>
          <input type="number" value={beVal} onChange={e => setBeVal(e.target.value)} style={iS} />
          <div style={{ display: "flex", gap: 5, marginTop: 6 }}>
            {[0, 10, 20, 35, 50].map(p => (
              <button key={p} onClick={() => setBeVal(String(p))} style={{ background: String(p) === beVal ? "rgba(59,130,246,.15)" : C.inputBg, border: `1px solid ${String(p) === beVal ? "rgba(59,130,246,.4)" : C.border}`, borderRadius: 5, padding: "4px 10px", fontSize: 10, fontWeight: 600, color: String(p) === beVal ? "#3B82F6" : C.subtext, cursor: "pointer", fontFamily: "inherit" }}>${p}</button>
            ))}
          </div>
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={lS}>Rating Style</label>
          <div style={{ display: "flex", gap: 8 }}>
            {[["stars", "★ Stars"], ["grades", "A+ Grades"]].map(([k, lb]) => (
              <button key={k} onClick={() => setRt(k)} style={{ flex: 1, background: rt === k ? "rgba(139,92,246,.12)" : C.inputBg, border: `1px solid ${rt === k ? "rgba(139,92,246,.4)" : C.border}`, borderRadius: 7, padding: "10px 8px", color: rt === k ? "#A78BFA" : C.subtext, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>{lb}</button>
            ))}
          </div>
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={lS}>Theme</label>
          <div style={{ display: "flex", gap: 8 }}>
            {["dark", "light"].map(t => (
              <button key={t} onClick={() => setTheme(t)} style={{ flex: 1, background: theme === t ? "rgba(34,197,94,.12)" : C.inputBg, border: `1px solid ${theme === t ? "rgba(34,197,94,.4)" : C.border}`, borderRadius: 7, padding: "10px 8px", color: theme === t ? "#22C55E" : C.subtext, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>{t.toUpperCase()}</button>
            ))}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button onClick={onClose} style={{ background: "transparent", border: `1px solid ${C.border}`, borderRadius: 7, padding: "7px 16px", color: C.subtext, fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
          <button onClick={() => { document.body.classList.remove("light", "dark"); document.body.classList.add(theme); localStorage.setItem("theme", theme); onSave({ startBal: parseFloat(v) || 50000, userName: name, beCap: parseFloat(beVal) || 0, ratingType: rt, theme }); onClose(); }} style={{ background: "#22C55E", border: "none", borderRadius: 7, padding: "7px 16px", color: "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>Save</button>
        </div>
      </div>
    </div>
  );
}

function TradeModal({ initial, onClose, onSave, savedTags, onSaveTags, beCap, ratingType, propRulesInModal }) {
  const C = useColors();
  const now = new Date();
  const localDate = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}T${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
  const blank = { asset: "", direction: "buy", pnl: "", rr: "", session: "NY AM", tags: [], mistakes: [], emotionBefore: "Neutral", emotionAfter: "Neutral", notes: "", date: localDate, chartImages: [], rating: 0 };
  const [f, setF] = useState(initial ? { ...initial, pnl: String(initial.pnl ?? ""), rr: String(initial.rr ?? ""), date: (initial.date || "").slice(0, 16) || blank.date, rating: initial.rating || 0, mistakes: initial.mistakes || [], tags: initial.tags || [], chartImages: initial.chartImages || (initial.chartImage ? [initial.chartImage] : []) } : blank);
  const [showCal, setShowCal] = useState(false);
  const [ctag, setCtag] = useState("");
  const [lbIdx, setLbIdx] = useState(null);
  const s = (k, v) => setF(p => ({ ...p, [k]: v }));
  const submit = () => { if (!f.asset || f.pnl === "") return alert("Fill required fields"); onSave(f, !!initial); onClose(); };
  const toggleTag = tag => s("tags", f.tags.includes(tag) ? f.tags.filter(t => t !== tag) : [...f.tags, tag]);
  const toggleMistake = tag => s("mistakes", f.mistakes.includes(tag) ? f.mistakes.filter(t => t !== tag) : [...f.mistakes, tag]);
  const addCtag = () => { const t = ctag.trim(); if (t && !f.tags.includes(t)) { s("tags", [...f.tags, t]); if (!savedTags.includes(t)) onSaveTags([...savedTags, t]); } setCtag(""); };
  const removeSavedTag = (tag) => { const updated = savedTags.filter(t => t !== tag); onSaveTags(updated); if (f.tags.includes(tag)) s("tags", f.tags.filter(t => t !== tag)); };
  const addImg = (file) => {
  if (!file || f.chartImages.length >= 5) return;

  const reader = new FileReader();
  reader.onload = (e) => {
    const img = new Image();
    img.onload = () => {
      let { width, height } = img;
      const MAX_W = 1920, MAX_H = 1080;
      if (width > MAX_W || height > MAX_H) {
        const ratio = Math.min(MAX_W / width, MAX_H / height);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      canvas.getContext('2d').drawImage(img, 0, 0, width, height);
      let compressed = canvas.toDataURL('image/jpeg', 0.92);
if (compressed.length > 600000) compressed = canvas.toDataURL('image/jpeg', 0.82);
      s('chartImages', [...f.chartImages, compressed]);
    };
    img.src = e.target.result;
  };
  reader.readAsDataURL(file);
};
  const rmImg = i => s("chartImages", f.chartImages.filter((_, j) => j !== i));
  useEffect(() => {
    const h = e => { const it = Array.from(e.clipboardData?.items || []).find(i => i.type.startsWith("image/")); if (it && f.chartImages.length < 7) { e.preventDefault(); addImg(it.getAsFile()); } };
    document.addEventListener("paste", h);
    return () => document.removeEventListener("paste", h);
  }, [f.chartImages.length]);
  const pv = parseFloat(f.pnl) || 0;
  const isBE = Math.abs(pv) <= beCap;
  const iS = { width: "100%", background: C.inputBg, border: `1px solid ${C.border}`, borderRadius: 7, padding: "8px 10px", color: C.text, fontSize: 12, outline: "none", fontFamily: "inherit" };
  const lS = { fontSize: 10, color: C.subtext, textTransform: "uppercase", letterSpacing: ".1em", display: "block", marginBottom: 4, fontWeight: 500 };
  const sS = { fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".12em", color: "#22C55E", marginBottom: 10, paddingBottom: 6, borderBottom: `1px solid ${C.border}` };
  return (
    <div className="modal-overlay" style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.8)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, backdropFilter: "blur(4px)" }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal-box" style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, width: "100%", maxWidth: 740, maxHeight: "90vh", overflowY: "auto" }}>
        <div style={{ padding: "16px 22px", borderBottom: `1px solid ${C.border}`, display: "flex", justifyContent: "space-between", alignItems: "center", position: "sticky", top: 0, background: C.card, zIndex: 10, borderRadius: "14px 14px 0 0" }}>
          <span style={{ fontSize: 15, fontWeight: 600, color: C.text }}>{initial ? "Edit Trade" : "New Trade"}</span>
          <button onClick={onClose} style={{ background: "none", border: `1px solid ${C.border}`, borderRadius: 6, padding: "4px 10px", color: C.subtext, cursor: "pointer", fontSize: 11 }}>✕</button>
        </div>
        <div style={{ padding: "16px 22px" }}>
          {f.pnl !== "" && (
            <div style={{ display: "flex", gap: 10, marginBottom: 16, background: C.insetBg, padding: "11px 16px", border: `1px solid ${C.border}`, borderRadius: 8 }}>
              {[[" P&L", pv >= 0 ? `+${pv.toFixed(2)}` : pv.toFixed(2), isBE ? "#3B82F6" : pv >= 0 ? "#22C55E" : "#EF4444"], f.rr ? ["R:R", parseFloat(f.rr).toFixed(2), "#818CF8"] : null, ["Result", isBE ? "B/E" : pv > 0 ? "WIN" : "LOSS", isBE ? "#3B82F6" : pv > 0 ? "#22C55E" : "#EF4444"]].filter(Boolean).map(([l, v, c]) => (
                <div key={l} style={{ flex: 1, textAlign: "center" }}>
                  <div style={{ fontSize: 9, color: C.subtext, textTransform: "uppercase", marginBottom: 3 }}>{l}</div>
                  <div style={{ fontSize: 20, fontWeight: 700, color: c }}>{v}</div>
                </div>
              ))}
            </div>
          )}
          <div style={{ marginBottom: 18 }}>
            <div style={sS}>Trade Details</div>
            <div className="modal-grid3" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
              <div style={{ position: "relative" }}>
                <label style={lS}>Asset *</label>
                <input value={f.asset} onChange={e => s("asset", e.target.value)} onFocus={e => e.target.nextSibling.style.display = "block"} onBlur={e => setTimeout(() => { if (e.target.nextSibling) e.target.nextSibling.style.display = "none"; }, 150)} placeholder="Type or select..." style={iS} autoComplete="off" />
                <div style={{ display: "none", position: "absolute", top: "100%", left: 0, right: 0, zIndex: 999, background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, marginTop: 3, maxHeight: 180, overflowY: "auto", boxShadow: "0 8px 24px rgba(0,0,0,.3)" }}>
                  {ASSETS.filter(a => a.toLowerCase().includes(f.asset.toLowerCase()) || !f.asset).map(a => (
                    <div key={a} onMouseDown={() => s("asset", a)} style={{ padding: "7px 10px", fontSize: 11, cursor: "pointer", color: C.text, borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", gap: 7 }}
                      onMouseEnter={e => e.currentTarget.style.background = C.insetBg} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                      <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#22C55E", display: "inline-block", flexShrink: 0 }} />{a}
                    </div>
                  ))}
                </div>
              </div>
              <div><label style={lS}>Direction</label><select value={f.direction} onChange={e => s("direction", e.target.value)} style={iS}><option value="buy">BUY</option><option value="sell">SELL</option></select></div>
              <div style={{ position: "relative" }}><label style={lS}>Date</label><button onClick={() => setShowCal(!showCal)} style={{ ...iS, textAlign: "left", cursor: "pointer" }}>{f.date ? new Date(f.date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "Pick"}</button>{showCal && <MiniCal value={f.date} onPick={v => s("date", v)} onClose={() => setShowCal(false)} />}</div>
              <div><label style={lS}>P&L ($) *</label><input type="number" value={f.pnl} onChange={e => s("pnl", e.target.value)} placeholder="450" step="any" style={iS} /></div>
              <div><label style={lS}>RR</label><input type="number" value={f.rr} onChange={e => s("rr", e.target.value)} placeholder="2.5" step="any" style={iS} /></div>
              <div><label style={lS}>Session</label><select value={f.session} onChange={e => s("session", e.target.value)} style={iS}>{SESSIONS.map(x => <option key={x}>{x}</option>)}</select></div>
            </div>
          </div>
          <div style={{ marginBottom: 18 }}>
            <div style={sS}>Rating</div>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <RatingWidget value={f.rating} onChange={v => s("rating", v)} type={ratingType} size={22} />
              <span style={{ fontSize: 11, color: C.subtext }}>{RATING_LABELS[ratingType][f.rating]}</span>
            </div>
          </div>
          <div style={{ marginBottom: 18 }}>
            <div style={sS}>Type</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 5, background: C.insetBg, border: `1px solid ${C.border}`, borderRadius: 7, padding: 6, minHeight: 34, marginBottom: 8 }}>
              {f.tags.filter(t => !MISTAKE_TAGS.includes(t)).map(t => <span key={t} style={{ display: "inline-flex", alignItems: "center", gap: 4, background: "rgba(34,197,94,.12)", color: "#22C55E", border: "1px solid rgba(34,197,94,.35)", borderRadius: 20, padding: "2px 8px", fontSize: 10, fontWeight: 500 }}>{t}<span style={{ cursor: "pointer", opacity: .7 }} onClick={() => toggleTag(t)}>×</span></span>)}
              {!f.tags.filter(t => !MISTAKE_TAGS.includes(t)).length && <span style={{ fontSize: 10, color: C.subtext, padding: "2px 4px" }}>Add tags…</span>}
            </div>
            <div style={{ display: "flex", gap: 5, marginBottom: 8 }}>
              <input value={ctag} onChange={e => setCtag(e.target.value)} onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addCtag(); } }} placeholder="Custom → Enter" style={{ flex: 1, background: C.inputBg, border: `1px solid ${C.border}`, borderRadius: 6, padding: "6px 9px", color: C.text, fontSize: 11, outline: "none", fontFamily: "inherit" }} />
              <button onClick={addCtag} style={{ background: "rgba(34,197,94,.12)", border: "1px solid rgba(34,197,94,.35)", borderRadius: 6, padding: "6px 12px", color: "#22C55E", fontSize: 10, fontWeight: 600, cursor: "pointer" }}>Add</button>
            </div>
            {savedTags.length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                {savedTags.map(t => <span key={t} style={{ display: "inline-flex", alignItems: "center", gap: 3, background: f.tags.includes(t) ? "rgba(139,92,246,.15)" : C.tagBg, color: f.tags.includes(t) ? "#A78BFA" : C.subtext, border: `1px solid ${f.tags.includes(t) ? "rgba(139,92,246,.35)" : C.border}`, borderRadius: 5, padding: "3px 8px", fontSize: 10, cursor: "pointer" }}><span onClick={() => toggleTag(t)}>{t}</span><span onClick={() => removeSavedTag(t)} style={{ opacity: .5, fontSize: 9, lineHeight: 1, marginLeft: 1 }}>×</span></span>)}
              </div>
            )}
          </div>
          <div style={{ marginBottom: 18 }}>
            <div style={{ ...sS, color: "#EF4444" }}>Mistakes</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 5, background: C.insetBg, border: `1px solid ${C.border}`, borderRadius: 7, padding: 6, minHeight: 34, marginBottom: 8 }}>
              {f.mistakes.map(t => <span key={t} style={{ display: "inline-flex", alignItems: "center", gap: 4, background: "rgba(239,68,68,.12)", color: "#EF4444", border: "1px solid rgba(239,68,68,.35)", borderRadius: 20, padding: "2px 8px", fontSize: 10, fontWeight: 500 }}>{t}<span style={{ cursor: "pointer", opacity: .7 }} onClick={() => toggleMistake(t)}>×</span></span>)}
              {!f.mistakes.length && <span style={{ fontSize: 10, color: C.subtext, padding: "2px 4px" }}>None…</span>}
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
              {MISTAKE_TAGS.map(t => <span key={t} onClick={() => toggleMistake(t)} style={{ background: f.mistakes.includes(t) ? "rgba(239,68,68,.12)" : C.tagBg, color: f.mistakes.includes(t) ? "#EF4444" : C.subtext, border: `1px solid ${f.mistakes.includes(t) ? "rgba(239,68,68,.35)" : C.border}`, borderRadius: 5, padding: "3px 8px", fontSize: 10, cursor: "pointer" }}>{t}</span>)}
            </div>
          </div>
          <div style={{ marginBottom: 18 }}>
            <div style={sS}>Psychology</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <div><label style={lS}>Before</label><select value={f.emotionBefore} onChange={e => s("emotionBefore", e.target.value)} style={iS}>{EMOTIONS.map(e => <option key={e}>{e}</option>)}</select></div>
              <div><label style={lS}>After</label><select value={f.emotionAfter} onChange={e => s("emotionAfter", e.target.value)} style={iS}>{EMOTIONS.map(e => <option key={e}>{e}</option>)}</select></div>
            </div>
          </div>
          <div style={{ marginBottom: 18 }}>
            <div style={sS}>Screenshots ({f.chartImages.length}/5)</div>
            {f.chartImages.length > 0 && (
              <div style={{ display: "grid", gridTemplateColumns: `repeat(${Math.min(f.chartImages.length, 4)},1fr)`, gap: 6, marginBottom: 10 }}>
                {f.chartImages.map((img, i) => (
                  <div key={i} style={{ position: "relative", borderRadius: 8, overflow: "hidden", border: `1px solid ${C.border}`, cursor: "pointer", aspectRatio: "16/10" }} onClick={() => setLbIdx(i)}>
                    <img src={img} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                    <button onClick={e => { e.stopPropagation(); rmImg(i); }} style={{ position: "absolute", top: 3, right: 3, background: "rgba(0,0,0,.75)", border: "none", borderRadius: "50%", width: 18, height: 18, color: "#EF4444", fontSize: 10, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>×</button>
                    <div style={{ position: "absolute", bottom: 3, left: 3, background: "rgba(0,0,0,.6)", borderRadius: 4, padding: "1px 5px", fontSize: 8, color: "#9CA3AF" }}>{i + 1}</div>
                  </div>
                ))}
              </div>
            )}
            {f.chartImages.length < 5 && (
              <div onDrop={e => { e.preventDefault(); Array.from(e.dataTransfer?.files || []).forEach(addImg); }} onDragOver={e => e.preventDefault()} onClick={() => document.getElementById("imgUp").click()} style={{ border: `2px dashed ${C.border}`, borderRadius: 10, padding: 16, textAlign: "center", cursor: "pointer", color: C.subtext, fontSize: 11 }}>
                <div style={{ fontWeight: 500 }}>Paste · Drag · Click — {5 - f.chartImages.length} slots left</div>
                <div style={{ fontSize: 9, color: "#F59E0B", marginTop: 4 }}>⚠ Keep images small — large screenshots may fail to save</div>
              </div>
            )}
            <input id="imgUp" type="file" accept="image/*" multiple style={{ display: "none" }} onChange={e => { Array.from(e.target?.files || []).forEach(addImg); e.target.value = ""; }} />
          </div>
          <div><div style={sS}>Notes</div><textarea value={f.notes} onChange={e => s("notes", e.target.value)} placeholder="Context…" style={{ ...iS, resize: "vertical", minHeight: 70 }} /></div>

{/* Quick Rules Checklist inside trade modal */}
{propRulesInModal && propRulesInModal.length > 0 && (
  <div style={{ marginTop: 18 }}>
    <div style={{ ...sS, color: "#3B82F6" }}>📋 Rule Check</div>
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {propRulesInModal.map(rule => {
        const checked = (f.rulesFollowed || []).includes(rule.id);
        return (
          <div key={rule.id} onClick={() => s("rulesFollowed", checked ? (f.rulesFollowed||[]).filter(x => x !== rule.id) : [...(f.rulesFollowed||[]), rule.id])}
            style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", background: checked ? "rgba(34,197,94,.06)" : C.insetBg, border: `1px solid ${checked ? "rgba(34,197,94,.3)" : C.border}`, borderRadius: 8, cursor: "pointer" }}>
            <div style={{ width: 16, height: 16, borderRadius: 4, border: `2px solid ${checked ? "#22C55E" : C.subtext}`, background: checked ? "#22C55E" : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, transition: "all .15s" }}>
              {checked && <span style={{ color: "#fff", fontSize: 10, lineHeight: 1 }}>✓</span>}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 12, fontWeight: 500, color: C.text }}>{rule.title}</div>
              {rule.description && <div style={{ fontSize: 10, color: C.subtext, marginTop: 1 }}>{rule.description}</div>}
            </div>
            <span style={{ fontSize: 9, padding: "1px 6px", borderRadius: 4, background: "rgba(59,130,246,.1)", color: "#3B82F6", fontWeight: 600 }}>{rule.category}</span>
          </div>
        );
      })}
    </div>
    {(f.rulesFollowed||[]).length > 0 && (
      <div style={{ fontSize: 10, color: "#22C55E", marginTop: 6 }}>✓ {f.rulesFollowed.length}/{propRulesInModal.length} rules checked</div>
    )}
  </div>
)}
        </div>
        <div style={{ padding: "12px 22px 16px", borderTop: `1px solid ${C.border}`, display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button onClick={onClose} style={{ background: "transparent", border: `1px solid ${C.border}`, borderRadius: 7, padding: "7px 16px", color: C.subtext, fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
          <button onClick={submit} style={{ background: "#22C55E", border: "none", borderRadius: 7, padding: "7px 16px", color: "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>{initial ? "Update" : "Save"}</button>
        </div>
      </div>
      {lbIdx !== null && <ImageLightbox images={f.chartImages} startIdx={lbIdx} onClose={() => setLbIdx(null)} />}
    </div>
  );
}

function TagFilterBuilder({ trades, savedTags, ratingType, beCap, onEditTrade }) {
  const C = useColors();
  const [sel, setSel] = useState([]);
  const [mode, setMode] = useState("AND");
  const [lbIdx, setLbIdx] = useState(null);
  const [lbImgs, setLbImgs] = useState([]);
  const [expandedTrades, setExpandedTrades] = useState(new Set());
  const toggle = t => setSel(p => p.includes(t) ? p.filter(x => x !== t) : [...p, t]);
  const filtered = useMemo(() => {
    if (!sel.length) return [];
    return trades.filter(t => {
      const tags = [...(t.tags || []), t.asset, t.session, t.direction];
      return mode === "AND" ? sel.every(s => tags.includes(s)) : sel.some(s => tags.includes(s));
    });
  }, [trades, sel, mode]);
  const stats = useMemo(() => {
    if (!filtered.length) return null;
    const w = filtered.filter(t => t.win), l = filtered.filter(t => !t.win && !t.isBE);
    const net = filtered.reduce((a, t) => a + t.pnl, 0);
    const c = w.length + l.length;
    const wr = c ? w.length / c * 100 : 0;
    const aw = w.length ? w.reduce((a, t) => a + t.pnl, 0) / w.length : 0;
    const al = l.length ? Math.abs(l.reduce((a, t) => a + t.pnl, 0) / l.length) : 0;
    const tp = w.reduce((a, t) => a + t.pnl, 0), tl = Math.abs(l.reduce((a, t) => a + t.pnl, 0));
    const pf = tl > 0 ? tp / tl : tp > 0 ? 999 : 0;
    const ar = filtered.length ? filtered.reduce((a, t) => a + t.rr, 0) / filtered.length : 0;
    return { total: filtered.length, wins: w.length, losses: l.length, net, wr, aw, al, pf, ar };
  }, [filtered]);
  const avail = useMemo(() => {
    const s = new Set();
    trades.forEach(t => (t.tags || []).forEach(tag => s.add(tag)));
    savedTags.forEach(t => s.add(t));
    return Array.from(s).sort();
  }, [trades, savedTags]);
  const cust = avail.filter(t => !MISTAKE_TAGS.includes(t)), mist = avail.filter(t => MISTAKE_TAGS.includes(t));
  const tS = (on, cs) => ({ background: on ? cs[0] : C.tagBg, color: on ? cs[1] : C.subtext, border: `1px solid ${on ? cs[2] : C.border}`, borderRadius: 5, padding: "4px 10px", fontSize: 10, cursor: "pointer", fontWeight: on ? 600 : 400 });
  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, overflow: "hidden" }}>
      <div style={{ padding: "14px 16px", borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: C.text }}>🔬 Tag Combo</span>
        <div style={{ display: "flex", gap: 4 }}>
          {["AND", "OR"].map(m => (
            <button key={m} onClick={() => setMode(m)} style={{ background: mode === m ? "rgba(139,92,246,.15)" : "transparent", border: `1px solid ${mode === m ? "rgba(139,92,246,.4)" : C.border}`, borderRadius: 5, padding: "4px 10px", fontSize: 10, fontWeight: 600, color: mode === m ? "#A78BFA" : C.subtext, cursor: "pointer", fontFamily: "inherit" }}>{m}</button>
          ))}
        </div>
      </div>
      <div style={{ padding: "12px 16px" }}>
        {sel.length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 10, background: C.insetBg, border: `1px solid ${C.border}`, borderRadius: 7, padding: 8 }}>
            {sel.map(t => <span key={t} onClick={() => toggle(t)} style={{ background: "rgba(139,92,246,.15)", color: "#A78BFA", border: "1px solid rgba(139,92,246,.4)", borderRadius: 20, padding: "3px 10px", fontSize: 10, fontWeight: 600, cursor: "pointer" }}>{t} ×</span>)}
          </div>
        )}
        {cust.length > 0 && <div style={{ marginBottom: 8 }}><div style={{ fontSize: 9, color: C.subtext, textTransform: "uppercase", marginBottom: 4 }}>Types</div><div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>{cust.map(t => <span key={t} onClick={() => toggle(t)} style={tS(sel.includes(t), ["rgba(139,92,246,.15)", "#A78BFA", "rgba(139,92,246,.35)"])}>{t}</span>)}</div></div>}
        {mist.length > 0 && <div style={{ marginBottom: 8 }}><div style={{ fontSize: 9, color: C.subtext, textTransform: "uppercase", marginBottom: 4 }}>Mistakes</div><div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>{mist.map(t => <span key={t} onClick={() => toggle(t)} style={tS(sel.includes(t), ["rgba(239,68,68,.12)", "#EF4444", "rgba(239,68,68,.35)"])}>{t}</span>)}</div></div>}
        <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
          {SESSIONS.map(s => <span key={s} onClick={() => toggle(s)} style={tS(sel.includes(s), ["rgba(99,102,241,.12)", "#818CF8", "rgba(99,102,241,.35)"])}>{s}</span>)}
          {ASSETS.map(a => <span key={a} onClick={() => toggle(a)} style={tS(sel.includes(a), ["rgba(34,197,94,.12)", "#22C55E", "rgba(34,197,94,.35)"])}>{a}</span>)}
        </div>
      </div>
      {sel.length > 0 && stats && (
        <>
          <div style={{ borderTop: `1px solid ${C.border}`, padding: "14px 16px" }}>
            <div style={{ fontSize: 10, color: "#818CF8", fontWeight: 600, marginBottom: 10 }}>{stats.total} matched trades</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 8, marginBottom: 8 }}>
              {[["WR", `${stats.wr.toFixed(1)}%`, stats.wr >= 50 ? "#22C55E" : "#EF4444"], ["Net", `${stats.net >= 0 ? "+" : ""}$${stats.net.toFixed(0)}`, stats.net >= 0 ? "#22C55E" : "#EF4444"], ["PF", stats.pf >= 999 ? "∞" : stats.pf.toFixed(2), stats.pf >= 1.5 ? "#22C55E" : "#EF4444"], ["Avg RR", stats.ar.toFixed(2), "#818CF8"]].map(([l, v, c]) => (
                <div key={l} style={{ background: C.insetBg, border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 12px", textAlign: "center" }}>
                  <div style={{ fontSize: 9, color: C.subtext, textTransform: "uppercase", marginBottom: 4 }}>{l}</div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: c }}>{v}</div>
                </div>
              ))}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 8 }}>
              {[["Avg Win", `+$${stats.aw.toFixed(0)}`, "#22C55E"], ["Avg Loss", `-$${stats.al.toFixed(0)}`, "#EF4444"], ["W/L", `${stats.wins}W / ${stats.losses}L`, C.subtext]].map(([l, v, c]) => (
                <div key={l} style={{ background: C.insetBg, border: `1px solid ${C.border}`, borderRadius: 8, padding: "8px 12px", textAlign: "center" }}>
                  <div style={{ fontSize: 9, color: C.subtext, textTransform: "uppercase", marginBottom: 3 }}>{l}</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: c }}>{v}</div>
                </div>
              ))}
            </div>
          </div>
          <div style={{ borderTop: `1px solid ${C.border}`, padding: "14px 16px" }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.text, marginBottom: 12 }}>Matched Trades</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {[...filtered].sort((a, b) => new Date(b.date) - new Date(a.date)).map(t => {
                const imgs = t.chartImages || (t.chartImage ? [t.chartImage] : []);
                return (
                  <div key={t.id} style={{ background: C.insetBg, border: `1px solid ${t.isBE ? "rgba(59,130,246,.25)" : t.win ? "rgba(34,197,94,.25)" : "rgba(239,68,68,.25)"}`, borderRadius: 10, overflow: "hidden" }}>
                    <div onClick={() => setExpandedTrades(prev => { const s = new Set(prev); s.has(t.id) ? s.delete(t.id) : s.add(t.id); return s; })} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 14px", borderBottom: expandedTrades.has(t.id) ? `1px solid ${C.border}` : "none", cursor: "pointer" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <div style={{ width: 8, height: 8, borderRadius: "50%", background: t.isBE ? "#3B82F6" : t.win ? "#22C55E" : "#EF4444", flexShrink: 0 }} />
                        <div>
                          <span style={{ fontSize: 14, fontWeight: 700, color: C.text }}>{t.asset}</span>
                          <span style={{ fontSize: 10, color: t.direction === "buy" ? "#22C55E" : "#EF4444", marginLeft: 6, fontWeight: 600 }}>{t.direction.toUpperCase()}</span>
                        </div>
                        <span style={{ fontSize: 10, color: C.subtext }}>{new Date(t.date).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}</span>
                        {t.session && <span style={{ fontSize: 9, padding: "2px 7px", borderRadius: 10, background: C.border, color: C.subtext }}>{t.session}</span>}
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <div style={{ textAlign: "right" }}>
                          <div style={{ fontSize: 16, fontWeight: 800, color: t.isBE ? "#3B82F6" : t.pnl >= 0 ? "#22C55E" : "#EF4444" }}>{t.pnl >= 0 ? "+" : ""}${t.pnl.toFixed(2)}</div>
                          {t.rr > 0 && <div style={{ fontSize: 10, color: "#818CF8" }}>{t.rr.toFixed(2)}R</div>}
                        </div>
                        <span style={{ fontSize: 10, fontWeight: 700, padding: "3px 9px", borderRadius: 10, background: t.isBE ? "rgba(59,130,246,.12)" : t.win ? "rgba(34,197,94,.12)" : "rgba(239,68,68,.12)", color: t.isBE ? "#3B82F6" : t.win ? "#22C55E" : "#EF4444", border: `1px solid ${t.isBE ? "rgba(59,130,246,.3)" : t.win ? "rgba(34,197,94,.3)" : "rgba(239,68,68,.3)"}` }}>{t.isBE ? "B/E" : t.win ? "WIN" : "LOSS"}</span>
                        <span style={{ fontSize: 10, color: C.subtext, marginLeft: 4 }}>{expandedTrades.has(t.id) ? "▲" : "▼"}</span>
                        {onEditTrade && <button onClick={e => { e.stopPropagation(); onEditTrade(t); }} style={{ background: "none", border: `1px solid ${C.border}`, borderRadius: 5, padding: "3px 8px", color: C.subtext, fontSize: 10, cursor: "pointer" }}>Edit</button>}
                      </div>
                    </div>
                    {expandedTrades.has(t.id) && (
                      <div style={{ padding: "10px 14px" }}>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: (t.notes || imgs.length) ? 10 : 0 }}>
                          {(t.tags || []).map(tg => <span key={tg} style={{ fontSize: 9, padding: "2px 7px", borderRadius: 10, background: MISTAKE_TAGS.includes(tg) ? "rgba(239,68,68,.1)" : "rgba(139,92,246,.1)", color: MISTAKE_TAGS.includes(tg) ? "#EF4444" : "#A78BFA", border: `1px solid ${MISTAKE_TAGS.includes(tg) ? "rgba(239,68,68,.25)" : "rgba(139,92,246,.25)"}` }}>{tg}</span>)}
                          {(t.mistakes || []).map(tg => <span key={tg} style={{ fontSize: 9, padding: "2px 7px", borderRadius: 10, background: "rgba(239,68,68,.1)", color: "#EF4444", border: "1px solid rgba(239,68,68,.25)" }}>⚠ {tg}</span>)}
                          {t.emotionBefore && <span style={{ fontSize: 9, padding: "2px 7px", borderRadius: 10, background: C.border, color: C.subtext }}>😐 {t.emotionBefore}</span>}
                          {t.rating > 0 && <span style={{ fontSize: 9, padding: "2px 7px", borderRadius: 10, background: "rgba(245,158,11,.1)", color: "#F59E0B", border: "1px solid rgba(245,158,11,.25)" }}>{'★'.repeat(t.rating)} {t.rating}/5</span>}
                        </div>
                        {t.notes && <div style={{ fontSize: 11, color: C.subtext, lineHeight: 1.6, fontStyle: "italic", marginBottom: imgs.length ? 10 : 0, padding: "8px 10px", background: C.card, borderRadius: 7, border: `1px solid ${C.border}` }}>"{t.notes}"</div>}
                        {imgs.length > 0 && (
                          <div style={{ marginTop: t.notes ? 8 : 0 }}>
                            <div style={{ display: "flex", gap: 6, overflowX: "auto", paddingBottom: 4 }}>
                              {imgs.map((img, i) => (
                                <div key={i} onClick={() => { setLbImgs(imgs); setLbIdx(i); }} style={{ flexShrink: 0, width: 120, height: 72, borderRadius: 7, overflow: "hidden", border: `1px solid ${C.border}`, cursor: "pointer", position: "relative" }}>
                                  <img src={img} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                                  {imgs.length > 1 && <div style={{ position: "absolute", bottom: 3, right: 4, fontSize: 8, color: "#fff", background: "rgba(0,0,0,.6)", borderRadius: 3, padding: "1px 5px", fontWeight: 600 }}>{i + 1}/{imgs.length}</div>}
                                </div>
                              ))}
                            </div>
                            <div style={{ fontSize: 9, color: C.subtext, marginTop: 4 }}>{imgs.length} screenshot{imgs.length > 1 ? "s" : ""} · scroll to view all</div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
      {lbIdx !== null && <ImageLightbox images={lbImgs} startIdx={lbIdx} onClose={() => { setLbIdx(null); setLbImgs([]); }} />}
    </div>
  );
}

function CalDayPopover({ day, trades, onClose, onEdit, onDelete }) {
  const C = useColors();
  if (!day || !trades.length) return null;
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.7)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, backdropFilter: "blur(3px)" }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, width: "100%", maxWidth: 420, maxHeight: "70vh", overflowY: "auto" }}>
        <div style={{ padding: "14px 18px", borderBottom: `1px solid ${C.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div><div style={{ fontSize: 14, fontWeight: 600, color: C.text }}>{new Date(trades[0].date).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}</div><div style={{ fontSize: 11, color: C.subtext, marginTop: 2 }}>{trades.length} trade{trades.length !== 1 ? "s" : ""}</div></div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 16, fontWeight: 700, color: day.pnl > 0 ? "#22C55E" : day.pnl < 0 ? "#EF4444" : "#3B82F6" }}>{day.pnl >= 0 ? "+" : ""}${day.pnl.toFixed(2)}</span>
            <button onClick={onClose} style={{ background: "none", border: `1px solid ${C.border}`, borderRadius: 6, padding: "4px 10px", color: C.subtext, cursor: "pointer", fontSize: 11 }}>✕</button>
          </div>
        </div>
        <div style={{ padding: "8px 12px" }}>
          {trades.map(t => (
  <div key={t.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px", marginBottom: 4, background: C.insetBg, border: `1px solid ${C.border}`, borderRadius: 8 }}>
    <div onClick={() => onEdit(t)} style={{ display: "flex", alignItems: "center", gap: 10, flex: 1, cursor: "pointer" }}>
      <div style={{ width: 6, height: 6, borderRadius: "50%", background: t.isBE ? "#3B82F6" : t.win ? "#22C55E" : "#EF4444", flexShrink: 0 }} />
      <div>
        <span style={{ fontWeight: 600, fontSize: 13, color: C.text }}>{t.asset}</span>
        <span style={{ fontSize: 9, color: t.direction === "buy" ? "#22C55E" : "#EF4444" }}> {t.direction.toUpperCase()}</span>
        <div style={{ display: "flex", gap: 3, marginTop: 3 }}>{(t.tags || []).slice(0, 3).map(tg => <span key={tg} style={{ fontSize: 8, padding: "1px 5px", borderRadius: 8, background: "rgba(139,92,246,.08)", color: "#A78BFA" }}>{tg}</span>)}</div>
      </div>
    </div>
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <div style={{ textAlign: "right" }}>
        <div style={{ fontWeight: 700, fontSize: 14, color: t.isBE ? "#3B82F6" : t.pnl >= 0 ? "#22C55E" : "#EF4444" }}>{t.pnl >= 0 ? "+" : ""}${t.pnl.toFixed(2)}</div>
        <div style={{ fontSize: 9, color: C.subtext }}>{t.isBE ? "B/E" : t.win ? "WIN" : "LOSS"}</div>
      </div>
      <button onClick={() => { if(window.confirm("Delete trade?")) { onDelete(t.id); } }}
        style={{ background: "rgba(239,68,68,.1)", border: "1px solid rgba(239,68,68,.3)", borderRadius: 6, padding: "4px 8px", color: "#EF4444", fontSize: 10, fontWeight: 600, cursor: "pointer", flexShrink: 0 }}>
        Del
      </button>
    </div>
  </div>
))}
        </div>
      </div>
    </div>
  );
}

function TradeHeatmap({ trades, beCap, onDayClick, C }) {
  const dayMap = useMemo(() => {
    const m = {};
    trades.forEach(t => {
      const d = new Date(t.date);
      if (isNaN(d.getTime())) return;
      const key = d.toISOString().slice(0, 10);
      if (!m[key]) m[key] = { pnl: 0, trades: [] };
      m[key].pnl += t.pnl;
      m[key].trades.push(t);
    });
    return m;
  }, [trades]);
  const cells = useMemo(() => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const dayOfWeek = today.getDay();
    const daysToLastMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    const startMonday = new Date(today);
    startMonday.setDate(today.getDate() - daysToLastMonday - (16 * 7 - 7));
    const result = [];
    for (let i = 0; i < 16 * 7; i++) {
      const date = new Date(startMonday); date.setDate(startMonday.getDate() + i);
      const key = date.toISOString().slice(0, 10);
      result.push({ date, key, data: dayMap[key] || null });
    }
    return result;
  }, [dayMap]);
  const weeks = useMemo(() => { const w = []; for (let i = 0; i < 16; i++) w.push(cells.slice(i * 7, i * 7 + 7)); return w; }, [cells]);
  const getColor = (data) => {
    if (!data || data.trades.length === 0) return C.isLight ? "#e2e8f0" : "#1a1b22";
    const pnl = data.pnl, abs = Math.abs(pnl);
    if (Math.abs(pnl) <= beCap) return "#3B82F6";
    if (pnl > 0) return abs < 100 ? "#166534" : abs < 300 ? "#16a34a" : "#22C55E";
    return abs < 100 ? "#7f1d1d" : abs < 300 ? "#b91c1c" : "#EF4444";
  };
  const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const monthLabels = useMemo(() => weeks.map((week, wi) => {
    const firstDay = week[0].date;
    const prevFirstDay = wi > 0 ? weeks[wi - 1][0].date : null;
    return (!prevFirstDay || firstDay.getMonth() !== prevFirstDay.getMonth())
      ? { wi, label: firstDay.toLocaleDateString("en-US", { month: "short" }) } : null;
  }), [weeks]);
  const CELL_SIZE = 14, GAP = 3;
  return (
    <div style={{ overflowX: "auto", paddingBottom: 4 }}>
      <div style={{ display: "inline-flex", flexDirection: "column", gap: 0 }}>
        <div style={{ display: "flex", marginLeft: 32, marginBottom: 4 }}>
          {monthLabels.map((ml, wi) => <div key={wi} style={{ width: CELL_SIZE, marginRight: GAP, fontSize: 9, color: C.subtext, whiteSpace: "nowrap", overflow: "visible", minWidth: CELL_SIZE }}>{ml ? ml.label : ""}</div>)}
        </div>
        <div style={{ display: "flex", gap: 0 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: GAP, marginRight: 6, paddingTop: 0 }}>
            {DAY_LABELS.map((d, i) => <div key={i} style={{ height: CELL_SIZE, fontSize: 9, color: C.subtext, lineHeight: `${CELL_SIZE}px`, width: 26, textAlign: "right", userSelect: "none" }}>{d}</div>)}
          </div>
          <div style={{ display: "flex", gap: GAP }}>
            {weeks.map((week, wi) => (
              <div key={wi} style={{ display: "flex", flexDirection: "column", gap: GAP }}>
                {week.map((cell, di) => {
                  const color = getColor(cell.data), hasTrades = cell.data && cell.data.trades.length > 0;
                  const isToday = cell.key === new Date().toISOString().slice(0, 10);
                  return (
                    <div key={di} onClick={() => hasTrades && onDayClick && onDayClick(cell)}
                      title={hasTrades ? `${cell.key}  |  $${cell.data.pnl >= 0 ? "+" : ""}${cell.data.pnl.toFixed(2)}  |  ${cell.data.trades.length} trade${cell.data.trades.length !== 1 ? "s" : ""}` : cell.key}
                      style={{ width: CELL_SIZE, height: CELL_SIZE, borderRadius: 3, background: color, cursor: hasTrades ? "pointer" : "default", boxSizing: "border-box", border: isToday ? "1.5px solid #A78BFA" : "none", flexShrink: 0 }}
                      onMouseEnter={e => { if (hasTrades) e.currentTarget.style.opacity = "0.8"; }}
                      onMouseLeave={e => { e.currentTarget.style.opacity = "1"; }} />
                  );
                })}
              </div>
            ))}
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 10, marginLeft: 32, fontSize: 9, color: C.subtext }}>
          <span>Less</span>
          {[C.isLight ? "#e2e8f0" : "#1a1b22", "#166534", "#16a34a", "#22C55E"].map((c, i) => <div key={i} style={{ width: CELL_SIZE, height: CELL_SIZE, borderRadius: 3, background: c }} />)}
          <span>More Profit</span>
          <div style={{ width: 1, height: 10, background: C.border, margin: "0 4px" }} />
          {["#7f1d1d", "#b91c1c", "#EF4444"].map((c, i) => <div key={i} style={{ width: CELL_SIZE, height: CELL_SIZE, borderRadius: 3, background: c }} />)}
          <span>Loss</span>
          <div style={{ width: CELL_SIZE, height: CELL_SIZE, borderRadius: 3, background: "#3B82F6" }} />
          <span>B/E</span>
        </div>
      </div>
    </div>
  );
}

function ConsistencyPanel({ trades, C }) {
  const data = useMemo(() => {
    if (!trades.length) return { score: 0, tradingDays: 0, profitDays: 0, lossDays: 0, beDays: 0, avgDailyPnl: 0, bestDay: null, worstDay: null, currentStreak: 0, streakType: null, longestWinStreak: 0, longestLossStreak: 0, dailyPnls: [] };
    const dayMap = {};
    [...trades].sort((a, b) => new Date(a.date) - new Date(b.date)).forEach(t => {
      const k = t.date.slice(0, 10);
      if (!dayMap[k]) dayMap[k] = { pnl: 0, wins: 0, losses: 0, total: 0 };
      dayMap[k].pnl += t.pnl; dayMap[k].total++;
      if (t.win) dayMap[k].wins++; else if (!t.isBE) dayMap[k].losses++;
    });
    const days = Object.entries(dayMap).sort((a, b) => a[0].localeCompare(b[0]));
    const profitDays = days.filter(([, d]) => d.pnl > 35).length;
    const lossDays = days.filter(([, d]) => d.pnl < -35).length;
    const beDays = days.length - profitDays - lossDays;
    const bestDay = days.reduce((a, b) => b[1].pnl > a[1].pnl ? b : a, days[0]);
    const worstDay = days.reduce((a, b) => b[1].pnl < a[1].pnl ? b : a, days[0]);
    const avgDailyPnl = days.reduce((a, [, d]) => a + d.pnl, 0) / days.length;
    let longestW = 0, longestL = 0, curW = 0, curL = 0;
    days.forEach(([, d]) => {
      const isWin = d.pnl > 35, isLoss = d.pnl < -35;
      if (isWin) { curW++; curL = 0; if (curW > longestW) longestW = curW; }
      else if (isLoss) { curL++; curW = 0; if (curL > longestL) longestL = curL; }
      else { curW = 0; curL = 0; }
    });
    let curStreak = 0, curType = null;
    const lastDay = days[days.length - 1]?.[1];
    if (lastDay) {
      if (lastDay.pnl > 35) { curType = "W"; let s = 0; for (let i = days.length - 1; i >= 0; i--) { if (days[i][1].pnl > 35) s++; else break; } curStreak = s; }
      else if (lastDay.pnl < -35) { curType = "L"; let s = 0; for (let i = days.length - 1; i >= 0; i--) { if (days[i][1].pnl < -35) s++; else break; } curStreak = s; }
    }
    const consistency = Math.max(0, Math.min(100, Math.round((profitDays / days.length) * 70 + (longestW / Math.max(days.length, 1)) * 20 + (avgDailyPnl > 0 ? 10 : 0))));
    const dailyPnls = days.slice(-20).map(([date, d]) => ({ date, pnl: d.pnl, wins: d.wins, losses: d.losses }));
    return { score: consistency, tradingDays: days.length, profitDays, lossDays, beDays, avgDailyPnl, bestDay, worstDay, currentStreak: curStreak, streakType: curType, longestWinStreak: longestW, longestLossStreak: longestL, dailyPnls };
  }, [trades]);
  const scoreCol = data.score >= 70 ? "#22C55E" : data.score >= 45 ? "#F59E0B" : "#EF4444";
  const scoreLabel = data.score >= 80 ? "Elite" : data.score >= 65 ? "Consistent" : data.score >= 45 ? "Developing" : data.score >= 25 ? "Inconsistent" : "Needs Work";
  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, overflow: "hidden" }}>
      <div style={{ padding: "12px 16px", borderBottom: `1px solid ${C.border}` }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: C.text }}>📊 Consistency Score</span>
      </div>
      <div style={{ padding: "16px" }}>
        <div style={{ display: "flex", gap: 20, alignItems: "center", marginBottom: 16 }}>
          <div style={{ position: "relative", width: 90, height: 90, flexShrink: 0 }}>
            <svg width="90" height="90" style={{ transform: "rotate(-90deg)" }}>
              <circle cx="45" cy="45" r="36" fill="none" stroke={C.border} strokeWidth="8" />
              <circle cx="45" cy="45" r="36" fill="none" stroke={scoreCol} strokeWidth="8" strokeLinecap="round"
                strokeDasharray={`${2 * Math.PI * 36}`}
                strokeDashoffset={`${2 * Math.PI * 36 * (1 - data.score / 100)}`}
                style={{ transition: "stroke-dashoffset 1s" }} />
            </svg>
            <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
              <div style={{ fontSize: 20, fontWeight: 800, color: scoreCol, lineHeight: 1 }}>{data.score}</div>
              <div style={{ fontSize: 8, color: C.subtext, marginTop: 2 }}>/100</div>
            </div>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: scoreCol, marginBottom: 4 }}>{scoreLabel}</div>
            <div style={{ fontSize: 11, color: C.subtext, lineHeight: 1.6 }}>{data.tradingDays} trading days · avg ${data.avgDailyPnl.toFixed(0)}/day</div>
            <div style={{ display: "flex", gap: 10, marginTop: 8, fontSize: 10 }}>
              <span style={{ color: "#22C55E" }}>🟢 {data.profitDays} green</span>
              <span style={{ color: "#EF4444" }}>🔴 {data.lossDays} red</span>
              <span style={{ color: "#3B82F6" }}>🔵 {data.beDays} flat</span>
            </div>
          </div>
        </div>
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 10, color: C.subtext, marginBottom: 6, fontWeight: 600, textTransform: "uppercase" }}>Day Distribution</div>
          <div style={{ height: 10, borderRadius: 5, overflow: "hidden", display: "flex" }}>
            <div style={{ width: `${data.profitDays / data.tradingDays * 100}%`, background: "#22C55E" }} />
            <div style={{ width: `${data.beDays / data.tradingDays * 100}%`, background: "#3B82F6" }} />
            <div style={{ width: `${data.lossDays / data.tradingDays * 100}%`, background: "#EF4444" }} />
          </div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 8, marginBottom: 16 }}>
          {[
            ["Current", data.streakType ? `${data.streakType === "W" ? "+" : "-"}${data.currentStreak}d` : "—", data.streakType === "W" ? "#22C55E" : data.streakType === "L" ? "#EF4444" : C.subtext],
            ["Best Run", `${data.longestWinStreak}d`, "#22C55E"],
            ["Worst Run", `${data.longestLossStreak}d`, "#EF4444"],
            ["Avg/Day", `${data.avgDailyPnl >= 0 ? "+" : ""}$${data.avgDailyPnl.toFixed(0)}`, data.avgDailyPnl >= 0 ? "#22C55E" : "#EF4444"],
          ].map(([l, v, c]) => (
            <div key={l} style={{ background: C.insetBg, border: `1px solid ${C.border}`, borderRadius: 8, padding: "8px 10px", textAlign: "center" }}>
              <div style={{ fontSize: 8, color: C.subtext, textTransform: "uppercase", marginBottom: 4 }}>{l}</div>
              <div style={{ fontSize: 15, fontWeight: 700, color: c }}>{v}</div>
            </div>
          ))}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 16 }}>
          {[[data.bestDay, "Best Day", "#22C55E", "rgba(34,197,94,.07)"], [data.worstDay, "Worst Day", "#EF4444", "rgba(239,68,68,.07)"]].map(([day, label, col, bg]) => day && (
            <div key={label} style={{ background: bg, border: `1px solid ${col}30`, borderRadius: 8, padding: "10px 12px" }}>
              <div style={{ fontSize: 9, color: C.subtext, textTransform: "uppercase", marginBottom: 4 }}>{label}</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: col }}>{day[1].pnl >= 0 ? "+" : ""}${day[1].pnl.toFixed(0)}</div>
              <div style={{ fontSize: 10, color: C.subtext, marginTop: 2 }}>{new Date(day[0] + "T12:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}</div>
            </div>
          ))}
        </div>
        {data.dailyPnls.length > 0 && (() => {
          const maxAbs = Math.max(...data.dailyPnls.map(x => Math.abs(x.pnl)), 1);
          const MID_H = 60;
          return (
            <div>
              <div style={{ fontSize: 10, color: C.subtext, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".08em", marginBottom: 12 }}>Last {data.dailyPnls.length} Trading Days</div>
              <div style={{ position: "relative", width: "100%", marginBottom: 6 }}>
                <div style={{ position: "absolute", top: MID_H, left: 0, right: 0, height: 1, background: C.border, zIndex: 1 }} />
                <div style={{ display: "flex", gap: 3, alignItems: "flex-start", height: MID_H * 2 + 2 }}>
                  {data.dailyPnls.map((d, i) => {
                    const pct = Math.abs(d.pnl) / maxAbs;
                    const barH = Math.max(pct * (MID_H - 6), 3);
                    const col = d.pnl > 35 ? "#22C55E" : d.pnl < -35 ? "#EF4444" : "#3B82F6";
                    const glow = d.pnl > 35 ? "rgba(34,197,94,.3)" : d.pnl < -35 ? "rgba(239,68,68,.3)" : "rgba(59,130,246,.3)";
                    const isProfit = d.pnl > 35, isLoss = d.pnl < -35;
                    return (
                      <div key={i} title={`${d.date}: ${d.pnl >= 0 ? "+" : ""}$${d.pnl.toFixed(2)} · ${d.wins}W ${d.losses}L`}
                        style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", height: "100%", cursor: "default" }}>
                        <div style={{ flex: 1, display: "flex", alignItems: "flex-end", width: "100%", paddingBottom: 1 }}>
                          {isProfit && <div style={{ width: "100%", height: barH, background: col, borderRadius: "3px 3px 1px 1px", boxShadow: `0 -2px 8px ${glow}`, opacity: 0.88, minWidth: 4 }} />}
                        </div>
                        <div style={{ width: "100%", height: 2, background: col, opacity: 0.4, flexShrink: 0 }} />
                        <div style={{ flex: 1, display: "flex", alignItems: "flex-start", width: "100%", paddingTop: 1 }}>
                          {isLoss && <div style={{ width: "100%", height: barH, background: col, borderRadius: "1px 1px 3px 3px", boxShadow: `0 2px 8px ${glow}`, opacity: 0.88, minWidth: 4 }} />}
                          {!isProfit && !isLoss && d.pnl !== 0 && <div style={{ width: "100%", height: 5, background: "#3B82F6", borderRadius: 2, opacity: 0.7, minWidth: 4, marginTop: 2 }} />}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div style={{ display: "flex", gap: 3, marginTop: 6 }}>
                  {data.dailyPnls.map((d, i) => (
                    <div key={i} style={{ flex: 1, textAlign: "center", fontSize: 7, color: C.subtext, lineHeight: 1, whiteSpace: "nowrap", overflow: "hidden" }}>
                      {new Date(d.date + "T12:00").toLocaleDateString("en-US", { month: "numeric", day: "numeric" })}
                    </div>
                  ))}
                </div>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 8, color: C.subtext, marginBottom: 8, paddingTop: 2 }}>
                <span>${data.dailyPnls[0]?.pnl >= 0 ? "+" : ""}{data.dailyPnls[0]?.pnl.toFixed(0)}</span>
                <span style={{ color: "#9CA3AF" }}>P&L timeline</span>
                <span>${data.dailyPnls[data.dailyPnls.length - 1]?.pnl >= 0 ? "+" : ""}{data.dailyPnls[data.dailyPnls.length - 1]?.pnl.toFixed(0)}</span>
              </div>
              <div style={{ display: "flex", gap: 12, fontSize: 9 }}>
                {[["#22C55E", "Profit"], ["#EF4444", "Loss"], ["#3B82F6", "Flat"]].map(([c, l]) => (
                  <span key={l} style={{ display: "flex", alignItems: "center", gap: 4, color: C.subtext }}>
                    <span style={{ width: 8, height: 8, borderRadius: 2, background: c, display: "inline-block" }} />{l} day
                  </span>
                ))}
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
}

function EconomicCalendar({ C }) {
  const [calTab, setCalTab] = useState("calendar");
  const [weekOffset, setWeekOffset] = useState(0);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeImps, setActiveImps] = useState(new Set(["HIGH", "MEDIUM"]));
  const [activeCur, setActiveCur] = useState("ALL");
  const [userTZ, setUserTZ] = useState(() => Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [showTZPicker, setShowTZPicker] = useState(false);
  const [activeHCur, setActiveHCur] = useState("ALL");
  const [tzSearch, setTzSearch] = useState("");

  const BANK_HOLIDAYS = [
    { date:"2025-05-26", name:"Memorial Day", country:"US", cur:"USD" },
    { date:"2025-06-19", name:"Juneteenth", country:"US", cur:"USD" },
    { date:"2025-07-04", name:"Independence Day", country:"US", cur:"USD" },
    { date:"2025-08-25", name:"Summer Bank Holiday", country:"UK", cur:"GBP" },
    { date:"2025-09-01", name:"Labor Day", country:"US", cur:"USD" },
    { date:"2025-10-13", name:"Thanksgiving (CA)", country:"CA", cur:"CAD" },
    { date:"2025-11-11", name:"Veterans Day", country:"US", cur:"USD" },
    { date:"2025-11-27", name:"Thanksgiving", country:"US", cur:"USD" },
    { date:"2025-12-25", name:"Christmas Day", country:"US", cur:"USD" },
    { date:"2025-12-25", name:"Christmas Day", country:"UK", cur:"GBP" },
    { date:"2025-12-25", name:"Christmas Day", country:"EU", cur:"EUR" },
    { date:"2025-12-26", name:"Boxing Day", country:"UK", cur:"GBP" },
    { date:"2026-01-01", name:"New Year's Day", country:"US", cur:"USD" },
    { date:"2026-01-01", name:"New Year's Day", country:"UK", cur:"GBP" },
    { date:"2026-01-19", name:"MLK Day", country:"US", cur:"USD" },
    { date:"2026-02-16", name:"Presidents Day", country:"US", cur:"USD" },
    { date:"2026-03-30", name:"Good Friday", country:"US", cur:"USD" },
    { date:"2026-03-30", name:"Good Friday", country:"UK", cur:"GBP" },
    { date:"2026-04-03", name:"Easter Monday", country:"UK", cur:"GBP" },
    { date:"2026-04-03", name:"Easter Monday", country:"EU", cur:"EUR" },
    { date:"2026-05-25", name:"Memorial Day", country:"US", cur:"USD" },
    { date:"2026-06-19", name:"Juneteenth", country:"US", cur:"USD" },
    { date:"2026-07-04", name:"Independence Day", country:"US", cur:"USD" },
    { date:"2026-09-07", name:"Labor Day", country:"US", cur:"USD" },
    { date:"2026-11-26", name:"Thanksgiving", country:"US", cur:"USD" },
    { date:"2026-12-25", name:"Christmas Day", country:"US", cur:"USD" },
    { date:"2026-12-25", name:"Christmas Day", country:"UK", cur:"GBP" },
    { date:"2026-12-26", name:"Boxing Day", country:"UK", cur:"GBP" },
  ];

  const FALLBACK_EVENTS = [
    { date:"2026-04-14", time:"08:30", currency:"USD", name:"Core Retail Sales m/m", impact:"HIGH", previous:"0.3%", forecast:"0.4%" },
    { date:"2026-04-14", time:"08:30", currency:"USD", name:"Retail Sales m/m", impact:"HIGH", previous:"0.2%", forecast:"0.3%" },
    { date:"2026-04-16", time:"08:30", currency:"USD", name:"Initial Jobless Claims", impact:"HIGH", previous:"223K", forecast:"220K" },
    { date:"2026-04-16", time:"09:15", currency:"EUR", name:"ECB Interest Rate Decision", impact:"HIGH", previous:"2.40%", forecast:"2.40%" },
    { date:"2026-04-16", time:"09:45", currency:"EUR", name:"ECB Press Conference", impact:"HIGH", previous:"", forecast:"" },
    { date:"2026-04-30", time:"14:00", currency:"USD", name:"FOMC Statement + Rate Decision", impact:"HIGH", previous:"4.50%", forecast:"4.50%" },
    { date:"2026-05-02", time:"08:30", currency:"USD", name:"Non-Farm Payrolls", impact:"HIGH", previous:"228K", forecast:"135K" },
    { date:"2026-05-02", time:"08:30", currency:"USD", name:"Unemployment Rate", impact:"HIGH", previous:"4.2%", forecast:"4.3%" },
  ];

  const convertETtoUserTZ = useCallback((timeStr, dateStr) => {
    if (!timeStr || timeStr === "All Day" || !dateStr) return timeStr || "All Day";
    const cleaned = timeStr.trim();
    if (!/^\d{1,2}:\d{2}(am|pm)?$/i.test(cleaned) && !/^\d{1,2}:\d{2}$/.test(cleaned)) return cleaned;
    try {
      let h, m;
      const match12 = cleaned.match(/^(\d{1,2}):(\d{2})(am|pm)$/i);
      const match24 = cleaned.match(/^(\d{1,2}):(\d{2})$/);
      if (match12) {
        h = parseInt(match12[1]); m = parseInt(match12[2]);
        const ampm = match12[3].toLowerCase();
        if (ampm === "pm" && h < 12) h += 12;
        if (ampm === "am" && h === 12) h = 0;
      } else if (match24) {
        h = parseInt(match24[1]); m = parseInt(match24[2]);
      } else { return cleaned; }
      const [yr, mo, dy] = dateStr.split("-").map(Number);
      const testDate = new Date(Date.UTC(yr, mo - 1, dy, h + 5, m));
      const etFormatter = new Intl.DateTimeFormat("en-US", { timeZone: "America/New_York", timeZoneName: "short" });
      const isDST = etFormatter.format(testDate).includes("EDT");
      const etOffsetMins = isDST ? 240 : 300;
      const utcMs = Date.UTC(yr, mo - 1, dy, h, m) + etOffsetMins * 60000;
      return new Date(utcMs).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: true, timeZone: userTZ });
    } catch (e) { return timeStr; }
  }, [userTZ]);

  const getWeekRange = useCallback((offset) => {
    const now = new Date();
    const day = now.getDay();
    const diffToMon = day === 0 ? -6 : 1 - day;
    const mon = new Date(now);
    mon.setDate(now.getDate() + diffToMon + offset * 7);
    mon.setHours(0, 0, 0, 0);
    const sun = new Date(mon);
    sun.setDate(mon.getDate() + 6);
    sun.setHours(23, 59, 59, 999);
    return { start: mon, end: sun };
  }, []);

  const weekLabel = useMemo(() => {
    const { start, end } = getWeekRange(weekOffset);
    const opts = { month: "short", day: "numeric" };
    return start.toLocaleDateString("en-US", opts) + " – " + end.toLocaleDateString("en-US", opts);
  }, [weekOffset, getWeekRange]);

  const parseFFDate = (raw) => {
    if (!raw) return null;
    try {
      const m1 = raw.match(/(\w+)\s+(\d+),\s+(\d+)/);
      if (m1) {
        const MONTHS = { January:1, February:2, March:3, April:4, May:5, June:6, July:7, August:8, September:9, October:10, November:11, December:12, Jan:1, Feb:2, Mar:3, Apr:4, Jun:6, Jul:7, Aug:8, Sep:9, Oct:10, Nov:11, Dec:12 };
        const mo = MONTHS[m1[1]];
        if (mo) return `${m1[3]}-${String(mo).padStart(2,"0")}-${String(parseInt(m1[2])).padStart(2,"0")}`;
      }
      const d = new Date(raw);
      if (!isNaN(d.getTime())) return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
    } catch {}
    return null;
  };

  useEffect(() => {
    const thisWeekUrl = "https://nfs.faireconomy.media/ff_calendar_thisweek.xml";
    const nextWeekUrl = "https://nfs.faireconomy.media/ff_calendar_nextweek.xml";
    const proxy = (u) => "https://api.allorigins.win/raw?url=" + encodeURIComponent(u);
    const parseXML = (xml) => {
      const parsed = [];
      try {
        const doc = new DOMParser().parseFromString(xml, "text/xml");
        doc.querySelectorAll("event").forEach(ev => {
          const impactRaw = ev.querySelector("impact")?.textContent?.trim() || "";
          const impact = impactRaw === "High" ? "HIGH" : impactRaw === "Medium" ? "MEDIUM" : "LOW";
          const dateRaw = ev.querySelector("date")?.textContent?.trim() || "";
          const parsedDate = parseFFDate(dateRaw);
          if (!parsedDate) return;
          const timeRaw = ev.querySelector("time")?.textContent?.trim() || "All Day";
          parsed.push({ date: parsedDate, time: timeRaw, currency: ev.querySelector("currency")?.textContent?.trim() || "", name: ev.querySelector("title")?.textContent?.trim() || "", impact, previous: ev.querySelector("previous")?.textContent?.trim() || "", forecast: ev.querySelector("forecast")?.textContent?.trim() || "" });
        });
      } catch {}
      return parsed;
    };
    Promise.all([
      fetch(proxy(thisWeekUrl)).then(r => r.ok ? r.text() : "").catch(() => ""),
      fetch(proxy(nextWeekUrl)).then(r => r.ok ? r.text() : "").catch(() => ""),
    ]).then(([xml1, xml2]) => {
      const all = [...parseXML(xml1), ...parseXML(xml2)];
      setEvents(all.length > 0 ? all : FALLBACK_EVENTS);
      setLoading(false);
    }).catch(() => { setEvents(FALLBACK_EVENTS); setLoading(false); });
  }, []);

  const impColor = imp => imp === "HIGH" ? "#EF4444" : imp === "MEDIUM" ? "#F59E0B" : "#EAB308";
  const currBg = { USD:"rgba(34,197,94,.1)", EUR:"rgba(59,130,246,.1)", GBP:"rgba(167,139,250,.1)", JPY:"rgba(245,158,11,.1)", CAD:"rgba(239,68,68,.08)", AUD:"rgba(234,179,8,.08)", CHF:"rgba(249,115,22,.1)", NZD:"rgba(20,184,166,.1)" };
  const currTx = { USD:"#22C55E", EUR:"#3B82F6", GBP:"#A78BFA", JPY:"#F59E0B", CAD:"#EF4444", AUD:"#EAB308", CHF:"#F97316", NZD:"#14B8A6" };
  const FLAGS = { US:"🇺🇸", UK:"🇬🇧", EU:"🇪🇺", JP:"🇯🇵", AU:"🇦🇺", CA:"🇨🇦" };

  const TZ_OPTIONS = [
    { label: "UTC (GMT+0)", tz: "UTC" },
    { label: "London (GMT/BST)", tz: "Europe/London" },
    { label: "Frankfurt (CET/CEST)", tz: "Europe/Berlin" },
    { label: "Paris (CET/CEST)", tz: "Europe/Paris" },
    { label: "Moscow (MSK)", tz: "Europe/Moscow" },
    { label: "Istanbul (TRT)", tz: "Europe/Istanbul" },
    { label: "Dubai (GST +4)", tz: "Asia/Dubai" },
    { label: "Mumbai / IST (+5:30)", tz: "Asia/Kolkata" },
    { label: "Singapore (SGT +8)", tz: "Asia/Singapore" },
    { label: "Shanghai (CST +8)", tz: "Asia/Shanghai" },
    { label: "Tokyo (JST +9)", tz: "Asia/Tokyo" },
    { label: "Sydney (AEST/AEDT)", tz: "Australia/Sydney" },
    { label: "New York (ET)", tz: "America/New_York" },
    { label: "Chicago (CT)", tz: "America/Chicago" },
    { label: "Los Angeles (PT)", tz: "America/Los_Angeles" },
    { label: "São Paulo (BRT)", tz: "America/Sao_Paulo" },
  ];

  const getTZOffsetLabel = (tz) => {
    try {
      const now = new Date();
      const formatter = new Intl.DateTimeFormat("en-US", { timeZone: tz, timeZoneName: "shortOffset" });
      const parts = formatter.formatToParts(now);
      const offsetPart = parts.find(p => p.type === "timeZoneName");
      return offsetPart ? offsetPart.value : "";
    } catch { return ""; }
  };

  const filteredTZOptions = useMemo(() => {
    if (!tzSearch) return TZ_OPTIONS;
    const q = tzSearch.toLowerCase();
    return TZ_OPTIONS.filter(t => t.label.toLowerCase().includes(q) || t.tz.toLowerCase().includes(q));
  }, [tzSearch]);

  const userTZLabel = useMemo(() => {
    const found = TZ_OPTIONS.find(t => t.tz === userTZ);
    const offset = getTZOffsetLabel(userTZ);
    return found ? `${found.label} ${offset}` : `${userTZ} ${offset}`;
  }, [userTZ]);

  const { start, end } = useMemo(() => getWeekRange(weekOffset), [weekOffset, getWeekRange]);
  const today = useMemo(() => { const d = new Date(); d.setHours(0,0,0,0); return d; }, []);

  const filteredEvents = useMemo(() => events.filter(ev => {
    const d = new Date(ev.date + "T12:00:00");
    if (d < start || d > end) return false;
    if (!activeImps.has(ev.impact)) return false;
    if (activeCur !== "ALL" && ev.currency !== activeCur) return false;
    return true;
  }), [events, start, end, activeImps, activeCur]);

  const grouped = useMemo(() => {
    const g = {};
    filteredEvents.forEach(ev => { if (!g[ev.date]) g[ev.date] = []; g[ev.date].push(ev); });
    return g;
  }, [filteredEvents]);

  const tabBtnS = (id) => ({ background: calTab === id ? "rgba(239,68,68,.12)" : "transparent", border: `1px solid ${calTab === id ? "rgba(239,68,68,.35)" : C.border}`, borderRadius: 6, padding: "6px 16px", fontSize: 11, fontWeight: 600, color: calTab === id ? "#EF4444" : C.subtext, cursor: "pointer", fontFamily: "inherit" });
  const pillS = (on, col = "rgba(239,68,68,") => ({ background: on ? col + ".12)" : C.tagBg, border: `1px solid ${on ? col + ".35)" : C.border}`, borderRadius: 5, padding: "4px 12px", fontSize: 10, fontWeight: 600, color: on ? (col.includes("197,94") ? "#22C55E" : col.includes("158,11") ? "#F59E0B" : col.includes("130,246") ? "#3B82F6" : "#EF4444") : C.subtext, cursor: "pointer", fontFamily: "inherit" });

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:8 }}>
        <div>
          <div style={{ fontSize:14, fontWeight:700, color:C.text }}>📅 Economic Calendar</div>
          <div style={{ fontSize:11, color:C.subtext, marginTop:2 }}>ForexFactory events · times shown in your timezone</div>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
          <span style={{ display:"inline-flex", alignItems:"center", gap:4, background:"rgba(239,68,68,.12)", border:"1px solid rgba(239,68,68,.3)", borderRadius:10, padding:"3px 9px", fontSize:9, fontWeight:700, color:"#EF4444" }}>
            <span style={{ width:5, height:5, borderRadius:"50%", background:"#EF4444", display:"inline-block" }} />LIVE FF
          </span>
          <div style={{ position:"relative" }}>
            <button onClick={() => setShowTZPicker(p => !p)} style={{ display:"inline-flex", alignItems:"center", gap:5, background:"rgba(129,140,248,.1)", border:"1px solid rgba(129,140,248,.3)", borderRadius:8, padding:"5px 10px", fontSize:10, fontWeight:600, color:"#818CF8", cursor:"pointer", fontFamily:"inherit", maxWidth:220, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
              🌐 {userTZLabel}
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9"/></svg>
            </button>
            {showTZPicker && (
              <div style={{ position:"absolute", top:"100%", right:0, zIndex:999, background:C.card, border:`1px solid ${C.border}`, borderRadius:10, padding:6, width:280, boxShadow:"0 12px 40px rgba(0,0,0,.6)", marginTop:4, maxHeight:380, display:"flex", flexDirection:"column" }}>
                <div style={{ fontSize:9, color:C.subtext, textTransform:"uppercase", letterSpacing:".1em", padding:"4px 8px 6px" }}>Select Timezone</div>
                <input value={tzSearch} onChange={e => setTzSearch(e.target.value)} placeholder="Search timezone..." style={{ margin:"0 4px 6px", background:C.inputBg, border:`1px solid ${C.border}`, borderRadius:6, padding:"6px 8px", color:C.text, fontSize:11, outline:"none", fontFamily:"inherit" }} />
                <div style={{ overflowY:"auto", flex:1 }}>
                  {filteredTZOptions.map(opt => {
                    const offset = getTZOffsetLabel(opt.tz);
                    return (
                      <div key={opt.tz} onClick={() => { setUserTZ(opt.tz); setShowTZPicker(false); setTzSearch(""); }}
                        style={{ padding:"7px 10px", borderRadius:7, fontSize:11, cursor:"pointer", color: userTZ === opt.tz ? "#818CF8" : C.text, background: userTZ === opt.tz ? "rgba(129,140,248,.12)" : "transparent", fontWeight: userTZ === opt.tz ? 600 : 400, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                        <span>{opt.label}</span>
                        <span style={{ fontSize:9, color: userTZ === opt.tz ? "#818CF8" : C.subtext, fontFamily:"monospace" }}>{offset}</span>
                      </div>
                    );
                  })}
                </div>
                <div style={{ borderTop:`1px solid ${C.border}`, padding:"6px 4px 4px" }}>
                  <div style={{ fontSize:9, color:C.subtext, marginBottom:4, paddingLeft:4 }}>Or type IANA timezone:</div>
                  <input placeholder="e.g. America/New_York" onKeyDown={e => { if(e.key === "Enter" && e.target.value.trim()) { setUserTZ(e.target.value.trim()); setShowTZPicker(false); } }} style={{ width:"100%", background:C.inputBg, border:`1px solid ${C.border}`, borderRadius:5, padding:"5px 8px", color:C.text, fontSize:10, outline:"none", fontFamily:"monospace" }} />
                </div>
              </div>
            )}
          </div>
          <button onClick={() => setWeekOffset(w => w - 1)} style={{ background:"transparent", border:`1px solid ${C.border}`, borderRadius:5, padding:"5px 10px", color:C.subtext, fontSize:13, cursor:"pointer" }}>‹</button>
          <button onClick={() => setWeekOffset(0)} style={{ background:weekOffset===0?"rgba(34,197,94,.1)":"transparent", border:`1px solid ${weekOffset===0?"rgba(34,197,94,.35)":C.border}`, borderRadius:5, padding:"5px 10px", color:weekOffset===0?"#22C55E":C.subtext, fontSize:10, fontWeight:600, cursor:"pointer", fontFamily:"inherit" }}>This Week</button>
          <span style={{ fontSize:11, color:"#9CA3AF", minWidth:120, textAlign:"center" }}>{weekLabel}</span>
          <button onClick={() => setWeekOffset(w => w + 1)} style={{ background:"transparent", border:`1px solid ${C.border}`, borderRadius:5, padding:"5px 10px", color:C.subtext, fontSize:13, cursor:"pointer" }}>›</button>
        </div>
      </div>
      <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
        {[["calendar","📅 Calendar"],["holidays","🏦 Bank Holidays"],["impact","📊 Impact Analysis"]].map(([id,lb]) => (
          <button key={id} onClick={() => setCalTab(id)} style={tabBtnS(id)}>{lb}</button>
        ))}
      </div>
      {calTab === "calendar" && (
        <>
          <div style={{ display:"flex", gap:6, flexWrap:"wrap", alignItems:"center" }}>
            <span style={{ fontSize:10, color:C.subtext, textTransform:"uppercase", letterSpacing:".1em" }}>Impact:</span>
            {[["HIGH","🔴 High","rgba(239,68,68,"],["MEDIUM","🟠 Medium","rgba(245,158,11,"],["LOW","🟡 Low","rgba(234,179,8,"]].map(([imp,lb,col]) => (
              <button key={imp} onClick={() => setActiveImps(prev => { const s = new Set(prev); s.has(imp) ? s.delete(imp) : s.add(imp); return new Set(s); })} style={pillS(activeImps.has(imp), col)}>{lb}</button>
            ))}
            <span style={{ fontSize:10, color:C.subtext, textTransform:"uppercase", letterSpacing:".1em", marginLeft:4 }}>Currency:</span>
            {["ALL","USD","EUR","GBP","JPY","AUD","CAD","CHF","NZD"].map(cur => (
              <button key={cur} onClick={() => setActiveCur(cur)} style={pillS(activeCur === cur, "rgba(34,197,94,")}>{cur}</button>
            ))}
          </div>
          <div style={{ fontSize:10, color:"#4B5563", background:C.insetBg, border:`1px solid ${C.border}`, borderRadius:6, padding:"6px 12px", display:"flex", alignItems:"center", gap:6 }}>
            <span style={{ color:"#F59E0B" }}>⚡</span>
            All times shown in: <span style={{ color:"#818CF8", fontWeight:600 }}>{userTZLabel}</span>
            <a href="https://www.forexfactory.com/calendar" target="_blank" rel="noreferrer" style={{ marginLeft:"auto", fontSize:10, color:"#22C55E", textDecoration:"none", border:"1px solid rgba(34,197,94,.3)", borderRadius:5, padding:"2px 8px" }}>Open FF ↗</a>
          </div>
          {loading ? (
            <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:12, padding:"40px 20px", textAlign:"center", color:C.subtext }}>
              <div style={{ fontSize:22, marginBottom:10 }}>📡</div>
              Fetching live data from ForexFactory...
            </div>
          ) : (
            <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:12, overflow:"hidden" }}>
              <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", borderBottom:`1px solid ${C.border}` }}>
                {[
                  [filteredEvents.length, "Events this week", "#fff"],
                  [filteredEvents.filter(e=>e.impact==="HIGH").length, "High Impact 🔴", "#EF4444"],
                  [filteredEvents.filter(e=>e.impact==="MEDIUM").length, "Medium Impact 🟠", "#F59E0B"],
                  [[...new Set(filteredEvents.map(e=>e.currency))].length, "Currencies", "#818CF8"]
                ].map(([n,l,c]) => (
                  <div key={l} style={{ padding:"12px 14px", textAlign:"center", borderRight:`1px solid ${C.border}` }}>
                    <div style={{ fontSize:22, fontWeight:800, color:c, lineHeight:1 }}>{n}</div>
                    <div style={{ fontSize:9, color:C.subtext, textTransform:"uppercase", marginTop:4 }}>{l}</div>
                  </div>
                ))}
              </div>
              {!filteredEvents.length ? (
                <div style={{ textAlign:"center", padding:"50px 20px", color:C.subtext }}>
                  <div style={{ fontSize:28, marginBottom:8 }}>📭</div>
                  <div style={{ fontSize:13, fontWeight:600, color:C.text, marginBottom:4 }}>No events match your filters this week</div>
                  <div style={{ fontSize:11 }}>Try adjusting the impact filter or switching weeks</div>
                </div>
              ) : (
                Object.keys(grouped).sort().map(dateStr => {
                  const dayDate = new Date(dateStr + "T12:00:00");
                  const isToday = dayDate.toDateString() === today.toDateString();
                  const isPast = dayDate < today && !isToday;
                  const dateLabel = dayDate.toLocaleDateString("en-US", { weekday:"long", month:"short", day:"numeric", year:"numeric" });
                  const dayHigh = grouped[dateStr].filter(e => e.impact === "HIGH").length;
                  const dayTotal = grouped[dateStr].length;
                  return (
                    <div key={dateStr}>
                      <div style={{ padding:"8px 16px", background:isToday?"rgba(34,197,94,.05)":C.tableBg, borderBottom:`1px solid ${C.border}`, display:"flex", alignItems:"center", gap:8 }}>
                        <span style={{ fontSize:11, fontWeight:700, color:isToday?"#22C55E":"#9CA3AF", textTransform:"uppercase", letterSpacing:".08em" }}>{dateLabel}</span>
                        {isToday && <span style={{ background:"rgba(34,197,94,.12)", border:"1px solid rgba(34,197,94,.3)", borderRadius:10, padding:"1px 8px", fontSize:9, fontWeight:700, color:"#22C55E" }}>TODAY</span>}
                        {isPast && <span style={{ fontSize:9, color:"#374151", background:C.border, borderRadius:10, padding:"1px 7px" }}>PAST</span>}
                        <div style={{ marginLeft:"auto", display:"flex", gap:8, alignItems:"center" }}>
                          <span style={{ fontSize:9, color:C.subtext }}>{dayTotal} event{dayTotal!==1?"s":""}</span>
                          {dayHigh > 0 && <span style={{ fontSize:9, color:"#EF4444", display:"flex", alignItems:"center", gap:3 }}><span style={{ width:5, height:5, borderRadius:"50%", background:"#EF4444", display:"inline-block" }} />{dayHigh} red folder</span>}
                        </div>
                      </div>
                      {grouped[dateStr].map((ev, i) => {
                        const convertedTime = convertETtoUserTZ(ev.time, ev.date);
                        return (
                          <div key={i} style={{ display:"flex", alignItems:"flex-start", gap:12, padding:"11px 16px", borderBottom:`1px solid ${C.border}`, background: isToday ? "rgba(34,197,94,.01)" : "transparent" }}>
                            <span style={{ width:8, height:8, borderRadius:"50%", background:impColor(ev.impact), flexShrink:0, marginTop:5 }} />
                            <span style={{ fontSize:11, color:C.subtext, minWidth:70, fontFamily:"monospace", flexShrink:0 }}>{convertedTime}</span>
                            <span style={{ fontSize:10, fontWeight:700, padding:"2px 6px", borderRadius:4, background:currBg[ev.currency]||"rgba(156,163,175,.08)", color:currTx[ev.currency]||C.subtext, flexShrink:0, minWidth:32, textAlign:"center" }}>{ev.currency}</span>
                            <div style={{ flex:1 }}>
                              <div style={{ fontSize:12, fontWeight:500, color:isPast?C.subtext:C.text }}>{ev.name}</div>
                              <div style={{ fontSize:10, color:C.subtext, marginTop:3, display:"flex", gap:10 }}>
                                {ev.previous && <span>Prev: <b style={{ color:"#9CA3AF" }}>{ev.previous}</b></span>}
                                {ev.forecast && <span>Fcst: <b style={{ color:"#818CF8" }}>{ev.forecast}</b></span>}
                              </div>
                            </div>
                            <span style={{ fontSize:9, fontWeight:700, padding:"2px 8px", borderRadius:10, flexShrink:0, background:ev.impact==="HIGH"?"rgba(239,68,68,.12)":ev.impact==="MEDIUM"?"rgba(245,158,11,.1)":"rgba(234,179,8,.08)", color:impColor(ev.impact), border:`1px solid ${ev.impact==="HIGH"?"rgba(239,68,68,.3)":ev.impact==="MEDIUM"?"rgba(245,158,11,.3)":"rgba(234,179,8,.25)"}` }}>{ev.impact==="HIGH"?"HIGH":ev.impact==="MEDIUM"?"MED":"LOW"}</span>
                          </div>
                        );
                      })}
                    </div>
                  );
                })
              )}
            </div>
          )}
        </>
      )}
      {calTab === "holidays" && (
        <>
          <div style={{ display:"flex", gap:6, flexWrap:"wrap", alignItems:"center" }}>
            <span style={{ fontSize:10, color:C.subtext, textTransform:"uppercase", letterSpacing:".1em" }}>Country:</span>
            {[["ALL","All"],["US","🇺🇸 US"],["UK","🇬🇧 UK"],["EU","🇪🇺 EU"],["JP","🇯🇵 JP"],["AU","🇦🇺 AU"],["CA","🇨🇦 CA"]].map(([cc,lb]) => (
              <button key={cc} onClick={() => setActiveHCur(cc)} style={pillS(activeHCur === cc, "rgba(59,130,246,")}>{lb}</button>
            ))}
          </div>
          <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:12, overflow:"hidden" }}>
            <div style={{ padding:"12px 16px", borderBottom:`1px solid ${C.border}`, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
              <span style={{ fontSize:13, fontWeight:600, color:C.text }}>Upcoming Bank Holidays</span>
              <span style={{ fontSize:10, color:C.subtext }}>2025–2026</span>
            </div>
            <div style={{ padding:"12px 14px" }}>
              {BANK_HOLIDAYS.filter(h => {
                const d = new Date(h.date + "T12:00:00");
                if (d < today) return false;
                if (activeHCur !== "ALL" && h.country !== activeHCur) return false;
                return true;
              }).sort((a,b) => a.date.localeCompare(b.date)).map((h, i) => {
                const d = new Date(h.date + "T12:00:00");
                const label = d.toLocaleDateString("en-US", { weekday:"short", month:"short", day:"numeric", year:"numeric" });
                const isUpcoming = (d - today) < 14 * 86400000 && d >= today;
                return (
                  <div key={i} style={{ background:isUpcoming?"rgba(59,130,246,.09)":"rgba(59,130,246,.04)", border:`1px solid ${isUpcoming?"rgba(59,130,246,.35)":"rgba(59,130,246,.15)"}`, borderRadius:8, padding:"10px 14px", marginBottom:6, display:"flex", alignItems:"center", gap:12 }}>
                    <div style={{ fontSize:18 }}>{FLAGS[h.country] || "🌍"}</div>
                    <div style={{ fontSize:11, fontWeight:700, color:"#3B82F6", minWidth:130 }}>{label}</div>
                    <div style={{ fontSize:12, fontWeight:500, color:C.text, flex:1 }}>{h.name}</div>
                    {isUpcoming && <span style={{ fontSize:9, fontWeight:700, color:"#3B82F6", background:"rgba(59,130,246,.12)", border:"1px solid rgba(59,130,246,.3)", borderRadius:10, padding:"2px 8px" }}>SOON</span>}
                    <span style={{ fontSize:10, fontWeight:700, color:C.subtext, background:C.insetBg, border:`1px solid ${C.border}`, borderRadius:4, padding:"2px 7px" }}>{h.country}</span>
                    <span style={{ fontSize:10, fontWeight:700, padding:"2px 6px", borderRadius:4, background:currBg[h.cur]||"transparent", color:currTx[h.cur]||C.subtext }}>{h.cur}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
      {calTab === "impact" && (() => {
        const { start: ws, end: we } = getWeekRange(weekOffset);
        const weekHigh = events.filter(ev => { const d = new Date(ev.date+"T12:00:00"); return d>=ws && d<=we && ev.impact==="HIGH"; });
        const pairRisk = [
          { pair:"EUR/USD", curs:["EUR","USD"] }, { pair:"GBP/USD", curs:["GBP","USD"] },
          { pair:"USD/JPY", curs:["USD","JPY"] }, { pair:"AUD/USD", curs:["AUD","USD"] },
          { pair:"USD/CAD", curs:["USD","CAD"] }, { pair:"USD/CHF", curs:["USD","CHF"] },
          { pair:"NZD/USD", curs:["NZD","USD"] }, { pair:"GBP/JPY", curs:["GBP","JPY"] },
          { pair:"EUR/GBP", curs:["EUR","GBP"] }, { pair:"AUD/JPY", curs:["AUD","JPY"] },
        ].map(p => ({ ...p, level: weekHigh.filter(ev => p.curs.includes(ev.currency)).length })).sort((a,b) => b.level-a.level);
        return (
          <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:12, overflow:"hidden" }}>
            <div style={{ padding:"12px 16px", borderBottom:`1px solid ${C.border}` }}>
              <span style={{ fontSize:13, fontWeight:600, color:C.text }}>📊 Weekly Pair Risk Assessment</span>
            </div>
            <div style={{ padding:"14px 16px" }}>
              <div style={{ fontSize:11, color:C.subtext, marginBottom:14 }}>Pairs with red folder events this week. Consider reducing size or sitting out during news windows.</div>
              <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(130px,1fr))", gap:8, marginBottom:16 }}>
                {pairRisk.map(p => {
                  const col = p.level>=3?"#EF4444":p.level>=1?"#F59E0B":"#22C55E";
                  const lbl = p.level>=3?"HIGH RISK":p.level>=1?"CAUTION":"LOW RISK";
                  const bg = p.level>=3?"rgba(239,68,68,.08)":p.level>=1?"rgba(245,158,11,.08)":"rgba(34,197,94,.06)";
                  return (
                    <div key={p.pair} style={{ background:bg, border:`1px solid ${col}30`, borderRadius:8, padding:"10px 12px", textAlign:"center" }}>
                      <div style={{ fontSize:13, fontWeight:700, color:C.text, marginBottom:4 }}>{p.pair}</div>
                      <div style={{ fontSize:9, fontWeight:700, color:col, letterSpacing:".08em" }}>{lbl}</div>
                      <div style={{ fontSize:10, color:C.subtext, marginTop:3 }}>{p.level} event{p.level!==1?"s":""}</div>
                    </div>
                  );
                })}
              </div>
              {weekHigh.length === 0 && <div style={{ textAlign:"center", color:C.subtext, padding:"20px 0" }}>No high-impact events this week.</div>}
              {Object.entries(weekHigh.reduce((g,ev) => { if(!g[ev.currency])g[ev.currency]=[]; g[ev.currency].push(ev); return g; }, {})).map(([cur, evs]) => (
                <div key={cur} style={{ marginBottom:12 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:7, marginBottom:7 }}>
                    <span style={{ fontSize:10, fontWeight:700, padding:"2px 5px", borderRadius:4, background:currBg[cur]||"transparent", color:currTx[cur]||C.subtext }}>{cur}</span>
                    <span style={{ fontSize:11, fontWeight:600, color:C.text }}>{evs.length} high-impact event{evs.length>1?"s":""} this week</span>
                  </div>
                  {evs.map((ev,i) => (
                    <div key={i} style={{ display:"flex", alignItems:"center", gap:10, padding:"7px 10px", background:C.insetBg, border:`1px solid ${C.border}`, borderRadius:7, marginBottom:4 }}>
                      <span style={{ width:5, height:5, borderRadius:"50%", background:"#EF4444", flexShrink:0 }} />
                      <span style={{ fontSize:11, color:C.subtext, minWidth:70, fontFamily:"monospace" }}>{convertETtoUserTZ(ev.time, ev.date)}</span>
                      <span style={{ fontSize:12, color:C.text, flex:1 }}>{ev.name}</span>
                      {ev.forecast && <span style={{ fontSize:10, color:"#818CF8" }}>Fcst: {ev.forecast}</span>}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        );
      })()}
    </div>
  );
}

// ─── Analytics Tag Performance Section (standalone to allow hooks) ───
function AnalyticsTagSection({ A, trades, C }) {
  const tagEntries = useMemo(() => Object.entries(A.tagMap || {}).map(([tag, d]) => {
    const wr = d.count ? d.wins / d.count * 100 : 0;
    const isMistake = MISTAKE_TAGS.includes(tag);
    const avgRR = trades.filter(t => (t.tags || []).includes(tag)).reduce((a, t) => a + (t.rr || 0), 0) / Math.max(d.count, 1);
    return { tag, ...d, wr, isMistake, avgRR };
  }), [A.tagMap, trades]);

  const wrCol = wr => wr >= 60 ? "#22C55E" : wr >= 45 ? "#F59E0B" : "#EF4444";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {/* Bubble chart */}
      {tagEntries.length > 0 && (
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, overflow: "hidden" }}>
          <div style={{ padding: "12px 16px", borderBottom: `1px solid ${C.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: C.text }}>🫧 Tag Scatter (WR vs P&L)</span>
            <div style={{ display: "flex", gap: 12, fontSize: 9, color: C.subtext }}>
              {[["#22C55E", "High WR"], ["#F59E0B", "Mid WR"], ["#EF4444", "Low WR"]].map(([c, l]) => (
                <span key={l} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: c, display: "inline-block" }} />{l}
                </span>
              ))}
            </div>
          </div>
          <TagBubbleChart tagEntries={tagEntries} trades={trades} C={C} />
        </div>
      )}

      {/* Setup tag cards */}
      {tagEntries.filter(t => !t.isMistake).length > 0 && (
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, overflow: "hidden" }}>
          <div style={{ padding: "12px 16px", borderBottom: `1px solid ${C.border}` }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: C.text }}>Setup Tags</span>
          </div>
          <div style={{ padding: "14px 16px" }}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(200px,1fr))", gap: 8 }}>
              {tagEntries.filter(t => !t.isMistake).sort((a, b) => b.pnl - a.pnl).map(t => {
                const best = trades.filter(tr => (tr.tags || []).includes(t.tag)).reduce((a, tr) => tr.pnl > a ? tr.pnl : a, -Infinity);
                const worst = trades.filter(tr => (tr.tags || []).includes(t.tag)).reduce((a, tr) => tr.pnl < a ? tr.pnl : a, Infinity);
                const col = wrCol(t.wr);
                const grade = t.wr >= 65 && t.pnl > 0 ? "A+" : t.wr >= 55 && t.pnl > 0 ? "A" : t.wr >= 45 ? "B" : t.wr >= 35 ? "C" : "D";
                const gradeCol = grade === "A+" ? "#A78BFA" : grade === "A" ? "#22C55E" : grade === "B" ? "#3B82F6" : grade === "C" ? "#F59E0B" : "#EF4444";
                return (
                  <div key={t.tag} style={{ background: C.insetBg, border: `1px solid ${C.border}`, borderRadius: 10, padding: "12px 14px", position: "relative", overflow: "hidden" }}>
                    <div style={{ position: "absolute", top: 0, left: 0, width: `${t.wr}%`, height: 3, background: col, borderRadius: "10px 0 0 0" }} />
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: C.text, flex: 1, marginRight: 6 }}>{t.tag}</div>
                      <div style={{ fontSize: 11, fontWeight: 800, color: gradeCol, background: gradeCol + "18", border: `1px solid ${gradeCol}40`, borderRadius: 5, padding: "1px 7px" }}>{grade}</div>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginBottom: 8 }}>
                      <div><div style={{ fontSize: 9, color: C.subtext, textTransform: "uppercase", marginBottom: 2 }}>Net P&L</div><div style={{ fontSize: 14, fontWeight: 700, color: t.pnl >= 0 ? "#22C55E" : "#EF4444" }}>{t.pnl >= 0 ? "+" : ""}${t.pnl.toFixed(0)}</div></div>
                      <div><div style={{ fontSize: 9, color: C.subtext, textTransform: "uppercase", marginBottom: 2 }}>Win Rate</div><div style={{ fontSize: 14, fontWeight: 700, color: col }}>{t.wr.toFixed(0)}%</div></div>
                      <div><div style={{ fontSize: 9, color: C.subtext, textTransform: "uppercase", marginBottom: 2 }}>Trades</div><div style={{ fontSize: 13, fontWeight: 600, color: "#818CF8" }}>{t.count} <span style={{ fontSize: 9, color: C.subtext }}>({t.wins}W/{t.count - t.wins}L)</span></div></div>
                      <div><div style={{ fontSize: 9, color: C.subtext, textTransform: "uppercase", marginBottom: 2 }}>Avg RR</div><div style={{ fontSize: 13, fontWeight: 600, color: "#A78BFA" }}>{t.avgRR.toFixed(2)}</div></div>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 4, marginBottom: 8 }}>
                      <div style={{ background: "rgba(34,197,94,.07)", border: "1px solid rgba(34,197,94,.15)", borderRadius: 5, padding: "4px 7px" }}><div style={{ fontSize: 8, color: C.subtext }}>Best</div><div style={{ fontSize: 11, fontWeight: 700, color: "#22C55E" }}>{best !== -Infinity ? `+$${best.toFixed(0)}` : "—"}</div></div>
                      <div style={{ background: "rgba(239,68,68,.07)", border: "1px solid rgba(239,68,68,.15)", borderRadius: 5, padding: "4px 7px" }}><div style={{ fontSize: 8, color: C.subtext }}>Worst</div><div style={{ fontSize: 11, fontWeight: 700, color: "#EF4444" }}>{worst !== Infinity ? `$${worst.toFixed(0)}` : "—"}</div></div>
                    </div>
                    <div style={{ height: 5, background: C.border, borderRadius: 3, overflow: "hidden" }}>
                      <div style={{ display: "flex", height: "100%" }}>
                        <div style={{ width: `${t.wins / t.count * 100}%`, background: "#22C55E" }} />
                        <div style={{ width: `${(t.count - t.wins) / t.count * 100}%`, background: "#EF4444" }} />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Mistake tag cards */}
      {tagEntries.filter(t => t.isMistake).length > 0 && (
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, overflow: "hidden" }}>
          <div style={{ padding: "12px 16px", borderBottom: `1px solid ${C.border}` }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: "#EF4444" }}>Mistake Tags</span>
          </div>
          <div style={{ padding: "14px 16px" }}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(200px,1fr))", gap: 8 }}>
              {tagEntries.filter(t => t.isMistake).sort((a, b) => a.pnl - b.pnl).map(t => {
                const wr = t.wr;
                return (
                  <div key={t.tag} style={{ background: "rgba(239,68,68,.04)", border: "1px solid rgba(239,68,68,.2)", borderRadius: 10, padding: "12px 14px", position: "relative", overflow: "hidden" }}>
                    <div style={{ position: "absolute", top: 0, left: 0, width: "100%", height: 3, background: `linear-gradient(90deg,#EF4444 ${wr}%,${C.border} ${wr}%)` }} />
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: "#EF4444" }}>{t.tag}</div>
                      <div style={{ fontSize: 9, padding: "2px 7px", borderRadius: 10, background: "rgba(239,68,68,.12)", color: "#EF4444", border: "1px solid rgba(239,68,68,.3)", fontWeight: 600 }}>{t.count}x</div>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
                      <div><div style={{ fontSize: 9, color: C.subtext, textTransform: "uppercase", marginBottom: 2 }}>P&L Impact</div><div style={{ fontSize: 14, fontWeight: 700, color: "#EF4444" }}>{t.pnl >= 0 ? "+" : ""}${t.pnl.toFixed(0)}</div></div>
                      <div><div style={{ fontSize: 9, color: C.subtext, textTransform: "uppercase", marginBottom: 2 }}>Win Rate</div><div style={{ fontSize: 14, fontWeight: 700, color: wr >= 50 ? "#F59E0B" : "#EF4444" }}>{wr.toFixed(0)}%</div></div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
const _norm = s => String(s || "").toLowerCase().replace(/[^a-z0-9]/g, "");
const _num = s => { let t = String(s ?? "").replace(/[\s\u00a0$,]/g, ""); const neg = /^\(.*\)$/.test(t); t = t.replace(/[()]/g, ""); const v = parseFloat(t); return isNaN(v) ? 0 : neg ? -v : v; };

function parseCSV(text) {
  const first = text.split(/\r?\n/, 1)[0] || "";
  const d = [",", ";", "\t"].map(c => [c, first.split(c).length]).sort((a, b) => b[1] - a[1])[0][0];
  const rows = []; let row = [], cell = "", q = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (q) { if (ch === '"') { if (text[i + 1] === '"') { cell += '"'; i++; } else q = false; } else cell += ch; }
    else if (ch === '"') q = true;
    else if (ch === d) { row.push(cell); cell = ""; }
    else if (ch === "\n") { row.push(cell); rows.push(row); row = []; cell = ""; }
    else if (ch !== "\r") cell += ch;
  }
  if (cell || row.length) { row.push(cell); rows.push(row); }
  return rows.map(r => r.map(c => c.trim()));
}
function parseHTMLRows(text) {
  const doc = new DOMParser().parseFromString(text, "text/html");
  return [...doc.querySelectorAll("tr")].map(tr => [...tr.querySelectorAll("th,td")].map(c => c.textContent.replace(/\u00a0/g, " ").trim()));
}
function toISO(s, offH = 0) {
  if (!s) return null;
  let y, mo, d, h, mi, sec;
  let m = String(s).match(/(\d{4})[.\-\/](\d{1,2})[.\-\/](\d{1,2})[ T](\d{1,2}):(\d{2})(?::(\d{2}))?/);
  if (m) { y = m[1]; mo = m[2]; d = m[3]; h = m[4]; mi = m[5]; sec = m[6]; }
  else {
    m = String(s).match(/(\d{1,2})\/(\d{1,2})\/(\d{4})[ T,]+(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)?/i);
    if (!m) return null;
    mo = m[1]; d = m[2]; y = m[3]; h = +m[4]; mi = m[5]; sec = m[6];
    if (m[7]) h = (h % 12) + (m[7].toUpperCase() === "PM" ? 12 : 0);
  }
  const ms = Date.UTC(+y, +mo - 1, +d, +h, +mi, +(sec || 0)) + offH * 3600000;
  return new Date(ms).toISOString().slice(0, 19);
}
const sessionOf = iso => { const h = +iso.slice(11, 13); return h >= 19 || h < 3 ? "Asia" : h < 8 ? "London" : h < 12 ? "NY AM" : "NY PM"; };
function baseSym(s) {
  let x = String(s || "").trim().toUpperCase().split(".")[0];
  const map = { CL: "CrudeOil", MCL: "CrudeOil", SI: "Silver", SIL: "Silver", XAUUSD: "XAUUSD", XAGUSD: "Silver", GC: "XAUUSD" };
  const m = x.match(/^(.*?)[FGHJKMNQUVXZ]\d{1,2}$/);
  if (m && m[1]) x = m[1];
  if (map[x]) return map[x];
  if (/^[A-Z]{6}$/.test(x)) return `${x.slice(0, 3)}/${x.slice(3)}`;
  return x;
}
const mkTrade = (src, sym, dir, pnl, entry, exit, extra) => ({ src, asset: baseSym(sym), direction: dir, pnl, entry, exit, date: entry.slice(0, 16), session: sessionOf(entry), extra });

function parseTradovate(rows, off) {
  const hi = rows.findIndex(r => { const n = r.map(_norm); return n.includes("boughttimestamp") && n.includes("soldtimestamp"); });
  if (hi < 0) return null;
  const h = rows[hi].map(_norm), ix = k => h.findIndex(x => k.includes(x));
  const iS = ix(["symbol", "contract"]), iQ = ix(["qty", "quantity"]), iP = ix(["pnl", "pl", "netpl"]), iB = h.indexOf("boughttimestamp"), iSo = h.indexOf("soldtimestamp");
  const out = [];
  rows.slice(hi + 1).forEach(r => {
    const b = toISO(r[iB], off), s = toISO(r[iSo], off);
    if (!b || !s) return;
    const buyFirst = b <= s;
    out.push(mkTrade("Tradovate", r[iS], buyFirst ? "buy" : "sell", _num(r[iP]), buyFirst ? b : s, buyFirst ? s : b, `qty ${r[iQ] || 1}`));
  });
  return out;
}
function parseMT5(rows, off) {
  const hi = rows.findIndex(r => { const n = r.map(_norm); return n.includes("position") && n.includes("symbol") && n.includes("profit") && n.includes("type"); });
  if (hi < 0) return null;
  const DT = /\d{4}[.\-\/]\d{1,2}[.\-\/]\d{1,2}[ T]\d{1,2}:\d{2}/;
  const out = [];
  for (const r of rows.slice(hi + 1)) {
    const ti = r.findIndex(c => /^(buy|sell)$/i.test(c));
    const dts = r.map((c, i) => (DT.test(c) ? i : -1)).filter(i => i >= 0);
    if (ti < 0 || dts.length < 2) { if (out.length) break; else continue; }
    const a = toISO(r[dts[0]], off), b = toISO(r[dts[1]], off);
    if (!a || !b) continue;
    const nums = r.slice(dts[1] + 1).filter(c => c !== "");
    const L = nums.length;
    const pnl = _num(nums[L - 1]) + _num(nums[L - 2]) + _num(nums[L - 3]);
    out.push(mkTrade("MT5", r[ti - 1], r[ti].toLowerCase(), pnl, a, b, `#${r[ti - 2] || ""}`));
  }
  return out;
}

function ImportModal({ existing, onSaveTrade, onClose, onDone }) {
  const C = useColors();
  const [raw, setRaw] = useState(null);
  const [off, setOff] = useState(0);
  const [sel, setSel] = useState([]);
  const [busy, setBusy] = useState(false);

  const readFile = async file => {
    if (!file) return;
    const buf = await file.arrayBuffer(), u = new Uint8Array(buf);
    const enc = u[0] === 0xFF && u[1] === 0xFE ? "utf-16le" : u[0] === 0xFE && u[1] === 0xFF ? "utf-16be" : (u[1] === 0 && u[3] === 0) ? "utf-16le" : "utf-8";
    setRaw({ name: file.name, text: new TextDecoder(enc).decode(buf) });
  };
  const parsed = useMemo(() => {
    if (!raw) return null;
    const rows = /<tr[\s>]/i.test(raw.text) ? parseHTMLRows(raw.text) : parseCSV(raw.text);
    const o = Number(off) || 0;
    let t = parseTradovate(rows, o); if (t && t.length) return { broker: "Tradovate", trades: t };
    t = parseMT5(rows, o); if (t && t.length) return { broker: "MT5", trades: t };
    return { error: true };
  }, [raw, off]);
  useEffect(() => {
    if (!parsed?.trades) return;
    const ds = new Set(parsed.trades.map(t => t.date.slice(0, 10)));
    setSel(prev => { const keep = prev.filter(d => ds.has(d)); return keep.length ? keep : [[...ds].sort().pop()]; });
  }, [parsed]);

  const keyOf = t => `${t.date}|${t.asset}|${(+t.pnl).toFixed(2)}`;
  const have = useMemo(() => new Set(existing.map(t => `${(t.date || "").slice(0, 16)}|${t.asset}|${(+t.pnl).toFixed(2)}`)), [existing]);
  const days = useMemo(() => {
    const m = {};
    (parsed?.trades || []).forEach(t => { const d = t.date.slice(0, 10); if (!m[d]) m[d] = { d, n: 0, pnl: 0 }; m[d].n++; m[d].pnl += t.pnl; });
    return Object.values(m).sort((a, b) => b.d.localeCompare(a.d));
  }, [parsed]);
  const picks = (parsed?.trades || []).filter(t => sel.includes(t.date.slice(0, 10)));
  const fresh = picks.filter(t => !have.has(keyOf(t)));
  const lbl = d => new Date(d + "T12:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });

  const doImport = async () => {
    setBusy(true);
    for (const t of fresh) {
      await onSaveTrade({ asset: t.asset, direction: t.direction, pnl: t.pnl.toFixed(2), rr: "", session: t.session, tags: [], mistakes: [], emotionBefore: "Neutral", emotionAfter: "Neutral", notes: `Imported from ${t.src} ${t.extra}`, date: t.date, entryTime: t.entry, exitTime: t.exit, chartImages: [], rating: 0 }, false);
      await new Promise(r => setTimeout(r, 80));
    }
    onDone(fresh.length);
  };

  const iS = { width: 70, background: C.inputBg, border: `1px solid ${C.border}`, borderRadius: 6, padding: "5px 8px", color: C.text, fontSize: 12, outline: "none", fontFamily: "inherit" };
  return (
    <div style={{ position:  "fixed", inset: 0, background: "rgba(0,0,0,.8)", zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, backdropFilter: "blur(4px)" }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, width: "100%", maxWidth: 560, maxHeight: "88vh", overflowY: "auto" }}>
        <div style={{ padding: "14px 20px", borderBottom: `1px solid ${C.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontSize: 15, fontWeight: 600, color: C.text }}>⬆ Import trades</span>
          <button onClick={onClose} style={{ background: "none", border: `1px solid ${C.border}`, borderRadius: 6, padding: "4px 10px", color: C.subtext, cursor: "pointer", fontSize: 11 }}>✕</button>
        </div>
        <div style={{ padding: 20 }}>
          {!raw && (
            <>
              <label onDrop={e => { e.preventDefault(); readFile(e.dataTransfer.files[0]); }} onDragOver={e => e.preventDefault()} style={{ display: "block", border: `2px dashed ${C.border}`, borderRadius: 12, padding: "34px 16px", textAlign: "center", cursor: "pointer", color: C.subtext, fontSize: 12 }}>
                <div style={{ fontSize: 26, marginBottom: 6 }}>📄</div>
                <div style={{ fontWeight: 600, color: C.text }}>Drop or click to choose your report</div> 
                <div style={{ fontSize: 10, marginTop: 4 }}>.csv · .html — MT5 or Tradovate (auto-detected)</div>
                <input type="file" accept=".csv,.html,.htm,.txt" style={{ display: "none" }} onChange={e => { readFile(e.target.files[0]); e.target.value = ""; }} />
              </label>
              <div style={{ fontSize: 10, color: C.subtext, lineHeight: 1.7, marginTop: 12 }}>
                <b style={{ color: C.text }}>MT5:</b> History tab → set Positions → right-click → Report → HTML (or save the Excel report as CSV).<br />
                <b style={{ color: C.text }}>Tradovate:</b> Account → Performance tab → Export CSV.
              </div>
            </>
          )}
          {raw && parsed?.error && (
            <div style={{ textAlign: "center", padding: "20px 0" }}>
              <div style={{ fontSize: 13, color: "#EF4444", fontWeight: 600, marginBottom: 6 }}>Couldn't find MT5 or Tradovate trades in {raw.name}</div>
              <div style={{ fontSize: 11, color: C.subtext, marginBottom: 14 }}>MT5 needs the Positions report (HTML/CSV). Excel files: Save As → CSV first.</div>
              <button onClick={() => setRaw(null)} style={pillBtn(C, true)}>Choose another file</button>
            </div>
          )}
          {parsed?.trades && (
            <>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, flexWrap: "wrap", gap: 8 }}>
                <div style={{ fontSize: 12, color: C.text }}><span style={{ fontWeight: 700, color: "#22C55E" }}>{parsed.broker}</span> · {parsed.trades.length} trades found <button onClick={() => setRaw(null)} style={{ ...pillBtn(C, false), marginLeft: 6 }}>change</button></div>
                <label style={{ fontSize: 10, color: C.subtext, display: "flex", alignItems: "center", gap: 6 }}>Shift hrs <input type="number" step="0.5" value={off} onChange={e => setOff(e.target.value)} style={iS} /></label>
              </div>
              <div style={{ fontSize: 9, color: C.subtext, textTransform: "uppercase", letterSpacing: ".1em", marginBottom: 6 }}>Choose date(s) to journal</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 14 }}>
                {days.map(d => {
                  const on = sel.includes(d.d);
                  return (
                    <button key={d.d} onClick={() => setSel(s => on ? s.filter(x => x !== d.d) : [...s, d.d])} style={{ background: on ? "rgba(34,197,94,.12)" : C.insetBg, border: `1px solid ${on ? "rgba(34,197,94,.4)" : C.border}`, borderRadius: 9, padding: "7px 11px", cursor: "pointer", textAlign: "left", fontFamily: "inherit" }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: on ? "#22C55E" : C.text }}>{lbl(d.d)}</div>
                      <div style={{ fontSize: 10, color: d.pnl >= 0 ? "#22C55E" : "#EF4444" }}>{d.n}t · {d.pnl >= 0 ? "+" : "-"}${Math.abs(d.pnl).toFixed(0)}</div>
                    </button>
                  );
                })}
                <button onClick={() => setSel(days.map(d => d.d))} style={{ ...pillBtn(C, false), alignSelf: "center" }}>All dates</button>
              </div>
              <div style={{ border: `1px solid ${C.border}`, borderRadius: 10, maxHeight: 220, overflowY: "auto", marginBottom: 14 }}>
                {picks.map((t, i) => {
                  const dup = have.has(keyOf(t));
                  return (
                    <div key={i} style={{ display: "grid", gridTemplateColumns: "50px 1fr 50px 70px", gap: 8, alignItems: "center", padding: "7px 12px", borderTop: i ? `1px solid ${C.border}` : "none", opacity: dup ? 0.4 : 1, fontSize: 11 }}>
                      <span style={{ color: C.subtext }}>{t.date.slice(11)}</span>
                      <span style={{ fontWeight: 600, color: C.text }}>{t.asset} {dup && <span style={{ fontSize: 9, color: C.subtext }}>· already journaled</span>}</span>
                      <span style={{ color: t.direction === "buy" ? "#22C55E" : "#EF4444", fontWeight: 600 }}>{t.direction.toUpperCase()}</span>
                      <span style={{ textAlign: "right", fontWeight: 700, color: t.pnl >= 0 ? "#22C55E" : "#EF4444" }}>{t.pnl >= 0 ? "+" : "-"}${Math.abs(t.pnl).toFixed(2)}</span>
                    </div>
                  );
                })}
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: 11, color: C.subtext }}>{fresh.length} new{picks.length - fresh.length > 0 ? ` · ${picks.length - fresh.length} skipped` : ""}</span>
                <button disabled={!fresh.length || busy} onClick={doImport} style={{ background: fresh.length ? "#22C55E" : C.border, border: "none", borderRadius: 7, padding: "8px 20px", color: fresh.length ? "#fff" : C.subtext, fontSize: 12, fontWeight: 600, cursor: fresh.length ? "pointer" : "default", fontFamily: "inherit" }}>{busy ? "Importing…" : `Import ${fresh.length} trade${fresh.length !== 1 ? "s" : ""}`}</button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
const pillBtn = (C, on) => ({ background: on ? "rgba(34,197,94,.12)" : "transparent", border: `1px solid ${on ? "rgba(34,197,94,.35)" : C.border}`, color: on ? "#22C55E" : C.subtext, borderRadius: 6, padding: "4px 10px", fontSize: 10, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" });

function PerfStrip({ A, total, C }) {
  const wr = (A.winRate || 0) * 100, circ = 2 * Math.PI * 36;
  const wrC = wr >= 55 ? "#22C55E" : wr >= 45 ? "#F59E0B" : "#EF4444";
  const t = Math.max(A.wc + A.lc + A.be, 1);
  const tiles = [
    ["Expectancy", `${A.expectancy >= 0 ? "+" : "-"}$${Math.abs(A.expectancy).toFixed(0)}`, A.expectancy >= 0 ? "#22C55E" : "#EF4444"],
    ["Profit Factor", A.profitFactor >= 999 ? "∞" : A.profitFactor.toFixed(2), A.profitFactor >= 1.5 ? "#22C55E" : A.profitFactor >= 1 ? "#F59E0B" : "#EF4444"],
    ["Avg Win", `+$${A.avgWin.toFixed(0)}`, "#22C55E"],
    ["Avg Loss", `-$${A.avgLoss.toFixed(0)}`, "#EF4444"],
    ["Max Drawdown", `-$${A.maxDD.toFixed(0)}`, "#EF4444"],
    ["Best W / Worst L", `${A.maxCW} / ${A.maxCL}`, "#818CF8"],
  ];
  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 16, display: "flex", gap: 20, flexWrap: "wrap", alignItems: "center" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 14, minWidth: 230 }}>
        <div style={{ position: "relative", width: 84, height: 84, flexShrink: 0 }}>
          <svg width="84" height="84" style={{ transform: "rotate(-90deg)" }}>
            <circle cx="42" cy="42" r="36" fill="none" stroke={C.border} strokeWidth="7" />
            <circle cx="42" cy="42" r="36" fill="none" stroke={wrC} strokeWidth="7" strokeLinecap="round" strokeDasharray={circ} strokeDashoffset={circ * (1 - wr / 100)} style={{ transition: "stroke-dashoffset .8s" }} />
          </svg>
          <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
            <div style={{ fontSize: 18, fontWeight: 800, color: wrC, lineHeight: 1 }}>{wr.toFixed(0)}%</div>
            <div style={{ fontSize: 8, color: C.subtext, marginTop: 2 }}>WIN RATE</div>
          </div>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 22, fontWeight: 800, color: C.text, lineHeight: 1 }}>{total}</div>
          <div style={{ fontSize: 9, color: C.subtext, textTransform: "uppercase", marginBottom: 8 }}>Trades</div>
          <div style={{ display: "flex", height: 5, borderRadius: 3, overflow: "hidden", background: C.border }}>
            <div style={{ width: `${A.wc / t * 100}%`, background: "#22C55E" }} />
            <div style={{ width: `${A.be / t * 100}%`, background: "#3B82F6" }} />
            <div style={{ width: `${A.lc / t * 100}%`, background: "#EF4444" }} />
          </div>
          <div style={{ fontSize: 9, color: C.subtext, marginTop: 4 }}>{A.wc}W · {A.lc}L · {A.be}BE</div>
        </div>
      </div>
      <div style={{ flex: 1, minWidth: 260, display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 8 }}>
        {tiles.map(([l, v, c]) => (
          <div key={l} style={{ background: C.insetBg, border: `1px solid ${C.border}`, borderRadius: 10, padding: "10px 12px" }}>
            <div style={{ fontSize: 9, color: C.subtext, textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 4 }}>{l}</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: c }}>{v}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function TagBoard({ A, trades, C }) {
  const [view, setView] = useState("setups");
  const [sort, setSort] = useState("pnl");
  const [q, setQ] = useState("");
  const [all, setAll] = useState(false);
  const [open, setOpen] = useState(null);
  const LIMIT = 6;
  const entries = useMemo(() => Object.entries(A.tagMap || {}).map(([tag, d]) => {
    const rel = trades.filter(t => (t.tags || []).includes(tag) || (t.mistakes || []).includes(tag));
    const pn = rel.map(t => t.pnl);
    return { tag, ...d, wr: d.count ? d.wins / d.count * 100 : 0, isMistake: MISTAKE_TAGS.includes(tag), avgRR: rel.length ? rel.reduce((a, t) => a + (t.rr || 0), 0) / rel.length : 0, best: pn.length ? Math.max(...pn) : 0, worst: pn.length ? Math.min(...pn) : 0 };
  }), [A.tagMap, trades]);
  const setups = entries.filter(t => !t.isMistake), mistakes = entries.filter(t => t.isMistake);
  const bestSetup = [...setups].sort((a, b) => b.pnl - a.pnl)[0];
  const costly = [...mistakes].sort((a, b) => a.pnl - b.pnl)[0];
  const list = entries.filter(t => view === "mistakes" ? t.isMistake : !t.isMistake)
    .filter(t => !q || t.tag.toLowerCase().includes(q.toLowerCase()))
    .sort((a, b) => sort === "wr" ? b.wr - a.wr : sort === "count" ? b.count - a.count : view === "mistakes" ? a.pnl - b.pnl : b.pnl - a.pnl);
  const shown = all ? list : list.slice(0, LIMIT);
  const maxAbs = Math.max(...list.map(t => Math.abs(t.pnl)), 1);
  const full = view === "mistakes" ? mistakes.length : setups.length;
  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, overflow: "hidden" }}>
      <div style={{ padding: "12px 16px", borderBottom: `1px solid ${C.border}`, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
        <div style={{ display: "flex", gap: 5 }}>
          <button onClick={() => { setView("setups"); setAll(false); setOpen(null); }} style={pillBtn(C, view === "setups")}>Setups · {setups.length}</button>
          <button onClick={() => { setView("mistakes"); setAll(false); setOpen(null); }} style={{ ...pillBtn(C, view === "mistakes"), ...(view === "mistakes" ? { color: "#EF4444", background: "rgba(239,68,68,.12)", border: "1px solid rgba(239,68,68,.35)" } : {}) }}>Mistakes · {mistakes.length}</button>
          <button onClick={() => setView("scatter")} style={pillBtn(C, view === "scatter")}>Scatter</button>
        </div>
        {view !== "scatter" && (
          <div style={{ display: "flex", gap: 4 }}>
            {[["pnl", "P&L"], ["wr", "Win %"], ["count", "Count"]].map(([k, l]) => <button key={k} onClick={() => setSort(k)} style={pillBtn(C, sort === k)}>{l}</button>)}
          </div>
        )}
      </div>
      {entries.length === 0 ? <div style={{ textAlign: "center", color: C.subtext, padding: 30, fontSize: 12 }}>No tags yet</div> : view === "scatter" ? (
        <TagBubbleChart tagEntries={entries} trades={trades} C={C} />
      ) : (
        <>
          {(bestSetup || costly) && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, padding: "12px 14px 4px" }}>
              {bestSetup && <div style={{ background: "rgba(34,197,94,.07)", border: "1px solid rgba(34,197,94,.2)", borderRadius: 9, padding: "8px 12px" }}><div style={{ fontSize: 9, color: C.subtext, textTransform: "uppercase" }}>Best setup</div><div style={{ fontSize: 12, fontWeight: 700, color: "#22C55E", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{bestSetup.tag} · {bestSetup.pnl >= 0 ? "+" : "-"}${Math.abs(bestSetup.pnl).toFixed(0)}</div></div>}
              {costly && <div style={{ background: "rgba(239,68,68,.07)", border: "1px solid rgba(239,68,68,.2)", borderRadius: 9, padding: "8px 12px" }}><div style={{ fontSize: 9, color: C.subtext, textTransform: "uppercase" }}>Costliest mistake</div><div style={{ fontSize: 12, fontWeight: 700, color: "#EF4444", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{costly.tag} · {costly.pnl >= 0 ? "+" : "-"}${Math.abs(costly.pnl).toFixed(0)}</div></div>}
            </div>
          )}
          {full > 8 && <div style={{ padding: "8px 14px 0" }}><input value={q} onChange={e => setQ(e.target.value)} placeholder="Search tags…" style={{ width: "100%", background: C.inputBg, border: `1px solid ${C.border}`, borderRadius: 7, padding: "6px 10px", color: C.text, fontSize: 11, outline: "none", fontFamily: "inherit" }} /></div>}
          <div style={{ marginTop: 8, maxHeight: all ? 360 : "none", overflowY: all ? "auto" : "visible" }}>
            {shown.length === 0 && <div style={{ textAlign: "center", color: C.subtext, padding: 24, fontSize: 12 }}>Nothing here yet</div>}
            {shown.map(t => {
              const pc = t.pnl >= 0 ? "#22C55E" : "#EF4444", wc = t.wr >= 60 ? "#22C55E" : t.wr >= 45 ? "#F59E0B" : "#EF4444", isOpen = open === t.tag;
              return (
                <div key={t.tag} style={{ borderTop: `1px solid ${C.border}` }}>
                  <div onClick={() => setOpen(isOpen ? null : t.tag)} style={{ position: "relative", display: "grid", gridTemplateColumns: "1fr 90px 70px", alignItems: "center", gap: 10, padding: "9px 14px", cursor: "pointer" }}>
                    <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: `${Math.abs(t.pnl) / maxAbs * 100}%`, background: pc, opacity: 0.07 }} />
                    <div style={{ position: "relative", minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: t.isMistake ? "#EF4444" : C.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{t.tag}</div>
                      <div style={{ fontSize: 9, color: C.subtext }}>{t.count} trade{t.count !== 1 ? "s" : ""}</div>
                    </div>
                    <div style={{ position: "relative" }}>
                      <div style={{ fontSize: 9, color: wc, fontWeight: 700, marginBottom: 3 }}>{t.wr.toFixed(0)}% WR</div>
                      <div style={{ height: 4, background: C.border, borderRadius: 2, overflow: "hidden" }}><div style={{ width: `${t.wr}%`, height: "100%", background: wc }} /></div>
                    </div>
                    <div style={{ position: "relative", textAlign: "right", fontSize: 13, fontWeight: 700, color: pc }}>{t.pnl >= 0 ? "+" : "-"}${Math.abs(t.pnl).toFixed(0)}</div>
                  </div>
                  {isOpen && (
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 6, padding: "0 14px 12px" }}>
                      {[["Avg RR", t.avgRR.toFixed(2), "#A78BFA"], ["W / L", `${t.wins}/${t.count - t.wins}`, C.text], ["Best", `+$${t.best.toFixed(0)}`, "#22C55E"], ["Worst", `$${t.worst.toFixed(0)}`, "#EF4444"]].map(([l, v, c]) => (
                        <div key={l} style={{ background: C.insetBg, border: `1px solid ${C.border}`, borderRadius: 7, padding: "6px 8px", textAlign: "center" }}>
                          <div style={{ fontSize: 8, color: C.subtext, textTransform: "uppercase" }}>{l}</div>
                          <div style={{ fontSize: 12, fontWeight: 700, color: c }}>{v}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {list.length > LIMIT && (
            <button onClick={() => setAll(a => !a)} style={{ width: "100%", background: "transparent", border: "none", borderTop: `1px solid ${C.border}`, padding: "10px", color: C.subtext, fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
              {all ? "Show less ▲" : `Show all ${list.length} ▼`}
            </button>
          )}
        </>
      )}
    </div>
  );
}

function InstrumentBoard({ stats, C }) {
  const [all, setAll] = useState(false);
  const shown = all ? stats : stats.slice(0, 5);
  const maxAbs = Math.max(...stats.map(s => Math.abs(s.pnl)), 1);
  const grade = wr => wr >= 65 ? ["A+", "#A78BFA"] : wr >= 55 ? ["A", "#22C55E"] : wr >= 45 ? ["B", "#3B82F6"] : wr >= 35 ? ["C", "#F59E0B"] : ["D", "#EF4444"];
  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, overflow: "hidden" }}>
      <div style={{ padding: "12px 16px", borderBottom: `1px solid ${C.border}`, display: "flex", justifyContent: "space-between" }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: C.text }}>🏆 Instruments</span>
        <span style={{ fontSize: 10, color: C.subtext }}>{stats.length} traded</span>
      </div>
      {stats.length === 0 && <div style={{ textAlign: "center", color: C.subtext, padding: 30, fontSize: 12 }}>No data</div>}
      <div style={{ maxHeight: all ? 360 : "none", overflowY: all ? "auto" : "visible" }}>
        {shown.map((s, i) => {
          const pc = s.pnl >= 0 ? "#22C55E" : "#EF4444", wc = s.wr >= 50 ? "#22C55E" : "#EF4444", [g, gc] = grade(s.wr);
          return (
            <div key={s.asset} style={{ position: "relative", display: "grid", gridTemplateColumns: "20px 1fr 80px 70px 28px", alignItems: "center", gap: 10, padding: "10px 14px", borderTop: i ? `1px solid ${C.border}` : "none" }}>
              <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: `${Math.abs(s.pnl) / maxAbs * 100}%`, background: pc, opacity: 0.06 }} />
              <div style={{ position: "relative", fontSize: 10, color: C.subtext, fontWeight: 700 }}>{i + 1}</div>
              <div style={{ position: "relative" }}><div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{s.asset}</div><div style={{ fontSize: 9, color: C.subtext }}>{s.count} trades · {s.avgRR.toFixed(2)}R</div></div>
              <div style={{ position: "relative" }}><div style={{ fontSize: 9, color: wc, fontWeight: 700, marginBottom: 3 }}>{s.wr.toFixed(0)}%</div><div style={{ height: 4, background: C.border, borderRadius: 2, overflow: "hidden" }}><div style={{ width: `${s.wr}%`, height: "100%", background: wc }} /></div></div>
              <div style={{ position: "relative", textAlign: "right", fontSize: 13, fontWeight: 700, color: pc }}>{s.pnl >= 0 ? "+" : "-"}${Math.abs(s.pnl).toFixed(0)}</div>
              <div style={{ position: "relative", fontSize: 10, fontWeight: 800, color: gc, background: gc + "18", border: `1px solid ${gc}40`, borderRadius: 5, textAlign: "center", padding: "1px 0" }}>{g}</div>
            </div>
          );
        })}
      </div>
      {stats.length > 5 && <button onClick={() => setAll(a => !a)} style={{ width: "100%", background: "transparent", border: "none", borderTop: `1px solid ${C.border}`, padding: "10px", color: C.subtext, fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>{all ? "Show less ▲" : `Show all ${stats.length} ▼`}</button>}
    </div>
  );
}
function ProfilePanel({ C, userName, startBal, netPnl, allAccounts, currentAccountId, onSwitchAccount, onAddAccount }) {
  const [open, setOpen] = useState(false);
  const [avatar, setAvatar] = useState(() => localStorage.getItem("obsidex_avatar") || "");
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState("");
  const [addingNew, setAddingNew] = useState(false);
  const [newName, setNewName] = useState("");
  const currentAccount = (allAccounts || []).find(a => a.id === currentAccountId) || { name: "Account" };
  const totalBal = startBal + netPnl;
  const pct = startBal > 0 ? ((netPnl / startBal) * 100).toFixed(2) : "0.00";
  const isUp = netPnl >= 0;

  const handleAvatar = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        const MAX = 300;
        if (width > MAX || height > MAX) {
          const ratio = Math.min(MAX / width, MAX / height);
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        canvas.getContext("2d").drawImage(img, 0, 0, width, height);
        let compressed = canvas.toDataURL("image/jpeg", 0.85);
        if (compressed.length > 300000) compressed = canvas.toDataURL("image/jpeg", 0.6);

        setAvatar(compressed);
        try {
          localStorage.setItem("obsidex_avatar", compressed);
        } catch (err) {
          console.error("Avatar storage failed:", err);
        }
      };
      img.onerror = () => alert("Couldn't read that image. Try a different photo.");
      img.src = ev.target.result;
    };
    reader.onerror = () => alert("Couldn't read that file.");
    reader.readAsDataURL(file);
  };

  const ACCOUNT_ICONS = ["💼", "🏆", "📊", "⚡", "🎯", "🔥"];
  const getIcon = (index) => ACCOUNT_ICONS[index % ACCOUNT_ICONS.length];
  const ACCENT = ["#22C55E", "#818CF8", "#F59E0B"];
  const getColor = (index) => ACCENT[index % ACCENT.length];

  return (
    <>
      <style>{`
        @keyframes floatUp {
          from { opacity: 0; transform: translateY(12px) scale(0.97); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes scanLine {
          0% { top: 0%; opacity: 0.6; }
          100% { top: 100%; opacity: 0; }
        }
        @keyframes pulse-ring {
          0% { transform: scale(1); opacity: 0.6; }
          100% { transform: scale(1.5); opacity: 0; }
        }
        @keyframes robo-flicker {
          0%,100% { opacity: 1; } 92% { opacity: 1; } 93% { opacity: 0.4; } 95% { opacity: 1; } 97% { opacity: 0.6; } 98% { opacity: 1; }
        }
        @keyframes gridMove {
          from { background-position: 0 0; }
          to { background-position: 0 20px; }
        }
        .pp-card { transition: all 0.2s; }
        .pp-card:hover { transform: translateY(-1px); }
        .pp-acc-btn { transition: all 0.18s ease; }
        .pp-acc-btn:hover { transform: translateX(3px); }
        .pp-name-input { background: transparent; border: none; border-bottom: 1px solid #22C55E; outline: none; color: inherit; font-family: inherit; font-size: 11px; font-weight: 600; width: 100%; padding: 1px 2px; }
      `}</style>

      {/* Trigger card */}
      <div onClick={() => setOpen(o => !o)} className="pp-card"
        style={{ borderRadius: 10, padding: "10px 12px", border: `1px solid ${C.border}`, cursor: "pointer", background: open ? "rgba(34,197,94,.05)" : C.insetBg, position: "relative", overflow: "hidden" }}>
        {open && <div style={{ position: "absolute", left: 0, right: 0, height: 1, background: "linear-gradient(90deg,transparent,#22C55E,transparent)", animation: "scanLine 2s linear infinite", pointerEvents: "none" }} />}
        <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
          <div style={{ position: "relative", flexShrink: 0 }}>
            <div style={{ width: 34, height: 34, borderRadius: "50%", border: `2px solid ${isUp ? "#22C55E" : "#EF4444"}`, overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center", background: C.card }}>
              {avatar ? <img src={avatar} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <span style={{ fontSize: 15 }}>{userName?.[0]?.toUpperCase() || "T"}</span>}
            </div>
            <div style={{ position: "absolute", inset: -3, borderRadius: "50%", border: `1px solid ${isUp ? "#22C55E" : "#EF4444"}`, animation: "pulse-ring 2s ease-out infinite", pointerEvents: "none" }} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{userName || "Trader"}</div>
            <div style={{ fontSize: 10, color: isUp ? "#22C55E" : "#EF4444", fontWeight: 600 }}>
              ${totalBal.toLocaleString(undefined, { maximumFractionDigits: 0 })} <span style={{ fontWeight: 400, color: C.subtext }}>({isUp ? "+" : ""}{pct}%)</span>
            </div>
          </div>
          <div style={{ fontSize: 9, color: C.subtext, transform: open ? "rotate(180deg)" : "none", transition: "transform 0.3s" }}>▲</div>
        </div>
        <div style={{ marginTop: 6, display: "flex", alignItems: "center", gap: 5 }}>
          <div style={{ width: 6, height: 6, borderRadius: "50%", background: getColor((allAccounts||[]).findIndex(a => a.id === currentAccountId)), flexShrink: 0 }} />
          <span style={{ fontSize: 9, color: C.subtext, fontWeight: 500 }}>{currentAccount.name}</span>
        </div>
      </div>

      {/* Floating Panel */}
      {open && (
        <div style={{ position: "fixed", bottom: 80, left: 10, width: 220, zIndex: 999, animation: "floatUp 0.22s ease both" }}>
          <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, overflow: "hidden", boxShadow: `0 20px 60px rgba(0,0,0,${C.isLight ? ".2" : ".7"})`, position: "relative" }}>
            <div style={{ position: "absolute", inset: 0, backgroundImage: `linear-gradient(${C.border} 1px, transparent 1px), linear-gradient(90deg, ${C.border} 1px, transparent 1px)`, backgroundSize: "20px 20px", opacity: 0.3, animation: "gridMove 3s linear infinite", pointerEvents: "none" }} />

            {/* Header */}
            <div style={{ padding: "14px 14px 10px", position: "relative" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                <div style={{ position: "relative", flexShrink: 0 }} onClick={e => { e.stopPropagation(); document.getElementById("pp-avatar-input").click(); }}>
                  <div style={{ width: 46, height: 46, borderRadius: "50%", border: "2px solid #22C55E", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center", background: C.insetBg, cursor: "pointer", position: "relative" }}>
                    {avatar ? <img src={avatar} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <span style={{ fontSize: 20 }}>{userName?.[0]?.toUpperCase() || "T"}</span>}
                    <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,.4)", display: "flex", alignItems: "center", justifyContent: "center", opacity: 0, transition: "opacity .2s" }}
                      onMouseEnter={e => e.currentTarget.style.opacity = "1"} onMouseLeave={e => e.currentTarget.style.opacity = "0"}>
                      <span style={{ fontSize: 10, color: "#fff", fontWeight: 600 }}>📷</span>
                    </div>
                  </div>
                  <div style={{ position: "absolute", bottom: 0, right: 0, width: 14, height: 14, borderRadius: "50%", background: "#22C55E", border: `2px solid ${C.card}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <span style={{ fontSize: 7, color: "#fff" }}>✎</span>
                  </div>
                </div>
                <input id="pp-avatar-input" type="file" accept="image/*" style={{ display: "none" }} onChange={handleAvatar} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{userName || "Trader"}</div>
                  <div style={{ fontSize: 10, color: "#22C55E", fontWeight: 600, marginTop: 1 }}>${totalBal.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
                  <div style={{ fontSize: 9, color: isUp ? "#22C55E" : "#EF4444" }}>{isUp ? "▲" : "▼"} {isUp ? "+" : ""}{pct}% all time</div>
                </div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
                {[["Start", `$${startBal.toLocaleString(undefined, { maximumFractionDigits: 0 })}`], ["P&L", `${isUp ? "+" : ""}$${Math.abs(netPnl).toFixed(0)}`]].map(([l, v]) => (
                  <div key={l} style={{ background: C.insetBg, border: `1px solid ${C.border}`, borderRadius: 7, padding: "6px 8px" }}>
                    <div style={{ fontSize: 8, color: C.subtext, textTransform: "uppercase", marginBottom: 2 }}>{l}</div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: l === "P&L" ? (isUp ? "#22C55E" : "#EF4444") : C.text }}>{v}</div>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ height: 1, background: `linear-gradient(90deg,transparent,${C.border},transparent)` }} />

            {/* Accounts */}
            <div style={{ padding: "10px 14px 12px" }}>
              <div style={{ fontSize: 9, color: C.subtext, textTransform: "uppercase", letterSpacing: ".1em", marginBottom: 8, display: "flex", alignItems: "center", gap: 5 }}>
                <span style={{ display: "inline-block", width: 12, height: 1, background: C.subtext }} />
                Accounts
                <span style={{ display: "inline-block", flex: 1, height: 1, background: C.subtext, opacity: 0.3 }} />
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                {(allAccounts || []).map((acc, idx) => {
                  const color = getColor(idx);
                  const isActive = acc.id === currentAccountId;
                  const isEditing = editingId === acc.id;
                  return (
                    <div key={acc.id} className="pp-acc-btn"
                      style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", borderRadius: 8, cursor: "pointer", background: isActive ? color + "18" : "transparent", border: `1px solid ${isActive ? color + "50" : C.border}`, position: "relative", overflow: "hidden" }}
                      onClick={() => { if (!isEditing) { onSwitchAccount(acc.id); setOpen(false); } }}>
                      {isActive && <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 2, background: color, borderRadius: 2 }} />}
                      <span style={{ fontSize: 14, flexShrink: 0 }}>{getIcon(idx)}</span>
                      <div style={{ flex: 1, minWidth: 0 }} onClick={e => e.stopPropagation()}>
                        {isEditing ? (
                          <input
                            className="pp-name-input"
                            autoFocus
                            value={editingName}
                            onChange={e => setEditingName(e.target.value)}
                            onKeyDown={e => {
                              if (e.key === "Enter" && editingName.trim()) {
                                const updated = (allAccounts || []).map(a => a.id === acc.id ? { ...a, name: editingName.trim() } : a);
                                onAddAccount("__update__", updated);
                                setEditingId(null);
                              }
                              if (e.key === "Escape") setEditingId(null);
                            }}
                            onBlur={() => {
                              if (editingName.trim()) {
                                const updated = (allAccounts || []).map(a => a.id === acc.id ? { ...a, name: editingName.trim() } : a);
                                onAddAccount("__update__", updated);
                              }
                              setEditingId(null);
                            }}
                          />
                        ) : (
                          <div style={{ fontSize: 11, fontWeight: isActive ? 700 : 500, color: isActive ? color : C.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{acc.name}</div>
                        )}
                        {isActive && !isEditing && <div style={{ fontSize: 9, color, opacity: 0.7 }}>● Active</div>}
                      </div>
                      <button onClick={e => { e.stopPropagation(); setEditingId(acc.id); setEditingName(acc.name); }}
                        style={{ background: "none", border: "none", color: C.subtext, fontSize: 10, cursor: "pointer", padding: "2px 4px", borderRadius: 4, flexShrink: 0, opacity: 0.5 }}
                        title="Rename">✎</button>
                    </div>
                  );
                })}

                {/* Add new account */}
                {(allAccounts || []).length < 3 && (
                  addingNew ? (
                    <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "7px 10px", borderRadius: 8, border: `1px dashed #22C55E`, background: "rgba(34,197,94,.04)" }}>
                      <span style={{ fontSize: 14 }}>✦</span>
                      <input
                        autoFocus
                        value={newName}
                        onChange={e => setNewName(e.target.value)}
                        placeholder="Account name..."
                        onKeyDown={e => {
                          if (e.key === "Enter" && newName.trim()) {
                            onAddAccount(newName.trim());
                            setNewName("");
                            setAddingNew(false);
                            setOpen(false);
                          }
                          if (e.key === "Escape") { setAddingNew(false); setNewName(""); }
                        }}
                        onBlur={() => { if (!newName.trim()) { setAddingNew(false); setNewName(""); } }}
                        style={{ flex: 1, background: "transparent", border: "none", borderBottom: "1px solid #22C55E", outline: "none", color: C.text, fontSize: 11, fontFamily: "inherit", padding: "1px 2px" }}
                      />
                      <button onClick={() => { setAddingNew(false); setNewName(""); }}
                        style={{ background: "none", border: "none", color: C.subtext, fontSize: 11, cursor: "pointer" }}>✕</button>
                    </div>
                  ) : (
                    <div onClick={e => { e.stopPropagation(); setAddingNew(true); }}
                      style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 10px", borderRadius: 8, cursor: "pointer", border: `1px dashed ${C.border}`, color: C.subtext, fontSize: 11, transition: "all 0.2s" }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor = "#22C55E"; e.currentTarget.style.color = "#22C55E"; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.color = C.subtext; }}>
                      <span style={{ fontSize: 16, fontWeight: 300 }}>+</span> Add Account
                    </div>
                  )
                )}
              </div>
            </div>
            <div style={{ height: 2, background: `linear-gradient(90deg,transparent,#22C55E,#818CF8,transparent)`, animation: "robo-flicker 3s infinite" }} />
          </div>
        </div>
      )}
      {open && <div style={{ position: "fixed", inset: 0, zIndex: 998 }} onClick={() => setOpen(false)} />}
    </>
  );
}

function useCountUp(target, run, dur = 1400) {
  const [v, setV] = useState(0);
  useEffect(() => {
    if (!run) { setV(0); return; }
    let raf, s;
    const tick = t => { s = s || t; const p = Math.min((t - s) / dur, 1); setV(target * (1 - Math.pow(1 - p, 3))); if (p < 1) raf = requestAnimationFrame(tick); };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, run, dur]);
  return v;
}

function SummaryModal({ trades, onClose, startKey }) {
  const months = useMemo(() => [...new Set(trades.map(t => (t.date || "").slice(0, 7)).filter(Boolean))].sort(), [trades]);
  const [mi, setMi] = useState(() => { const i = months.indexOf(startKey); return i >= 0 ? i : Math.max(months.length - 1, 0); });
  const [step, setStep] = useState(0);
  const key = months[mi];
  const S = useMemo(() => {
    const mt = trades.filter(t => (t.date || "").slice(0, 7) === key).sort((a, b) => new Date(a.date) - new Date(b.date));
    const days = {};
    mt.forEach(t => { const d = t.date.slice(0, 10); days[d] = (days[d] || 0) + t.pnl; });
    const dl = Object.entries(days).sort((a, b) => a[0].localeCompare(b[0]));
    let c = 0; const curve = dl.map(([, p]) => (c += p));
    const best = dl.reduce((a, b) => b[1] > a[1] ? b : a, dl[0] || ["", 0]);
    const worst = dl.reduce((a, b) => b[1] < a[1] ? b : a, dl[0] || ["", 0]);
    const w = mt.filter(t => t.win).length, l = mt.filter(t => !t.win && !t.isBE).length;
    return { n: mt.length, days: dl.length, net: mt.reduce((a, t) => a + t.pnl, 0), w, l, wr: w + l ? w / (w + l) * 100 : 0, curve, best, worst };
  }, [trades, key]);
  const run = step === 2;
  const net = useCountUp(S.net, run), wr = useCountUp(S.wr, run), wv = useCountUp(S.w, run), lv = useCountUp(S.l, run);

  useEffect(() => { if (step >= 2) return; const t = setTimeout(() => setStep(s => s + 1), step === 0 ? 2600 : 5200); return () => clearTimeout(t); }, [step, mi]);
  useEffect(() => {
    const h = e => { if (e.key === "Escape") onClose(); if (e.key === "ArrowRight") setStep(s => Math.min(s + 1, 2)); if (e.key === "ArrowLeft") setStep(s => Math.max(s - 1, 0)); };
    window.addEventListener("keydown", h); return () => window.removeEventListener("keydown", h);
  }, []);

  const col = S.net >= 0 ? "#22C55E" : "#EF4444";
  const mono = "'JetBrains Mono',ui-monospace,Menlo,monospace";
  const money = v => `$${Math.abs(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const fd = d => d ? new Date(d + "T12:00").toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" }) : "";
  const pts = [0, ...S.curve], mn = Math.min(...pts), mx = Math.max(...pts), rg = mx - mn || 1;
  const path = pts.map((v, i) => `${i ? "L" : "M"}${(i / (pts.length - 1 || 1)) * 520},${195 - ((v - mn) / rg) * 180}`).join("");
  const nav = d => { setMi(m => Math.min(Math.max(m + d, 0), months.length - 1)); setStep(0); };
  const pill = c => ({ fontFamily: mono, fontSize: 12, fontWeight: 700, letterSpacing: ".22em", textTransform: "uppercase", padding: "7px 16px", borderRadius: 999, color: c || "#e5e7eb", border: `1px solid ${c ? c + "55" : "rgba(255,255,255,.18)"}`, background: c ? c + "18" : "rgba(255,255,255,.06)" });
  const big = { fontWeight: 800, color: "#fff", letterSpacing: "-.03em", textShadow: "0 0 40px rgba(255,255,255,.25)" };
  const stat = (v, l, c) => <div style={{ textAlign: "center" }}><div style={{ fontFamily: mono, fontSize: 26, fontWeight: 700, color: c }}>{v}</div><div style={{ fontFamily: mono, fontSize: 10, letterSpacing: ".2em", color: "#6B7280", textTransform: "uppercase", marginTop: 3 }}>{l}</div></div>;

  return (
    <div onClick={() => setStep(s => Math.min(s + 1, 2))} style={{ position: "fixed", inset: 0, zIndex: 700, background: "rgba(6,8,10,.55)", backdropFilter: "blur(30px) saturate(140%)", WebkitBackdropFilter: "blur(30px) saturate(140%)", display: "flex", alignItems: "center", justifyContent: "center", animation: "smFade .4s ease" }}>
      <style>{`@keyframes smFade{from{opacity:0}to{opacity:1}}@keyframes smIn{from{opacity:0;transform:scale(.92) translateY(14px);filter:blur(12px)}to{opacity:1;transform:none;filter:none}}@keyframes smDraw{from{stroke-dashoffset:1}to{stroke-dashoffset:0}}.sm-s{display:flex;flex-direction:column;align-items:center;gap:22px;animation:smIn .7s cubic-bezier(.2,.8,.2,1) both;padding:0 20px;text-align:center}`}</style>
      <button onClick={e => { e.stopPropagation(); onClose(); }} style={{ position: "absolute", top: 20, right: 20, background: "rgba(255,255,255,.08)", border: "1px solid rgba(255,255,255,.15)", borderRadius: "50%", width: 36, height: 36, color: "#fff", cursor: "pointer" }}>✕</button>

      {!months.length ? <div style={{ color: "#9CA3AF" }}>No trades yet.</div> : <>
        {step === 0 && <div key="a" className="sm-s">
          <span style={pill()}>Month in review</span>
          <div style={{ ...big, fontSize: "clamp(40px,9vw,96px)", lineHeight: 1 }}>{new Date(key + "-01T12:00").toLocaleDateString("en-US", { month: "long", year: "numeric" })}</div>
          <div style={{ fontFamily: mono, fontSize: 13, color: "#9CA3AF" }}>{S.n} trades · {S.days} trading days</div>
        </div>}

        {step === 1 && <div key="b" className="sm-s" style={{ gap: 28 }}>
          <span style={pill()}>The shape of it</span>
          <svg viewBox="-10 -10 540 215" style={{ width: "min(88vw,640px)", overflow: "visible" }}>
            <path d={path} pathLength="1" fill="none" stroke={col} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" strokeDasharray="1" style={{ animation: "smDraw 2.6s ease-out .3s both", filter: `drop-shadow(0 0 8px ${col})` }} />
          </svg>
          <div style={{ display: "flex", gap: 14 }}>
            {[["Best day", S.best, "#22C55E"], ["Worst day", S.worst, "#EF4444"]].map(([l, d, c]) => (
              <div key={l} style={{ padding: "12px 20px", borderRadius: 12, background: "rgba(255,255,255,.05)", border: "1px solid rgba(255,255,255,.1)", textAlign: "center" }}>
                <div style={{ fontFamily: mono, fontSize: 10, letterSpacing: ".2em", color: "#6B7280", textTransform: "uppercase" }}>{l}</div>
                <div style={{ fontFamily: mono, fontSize: 18, fontWeight: 700, color: c, margin: "4px 0" }}>{d[1] >= 0 ? "+" : "-"}{money(d[1])}</div>
                <div style={{ fontSize: 11, color: "#9CA3AF" }}>{fd(d[0])}</div>
              </div>
            ))}
          </div>
        </div>}

        {step === 2 && <div key="c" className="sm-s">
          <span style={pill(col)}>Net result</span>
          <div style={{ ...big, fontFamily: mono, fontSize: "clamp(38px,8vw,84px)", color: col, textShadow: `0 0 50px ${col}66` }}>{net >= 0 ? "+" : "-"}{money(net)}</div>
          <div style={{ display: "flex", gap: 34 }}>
            {stat(`${wr.toFixed(0)}%`, "Win rate", "#fff")}{stat(Math.round(wv), "Wins", "#22C55E")}{stat(Math.round(lv), "Losses", "#EF4444")}
          </div>
        </div>}

        <div onClick={e => e.stopPropagation()} style={{ position: "absolute", bottom: 26, display: "flex", alignItems: "center", gap: 12 }}>
          <button onClick={() => nav(-1)} disabled={mi === 0} style={{ background: "none", border: "1px solid rgba(255,255,255,.15)", borderRadius: 6, color: "#9CA3AF", padding: "3px 10px", cursor: "pointer", opacity: mi === 0 ? .3 : 1 }}>‹</button>
          {[0, 1, 2].map(i => <div key={i} onClick={() => setStep(i)} style={{ width: i === step ? 22 : 7, height: 7, borderRadius: 4, background: i === step ? col : "rgba(255,255,255,.2)", cursor: "pointer", transition: ".3s" }} />)}
          <button onClick={() => nav(1)} disabled={mi === months.length - 1} style={{ background: "none", border: "1px solid rgba(255,255,255,.15)", borderRadius: 6, color: "#9CA3AF", padding: "3px 10px", cursor: "pointer", opacity: mi === months.length - 1 ? .3 : 1 }}>›</button>
        </div>
      </>}
    </div>
  );
}
export default function DashboardUI({ trades: propTrades, savedTags: propSavedTags, settings: propSettings, userName: propUserName, onSaveTrade, onDeleteTrade, onSaveSettings, onSaveTags, onLogout, rules: propRules, rulesHistory: propRulesHistory, onSaveRules, onSaveRulesHistory, allAccounts, currentAccountId, onSwitchAccount, onAddAccount }) {
  const C = useColors();

  useEffect(() => {
    const savedTheme = propSettings?.theme || localStorage.getItem("theme") || "dark";
    document.body.classList.remove("light", "dark");
    document.body.classList.add(savedTheme);
  }, [propSettings?.theme]);

  const beCap = propSettings?.beCap ?? 35;
  const ratingType = propSettings?.ratingType || "stars";
  const startBal = propSettings?.startBal || 50000;
  const userName = propUserName || "";
  const trades = useMemo(() => (propTrades || []).map(t => enrich(t, beCap)), [propTrades, beCap]);
  const savedTags = useMemo(() => {
    const fromProps = propSavedTags || [];
    const fromTrades = new Set(fromProps);
    (propTrades || []).forEach(t => { (t.tags || []).forEach(tag => { if (!MISTAKE_TAGS.includes(tag) && !ASSETS.includes(tag) && !SESSIONS.includes(tag) && tag !== t.asset && tag !== t.session && tag !== t.direction) fromTrades.add(tag); }); });
    return Array.from(fromTrades);
  }, [propSavedTags, propTrades]);

  const [tab, setTab] = useState("dashboard");
  const [modal, setModal] = useState(false);
  const [editT, setEditT] = useState(null);
  const [fA, setFA] = useState("All");
  const [fS, setFS] = useState("All");
  const [fD, setFD] = useState("All");
  const [sq, setSq] = useState("");
  const [sc, setSc] = useState("date");
  const [sd, setSd] = useState("desc");
  const [calM, setCalM] = useState(() => new Date(new Date().getFullYear(), new Date().getMonth(), 1));
  const [sbPinned, setSbPinned] = useState(false);
  const [sbHover, setSbHover] = useState(false);
  const sbOpen = sbPinned || sbHover;
  const [showSettings, setShowSettings] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const [calPop, setCalPop] = useState(null);
  const [expandedTradeLog, setExpandedTradeLog] = useState(new Set());

      useEffect(() => {
    const isMobile = window.innerWidth < 768 || "ontouchstart" in window || navigator.maxTouchPoints > 0;
    if (isMobile) return;

    let cleanup = () => {};
    const init = setTimeout(() => {
      const c = document.getElementById("cur");
      const c2 = document.getElementById("cur2");
      if (!c || !c2) return;

      let mx = 0, my = 0, lx = 0, ly = 0, raf = null, active = true, first = true;

      const show = () => {
        c.style.opacity = "1"; c2.style.opacity = "1";
        document.body.classList.add("custom-cursor"); // hide real cursor ONLY after we have a real position
      };
      const hide = () => {
        c.style.opacity = "0"; c2.style.opacity = "0";
        document.body.classList.remove("custom-cursor"); // real cursor comes back
        first = true;
      };

      const setPos = (x, y) => {
        if (typeof x !== "number" || (x === 0 && y === 0)) return; // Firefox drag event reports 0,0
        mx = x; my = y;
        if (first) { lx = x; ly = y; first = false; show(); }
      };

      const onMove = e => {
        // touch or pen detected -> give the real cursor back
        if (e.pointerType && e.pointerType !== "mouse") { hide(); return; }
        setPos(e.clientX, e.clientY);
      };
      const onOut = e => { if (!e.relatedTarget) hide(); };
      const onVis = () => { if (document.hidden) hide(); };

      const evs = ["mousemove", "pointermove", "dragover", "drag", "mousedown", "mouseup", "mouseover"];
      evs.forEach(n => window.addEventListener(n, onMove, { passive: true, capture: true }));
      document.addEventListener("mouseout", onOut);
      window.addEventListener("blur", hide);
      document.addEventListener("visibilitychange", onVis);

      const tick = () => {
        if (!active) return;
        try {
          c.style.transform = `translate(${mx - 6}px,${my - 6}px)`;
          lx += (mx - lx) * 0.15;
          ly += (my - ly) * 0.15;
          c2.style.transform = `translate(${lx - 16}px,${ly - 16}px)`;
          raf = requestAnimationFrame(tick);
        } catch (err) {
          active = false; hide(); // any failure -> restore real cursor
        }
      };
      raf = requestAnimationFrame(tick);

      cleanup = () => {
        active = false;
        evs.forEach(n => window.removeEventListener(n, onMove, { capture: true }));
        document.removeEventListener("mouseout", onOut);
        window.removeEventListener("blur", hide);
        document.removeEventListener("visibilitychange", onVis);
        if (raf) cancelAnimationFrame(raf);
        document.body.classList.remove("custom-cursor");
      };
    }, 50);

    return () => { clearTimeout(init); cleanup(); document.body.classList.remove("custom-cursor"); };
  }, []);

  useEffect(() => {
    if (sbPinned) return;
    const h = e => { if (e.clientX <= 5) setSbHover(true); else if (e.clientX > 240) setSbHover(false); };
    window.addEventListener("mousemove", h);
    return () => window.removeEventListener("mousemove", h);
  }, [sbPinned]);

  const A = useMemo(() => calc(trades), [trades]);
  const pctChange = useMemo(() => startBal > 0 ? ((A.netPnl / startBal) * 100) : 0, [A.netPnl, startBal]);
  const fp = v => `${v >= 0 ? "+" : "-"}$${Math.abs(v ?? 0).toFixed(2)}`;
  const sB = s => s === "London" ? "l" : s === "NY AM" ? "na" : s === "NY PM" ? "np" : "a";
  const bSt = c => { const m = { l: ["rgba(99,102,241,.1)", "#818CF8", "rgba(99,102,241,.2)"], na: ["rgba(245,158,11,.1)", "#F59E0B", "rgba(245,158,11,.2)"], np: ["rgba(168,85,247,.1)", "#A855F7", "rgba(168,85,247,.2)"], a: ["rgba(156,163,175,.08)", "#9CA3AF", "rgba(156,163,175,.15)"] }; const [bg, co, bc] = m[c] || m.a; return { display: "inline-flex", padding: "2px 8px", borderRadius: 20, fontSize: 10, fontWeight: 500, background: bg, color: co, border: `1px solid ${bc}` }; };

  const eqD = useMemo(() => {
    let b = startBal;
    return [{ name: "Start", balance: startBal }, ...[...trades].sort((a, c) => new Date(a.date) - new Date(c.date)).map((t, i) => { b += t.pnl; return { name: `T${i + 1}`, balance: +b.toFixed(2) }; })];
  }, [trades, startBal]);

  const ddD = useMemo(() => {
    const m = {};
    [...trades].sort((a, b) => new Date(a.date) - new Date(b.date)).forEach(t => {
      const d = t.date.slice(0, 10);
      if (!m[d]) m[d] = { date: d, pnl: 0 };
      m[d].pnl += t.pnl;
    });
    let c = 0;
    return Object.values(m).map(d => { c += d.pnl; return { name: new Date(d.date).toLocaleDateString("en-US", { month: "short", day: "numeric" }), daily: +d.pnl.toFixed(2), cum: +c.toFixed(2) }; });
  }, [trades]);

  const calDays = useMemo(() => {
    const yr = calM.getFullYear(), mo = calM.getMonth();
    const firstDay = new Date(yr, mo, 1).getDay(), daysInMonth = new Date(yr, mo + 1, 0).getDate();
    const dayMap = {};
    trades.forEach(t => { const td = new Date(t.date); if (td.getFullYear() === yr && td.getMonth() === mo) { const d = td.getDate(); if (!dayMap[d]) dayMap[d] = { day: d, pnl: 0, trades: [], wins: 0 }; dayMap[d].pnl += t.pnl; dayMap[d].trades.push(t); if (t.win) dayMap[d].wins++; } });
    const cells = [];
    for (let i = 0; i < firstDay; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(dayMap[d] || { day: d, pnl: 0, trades: [], wins: 0 });
    return cells;
  }, [trades, calM]);

  const calStats = useMemo(() => {
    const allTrades = calDays.flatMap(d => d ? d.trades : []);
    const p = allTrades.filter(t => !t.isBE && t.win).length;
    const l = allTrades.filter(t => !t.isBE && !t.win).length;
    const b = allTrades.filter(t => t.isBE).length;
    return { p, l, b, t: allTrades.length };
  }, [calDays]);

  const wkStats = useMemo(() => {
    const wks = []; let w = [];
    calDays.forEach((d, i) => { w.push(d); if ((i + 1) % 7 === 0) { wks.push(w); w = []; } });
    if (w.length) wks.push(w);
    return wks.map((wk, wi) => {
      const pnl = +wk.reduce((a, d) => a + (d ? d.pnl : 0), 0).toFixed(2);
      const tt = wk.reduce((a, d) => a + (d ? d.trades.length : 0), 0);
      const wins = wk.reduce((a, d) => a + (d ? d.wins : 0), 0);
      const wr = tt ? Math.round(wins / tt * 100) : null;
      const s2 = wk.find(d => d)?.day, e = [...wk].reverse().find(d => d)?.day;
      return { wi, label: s2 && e ? `${s2}–${e} ${calM.toLocaleDateString("en-US", { month: "short" })}` : `Wk ${wi + 1}`, pnl, wr, tt };
    }).filter(wk => wk.tt > 0);
  }, [calDays, calM]);

  const dayWR = useMemo(() => {
    const today = new Date();
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
    const todayTrades = trades.filter(t => {
      if (!t.date) return false;
      const td = new Date(t.date);
      if (isNaN(td.getTime())) return false;
      const ds = `${td.getFullYear()}-${String(td.getMonth() + 1).padStart(2, "0")}-${String(td.getDate()).padStart(2, "0")}`;
      return ds === todayStr;
    });
    const wn = todayTrades.filter(t => t.win).length, ln = todayTrades.filter(t => !t.win && !t.isBE).length;
    return (wn + ln) ? wn / (wn + ln) : 0;
  }, [trades]);

  const ft = useMemo(() => {
    let t = [...trades];
    if (fA !== "All") t = t.filter(x => x.asset === fA);
    if (fS !== "All") t = t.filter(x => x.session === fS);
    if (fD !== "All") t = t.filter(x => x.direction === fD);
    if (sq) t = t.filter(x => x.asset.toLowerCase().includes(sq.toLowerCase()) || (x.notes || "").toLowerCase().includes(sq.toLowerCase()) || (x.tags || []).some(g => g.toLowerCase().includes(sq.toLowerCase())));
    t.sort((a, b) => { let va = a[sc], vb = b[sc]; if (sc === "date") { va = new Date(va); vb = new Date(vb); } return sd === "asc" ? (va < vb ? -1 : 1) : (va > vb ? -1 : 1); });
    return t;
  }, [trades, fA, fS, fD, sq, sc, sd]);

  const doSort = col => { if (sc === col) setSd(d => d === "asc" ? "desc" : "asc"); else { setSc(col); setSd("desc"); } };
  const save = (td) => { if (editT) { onSaveTrade({ ...td, id: editT.id }, true); } else { onSaveTrade(td, false); } setModal(false); setEditT(null); };
  const del = (id) => onDeleteTrade(id);

  const smartInsights = useMemo(() => generateInsights(trades, A, startBal), [trades, A, startBal]);

  const instrumentStats = useMemo(() => {
    const m = {};
    trades.forEach(t => {
      if (!m[t.asset]) m[t.asset] = { pnl: 0, wins: 0, count: 0, rr: 0 };
      m[t.asset].pnl += t.pnl; m[t.asset].count++;
      if (t.win) m[t.asset].wins++;
      m[t.asset].rr += t.rr;
    });
    return Object.entries(m).filter(([, d]) => d.count >= 1).map(([asset, d]) => ({
      asset, pnl: d.pnl, count: d.count, wr: d.count ? d.wins / d.count * 100 : 0, avgRR: d.count ? d.rr / d.count : 0
    })).sort((a, b) => b.pnl - a.pnl);
  }, [trades]);

  const getDayColor = day => {
    if (!day.trades.length) return { bg: C.isLight ? "#f8fafc" : "#0D0E12", border: "1px solid transparent" };
    if (Math.abs(day.pnl) <= beCap) return { bg: "rgba(59,130,246,.06)", border: "1px solid rgba(59,130,246,.2)" };
    return day.pnl > 0 ? { bg: "rgba(34,197,94,.08)", border: "1px solid rgba(34,197,94,.25)" } : { bg: "rgba(239,68,68,.08)", border: "1px solid rgba(239,68,68,.25)" };
  };
  const getDayC = day => Math.abs(day.pnl) <= beCap ? "#3B82F6" : day.pnl > 0 ? "#22C55E" : "#EF4444";

  const navs = [["dashboard", "Dashboard"], ["trades", "Trade Log"], ["analytics", "Analytics"], ["calendar", "Calendar"], ["psychology", "Psychology"], ["insights", "Insights"], ["calendar2", "News"], ["rules", "Rules"]];
  const ac = C.chartAxis;
  const cS = { background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, overflow: "hidden" };
  const cP = { ...cS, padding: "14px 16px" };
  const MC = ({ label, value, color, children, sub }) => (
    <div style={{ ...cP, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, minHeight: 88 }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 10, fontWeight: 500, color: C.subtext, textTransform: "uppercase", letterSpacing: ".08em", marginBottom: 5 }}>{label}</div>
        <div style={{ fontSize: 20, fontWeight: 700, color, lineHeight: 1 }}>{value}</div>
        {sub && <div style={{ marginTop: 5 }}>{sub}</div>}
      </div>
      {children}
    </div>
  );

  const CalView = ({ showTabs = true }) => {
    const monthStats = useMemo(() => {
      const m = {};
      trades.forEach(t => { const td = new Date(t.date); const k = `${td.getFullYear()}-${String(td.getMonth() + 1).padStart(2, "0")}`; if (!m[k]) m[k] = { pnl: 0, trades: 0, wins: 0, label: td.toLocaleDateString("en-US", { month: "short", year: "numeric" }) }; m[k].pnl += t.pnl; m[k].trades++; if (t.win) m[k].wins++; });
      return Object.entries(m).sort((a, b) => b[0].localeCompare(a[0])).map(([k, v]) => ({ ...v, key: k, wr: v.trades ? (v.wins / v.trades * 100) : 0, pct: startBal > 0 ? (v.pnl / startBal * 100) : 0 }));
    }, []);
    const weekStats = useMemo(() => {
      const w = {};
      trades.forEach(t => { const td = new Date(t.date); const day = td.getDay(); const diff = td.getDate() - day; const mon = new Date(td.setDate(diff)); const k = `${mon.getFullYear()}-${String(mon.getMonth() + 1).padStart(2, "0")}-${String(mon.getDate()).padStart(2, "0")}`; if (!w[k]) w[k] = { pnl: 0, trades: 0, wins: 0, label: `${mon.toLocaleDateString("en-US", { month: "short", day: "numeric" })}–${new Date(mon.getTime() + 6 * 86400000).toLocaleDateString("en-US", { month: "short", day: "numeric" })}` }; w[k].pnl += t.pnl; w[k].trades++; if (t.win) w[k].wins++; });
      return Object.entries(w).sort((a, b) => b[0].localeCompare(a[0])).map(([k, v]) => ({ ...v, key: k, wr: v.trades ? (v.wins / v.trades * 100) : 0, pct: startBal > 0 ? (v.pnl / startBal * 100) : 0 }));
    }, []);
    const dayStats = useMemo(() => {
      const d = {};
      trades.forEach(t => { const td = new Date(t.date); const day = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"][td.getDay()]; if (!d[day]) d[day] = { pnl: 0, trades: 0, wins: 0 }; d[day].pnl += t.pnl; d[day].trades++; if (t.win) d[day].wins++; });
      return ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"].filter(d2 => d[d2]).map(day => ({ day, short: day.slice(0, 3), ...d[day], wr: d[day].trades ? (d[day].wins / d[day].trades * 100) : 0 })).sort((a, b) => b.pnl - a.pnl);
    }, []);
    const [calTab, setCalTab] = useState("calendar");
    const tabBtn = (id, lb) => <button onClick={() => setCalTab(id)} style={{ background: calTab === id ? "rgba(34,197,94,.12)" : "transparent", border: `1px solid ${calTab === id ? "rgba(34,197,94,.35)" : C.border}`, borderRadius: 6, padding: "5px 14px", fontSize: 11, fontWeight: 600, color: calTab === id ? "#22C55E" : C.subtext, cursor: "pointer", fontFamily: "inherit" }}>{lb}</button>;

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {showTabs && <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>{tabBtn("calendar", "📅 Calendar")}{tabBtn("monthly", "Monthly")}{tabBtn("weekly", "Weekly")}{tabBtn("daily", "Daily")}</div>}
        {(calTab === "calendar" || !showTabs) && (
          <>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 220px", gap: 12 }} className="cal-grid">
              <div style={cS}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 16px", borderBottom: `1px solid ${C.border}` }}>
                  <span style={{ fontSize: 14, fontWeight: 600, color: C.text }}>{calM.toLocaleDateString("en-US", { month: "long", year: "numeric" })}</span>
                  <div style={{ display: "flex", gap: 5, alignItems: "center" }}>
                    
                    <button className="imp-btn sum-btn" onClick={() => setShowSummary(true)} style={{ padding: "4px 10px", fontSize: 11, marginRight: 4 }}>✦ Summary</button><button onClick={() => setCalM(d => new Date(d.getFullYear(), d.getMonth() - 1, 1))} style={{ background: "none", border: `1px solid ${C.border}`, borderRadius: 6, padding: "4px 10px", color: C.subtext, fontSize: 11, cursor: "pointer" }}>‹</button>

                    <button onClick={() => setCalM(d => new Date(d.getFullYear(), d.getMonth() + 1, 1))} style={{ background: "none", border: `1px solid ${C.border}`, borderRadius: 6, padding: "4px 10px", color: C.subtext, fontSize: 11, cursor: "pointer" }}>›</button>
                  </div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", borderBottom: `1px solid ${C.border}` }}>
                  {[[calStats.p, "Profit", "#22C55E"], [calStats.l, "Loss", "#EF4444"], [calStats.b, "B/E", "#3B82F6"], [calStats.t, "Trades", "#818CF8"]].map(([v, l, c]) => (
                    <div key={l} style={{ padding: "10px 12px", textAlign: "center", borderRight: `1px solid ${C.border}` }}>
                      <span style={{ fontSize: 18, fontWeight: 700, color: c, display: "block" }}>{v}</span>
                      <span style={{ fontSize: 9, color: C.subtext, textTransform: "uppercase" }}>{l}</span>
                    </div>
                  ))}
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 3, padding: 10 }}>
                  {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(d => <div key={d} style={{ textAlign: "center", fontSize: 10, fontWeight: 500, color: C.subtext, padding: "4px 0" }}>{d}</div>)}
                  {calDays.map((day, i) => {
                    if (!day) return <div key={`e${i}`} style={{ minHeight: 72 }} />;
                    const dc = getDayColor(day);
                    return (
                      <div key={day.day} className="cd" onClick={() => day.trades.length > 0 && setCalPop(day)} style={{ minHeight: 72, borderRadius: 7, padding: "6px 7px", background: dc.bg, border: dc.border, cursor: day.trades.length ? "pointer" : "default" }}>
                        <div style={{ fontSize: 11, fontWeight: 500, color: C.subtext, marginBottom: 2 }}>{day.day}</div>
                        {day.trades.length > 0 && (
                          <>
                            <div style={{ fontSize: 12, fontWeight: 700, color: getDayC(day) }}>{day.pnl > 0 ? "+" : day.pnl < 0 ? "-" : ""}${Math.abs(day.pnl).toFixed(0)}</div>
                            <div style={{ fontSize: 9, color: C.subtext }}>{day.trades.length}t</div>
                            <div style={{ display: "flex", gap: 2, marginTop: 3 }}>{day.trades.map((t, ti) => <div key={ti} style={{ width: 5, height: 5, borderRadius: "50%", background: t.isBE ? "#3B82F6" : t.win ? "#22C55E" : "#EF4444" }} />)}</div>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
              <div style={{ ...cS, padding: "14px 12px", overflowY: "auto", maxHeight: 600 }} className="cal-sidebar">
                <div style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".1em", color: C.subtext, marginBottom: 12 }}>Weekly P&L</div>
                {wkStats.length === 0 && <div style={{ fontSize: 11, color: C.subtext, textAlign: "center", paddingTop: 16 }}>No data</div>}
                {wkStats.map(w => (
                  <div key={w.wi} style={{ marginBottom: 12, paddingBottom: 12, borderBottom: `1px solid ${C.border}` }}>
                    <div style={{ fontSize: 10, color: C.subtext, marginBottom: 3, fontWeight: 500, textTransform: "uppercase" }}>{w.label}</div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: w.pnl >= 0 ? "#22C55E" : "#EF4444" }}>{w.pnl >= 0 ? "+" : "-"}${Math.abs(w.pnl).toFixed(2)}</div>
                    {w.wr !== null && <div style={{ fontSize: 10, color: C.subtext, marginTop: 2 }}>{w.wr}% · {w.tt}t</div>}
                    <div style={{ height: 3, background: C.border, borderRadius: 2, marginTop: 5, overflow: "hidden" }}>
                      <div style={{ height: "100%", width: `${Math.min(Math.abs(w.pnl) / 80 * 100, 100)}%`, background: w.pnl >= 0 ? "#22C55E" : "#EF4444", borderRadius: 2 }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
            {showTabs && (
              <div style={cP}>
                <div style={{ fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 4 }}>🟩 Activity Heatmap</div>
                <div style={{ fontSize: 11, color: C.subtext, marginBottom: 16 }}>Last 16 weeks · Mon → Sun · click any cell to see trades</div>
                {trades.length === 0
                  ? <div style={{ textAlign: "center", padding: 40, color: C.subtext }}>No trades yet.</div>
                  : <TradeHeatmap trades={trades} beCap={beCap} C={C} onDayClick={cell => { if (cell.data && cell.data.trades.length > 0) setCalPop({ pnl: cell.data.pnl, trades: cell.data.trades }); }} />
                }
              </div>
            )}
          </>
        )}
        {calTab === "monthly" && (
          <div style={cS}>
            <div style={{ padding: "12px 16px", borderBottom: `1px solid ${C.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}><span style={{ fontSize: 13, fontWeight: 600, color: C.text }}>Monthly Performance</span><span style={{ fontSize: 10, color: C.subtext }}>{monthStats.length} months</span></div>
            <div style={{ overflowX: "auto" }}>
              <table><thead><tr><th>Month</th><th>Net P&L</th><th>% Gain</th><th>Trades</th><th>Win Rate</th><th>Result</th></tr></thead>
                <tbody>
                  {monthStats.length === 0 && <tr><td colSpan={6} style={{ textAlign: "center", color: C.subtext, padding: 30 }}>No data</td></tr>}
                  {monthStats.map(m => (
                    <tr key={m.key}>
                      <td style={{ fontWeight: 600, color: C.text }}>{m.label}</td>
                      <td style={{ fontWeight: 700, color: m.pnl >= 0 ? "#22C55E" : "#EF4444" }}>{m.pnl >= 0 ? "+" : ""}${m.pnl.toFixed(2)}</td>
                      <td><span style={{ fontWeight: 700, color: m.pct >= 0 ? "#22C55E" : "#EF4444", background: m.pct >= 0 ? "rgba(34,197,94,.08)" : "rgba(239,68,68,.08)", padding: "2px 8px", borderRadius: 5, fontSize: 11, border: `1px solid ${m.pct >= 0 ? "rgba(34,197,94,.2)" : "rgba(239,68,68,.2)"}` }}>{m.pct >= 0 ? "+" : ""}{m.pct.toFixed(2)}%</span></td>
                      <td style={{ color: "#818CF8" }}>{m.trades}</td>
                      <td><div style={{ display: "flex", alignItems: "center", gap: 6 }}><div style={{ flex: 1, height: 4, background: C.border, borderRadius: 2, overflow: "hidden", minWidth: 60 }}><div style={{ height: "100%", width: `${m.wr}%`, background: m.wr >= 50 ? "#22C55E" : "#EF4444" }} /></div><span style={{ fontSize: 10, color: C.subtext, minWidth: 32 }}>{m.wr.toFixed(0)}%</span></div></td>
                      <td><span style={{ padding: "2px 10px", borderRadius: 20, fontSize: 10, fontWeight: 600, background: m.pnl >= 0 ? "rgba(34,197,94,.1)" : "rgba(239,68,68,.1)", color: m.pnl >= 0 ? "#22C55E" : "#EF4444" }}>{m.pnl >= 0 ? "PROFIT" : "LOSS"}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {monthStats.length > 0 && (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 10, padding: "14px 16px", borderTop: `1px solid ${C.border}` }}>
                {[["Best Month", monthStats.reduce((a, b) => a.pnl > b.pnl ? a : b), "#22C55E"], ["Worst Month", monthStats.reduce((a, b) => a.pnl < b.pnl ? a : b), "#EF4444"], ["Avg Monthly", { label: "Average", pnl: monthStats.reduce((a, b) => a + b.pnl, 0) / monthStats.length }, "#818CF8"]].map(([l, m, c]) => (
                  <div key={l} style={{ background: C.insetBg, border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 12px" }}>
                    <div style={{ fontSize: 9, color: C.subtext, textTransform: "uppercase", marginBottom: 4 }}>{l}</div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: c }}>{m.pnl >= 0 ? "+" : ""}${m.pnl.toFixed(2)}</div>
                    <div style={{ fontSize: 10, color: C.subtext, marginTop: 2 }}>{m.label}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        {calTab === "weekly" && (
          <div style={cS}>
            <div style={{ padding: "12px 16px", borderBottom: `1px solid ${C.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}><span style={{ fontSize: 13, fontWeight: 600, color: C.text }}>Weekly Performance</span><span style={{ fontSize: 10, color: C.subtext }}>{weekStats.length} weeks</span></div>
            <div style={{ overflowX: "auto" }}>
              <table><thead><tr><th>Week</th><th>Net P&L</th><th>% Gain</th><th>Trades</th><th>Win Rate</th><th>Result</th></tr></thead>
                <tbody>
                  {weekStats.length === 0 && <tr><td colSpan={6} style={{ textAlign: "center", color: C.subtext, padding: 30 }}>No data</td></tr>}
          {weekStats.map(w => <tr key={w.key}>
            <td style={{ fontWeight: 600, color: C.text, fontSize: 11 }}>{w.label}</td>
            <td style={{ fontWeight: 700, color: w.pnl >= 0 ? "#22C55E" : "#EF4444" }}>{w.pnl >= 0 ? "+" : ""}${w.pnl.toFixed(2)}</td>
            <td><span style={{ fontWeight: 700, color: w.pct >= 0 ? "#22C55E" : "#EF4444", background: w.pct >= 0 ? "rgba(34,197,94,.08)" : "rgba(239,68,68,.08)", padding: "2px 8px", borderRadius: 5, fontSize: 11, border: `1px solid ${w.pct >= 0 ? "rgba(34,197,94,.2)" : "rgba(239,68,68,.2)"}` }}>{w.pct >= 0 ? "+" : ""}{w.pct.toFixed(2)}%</span></td>
            <td style={{ color: "#818CF8" }}>{w.trades}</td>
            <td><div style={{ display: "flex", alignItems: "center", gap: 6 }}><div style={{ flex: 1, height: 4, background: C.border, borderRadius: 2, overflow: "hidden", minWidth: 60 }}><div style={{ height: "100%", width: `${w.wr}%`, background: w.wr >= 50 ? "#22C55E" : "#EF4444" }} /></div><span style={{ fontSize: 10, color: C.subtext, minWidth: 32 }}>{w.wr.toFixed(0)}%</span></div></td>
            <td><span style={{ padding: "2px 10px", borderRadius: 20, fontSize: 10, fontWeight: 600, background: w.pnl >= 0 ? "rgba(34,197,94,.1)" : "rgba(239,68,68,.1)", color: w.pnl >= 0 ? "#22C55E" : "#EF4444" }}>{w.pnl >= 0 ? "PROFIT" : "LOSS"}</span></td>
          </tr>)}
        </tbody></table></div>
        {weekStats.length > 0 && <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 10, padding: "14px 16px", borderTop: `1px solid ${C.border}` }}>
          {[["Best Week", weekStats.reduce((a, b) => a.pnl > b.pnl ? a : b), "#22C55E"], ["Worst Week", weekStats.reduce((a, b) => a.pnl < b.pnl ? a : b), "#EF4444"], ["Avg Weekly", { label: "Average", pnl: weekStats.reduce((a, b) => a + b.pnl, 0) / weekStats.length }, "#818CF8"]].map(([l, wk, c]) => <div key={l} style={{ background: C.insetBg, border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 12px" }}><div style={{ fontSize: 9, color: C.subtext, textTransform: "uppercase", marginBottom: 4 }}>{l}</div><div style={{ fontSize: 13, fontWeight: 700, color: c }}>{wk.pnl >= 0 ? "+" : ""}${wk.pnl.toFixed(2)}</div><div style={{ fontSize: 10, color: C.subtext, marginTop: 2 }}>{wk.label}</div></div>)}
        </div>}
      </div>
        )}

      {calTab === "daily" && <div style={cS}>
        <div style={{ padding: "12px 16px", borderBottom: `1px solid ${C.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}><span style={{ fontSize: 13, fontWeight: 600, color: C.text }}>Performance by Day of Week</span></div>
        <div style={{ padding: "16px" }}>
          {dayStats.length === 0 && <div style={{ textAlign: "center", color: C.subtext, padding: 30 }}>No data</div>}
          {dayStats.map((d, i) => { const maxAbs = Math.max(...dayStats.map(x => Math.abs(x.pnl))); const barW = maxAbs > 0 ? Math.abs(d.pnl) / maxAbs * 100 : 0; return (<div key={d.day} style={{ marginBottom: 14 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 5 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}><span style={{ fontSize: 10, fontWeight: 700, color: C.subtext, width: 32 }}>{d.short}</span><span style={{ fontSize: 11, fontWeight: 600, color: d.pnl >= 0 ? "#22C55E" : "#EF4444" }}>{d.pnl >= 0 ? "+" : ""}${d.pnl.toFixed(0)}</span>{i === 0 && <span style={{ fontSize: 9, padding: "1px 6px", borderRadius: 10, background: "rgba(34,197,94,.12)", color: "#22C55E", border: "1px solid rgba(34,197,94,.3)" }}>BEST</span>}{i === dayStats.length - 1 && d.pnl < 0 && <span style={{ fontSize: 9, padding: "1px 6px", borderRadius: 10, background: "rgba(239,68,68,.1)", color: "#EF4444", border: "1px solid rgba(239,68,68,.3)" }}>WORST</span>}</div>
              <div style={{ display: "flex", gap: 12, fontSize: 10, color: C.subtext }}><span>{d.wr.toFixed(0)}% WR</span><span style={{ color: "#818CF8" }}>{d.trades}t</span></div>
            </div>
            <div style={{ height: 6, background: C.border, borderRadius: 3, overflow: "hidden" }}><div style={{ height: "100%", width: `${barW}%`, background: d.pnl >= 0 ? "linear-gradient(90deg,#16A34A,#22C55E)" : "linear-gradient(90deg,#991B1B,#EF4444)", borderRadius: 3, transition: "width .6s" }} /></div>
          </div>); })}
        </div>
        {dayStats.length > 0 && <div style={{ display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: 10, padding: "0 16px 16px" }}>{[["Most Profitable Day", dayStats[0], "#22C55E"], ["Least Profitable Day", dayStats[dayStats.length - 1], "#EF4444"]].map(([l, d, c]) => <div key={l} style={{ background: C.insetBg, border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 12px" }}><div style={{ fontSize: 9, color: C.subtext, textTransform: "uppercase", marginBottom: 4 }}>{l}</div><div style={{ fontSize: 16, fontWeight: 700, color: c }}>{d.day}</div><div style={{ fontSize: 11, color: c, marginTop: 2 }}>{d.pnl >= 0 ? "+" : ""}${d.pnl.toFixed(2)} · {d.wr.toFixed(0)}% WR</div></div>)}
        </div>}
      </div>}
    </div>);
  };

  const dynamicCSS = `
    *,::before,::after{box-sizing:border-box;margin:0;padding:0;}
    html,body{background:${C.bg};color:${C.text};font-family:'Inter',-apple-system,sans-serif;font-size:14px;min-height:100vh;-webkit-font-smoothing:antialiased;}
    ::-webkit-scrollbar{width:5px}
    ::-webkit-scrollbar-thumb{background:${C.border};border-radius:4px;}
        table{width:100%;border-collapse:collapse;font-size:13px;}
    html,body{overflow-x:hidden;max-width:100vw}
    .app-main{min-width:0;max-width:100vw}
    .app-content>*{min-width:0}
    th{padding:9px 12px;text-align:left;font-size:10px;font-weight:500;text-transform:uppercase;letter-spacing:.1em;color:${C.subtext};border-bottom:1px solid ${C.border};background:${C.tableBg};cursor:pointer;white-space:nowrap;}
    th:hover{color:${C.text};}
    td{padding:10px 12px;border-bottom:1px solid ${C.border};color:${C.text};}
    tr:last-child td{border-bottom:none;}
    tbody tr:hover td{background:${C.rowHover};}
    .cd:hover{transform:translateY(-2px) scale(1.03);box-shadow:0 6px 20px rgba(0,0,0,.${C.isLight ? "15" : "5"});z-index:2;position:relative;}
    input, select, textarea { color-scheme: ${C.isLight ? "light" : "dark"}; }
    select option { background: ${C.card}; color: ${C.text}; }
    @media(max-width:1100px){.r1{grid-template-columns:1fr 1fr!important}.r2{grid-template-columns:1fr!important}}
    @media(max-width:768px){.cal-grid{grid-template-columns:1fr!important}.cal-sidebar{display:none!important}}
        @media(hover:hover) and (pointer:fine){body.custom-cursor,body.custom-cursor *{cursor:none!important}}

    .sidebar-backdrop{display:none}
.app-topbar{flex-wrap:wrap;row-gap:6px}
.topbar-new{margin-left:auto}
    @media(max-width:640px){
      .app-sidebar{width:78vw!important;max-width:300px!important}
      .app-main{margin-left:0!important}
      .app-topbar{flex-wrap:wrap;padding:8px 12px!important;gap:6px}
      .topbar-username{display:none!important}
            .topbar-title{flex:1 1 auto!important;min-width:0!important}
      .topbar-new{order:2;margin-left:auto}
      .app-topbar .promo-pill{order:3;flex:0 0 auto!important;width:auto;margin:2px auto 0!important;padding:3px 4px 3px 10px!important;gap:6px!important;animation:promoBorderSpin 4s linear infinite,promoGlow 2.4s ease-in-out infinite!important}
      .promo-pill span:nth-child(1){font-size:11px!important}
      .promo-pill span:nth-child(2){font-size:10px!important;animation:promoSlide 7s ease-in-out infinite}
      .promo-pill button{font-size:9px!important;padding:2px 7px!important;animation:promoSlide 7s ease-in-out .15s infinite}
      @keyframes promoSlide{0%,72%{transform:translateX(0);opacity:1}80%{transform:translateX(-26px);opacity:0}81%{transform:translateX(26px);opacity:0}90%,100%{transform:translateX(0);opacity:1}}
      .grid2{grid-template-columns:1fr!important}
      table{display:block;overflow-x:auto;white-space:nowrap}
      .app-content{padding:10px 10px!important;gap:8px!important}
      .r1{grid-template-columns:1fr 1fr!important;gap:8px!important}
      table{font-size:11px}
      th,td{padding:7px 8px!important}
      .tradelog-row{flex-wrap:wrap;gap:8px!important}
      .modal-overlay{padding:0!important;align-items:flex-end!important}
      .modal-box{max-width:100%!important;width:100%!important;max-height:95vh!important;border-radius:14px 14px 0 0!important}
      .modal-grid3{grid-template-columns:1fr 1fr!important}
      .grid2{grid-template-columns:1fr!important}
      .sidebar-backdrop{display:block;position:fixed;inset:0;background:rgba(0,0,0,.5);z-index:90}
    }
        @keyframes promoBorderSpin {
      0% { background-position: 0% 50%; }
      100% { background-position: 200% 50%; }
    }
    @keyframes promoShine {
      0% { transform: translateX(-120%) skewX(-20deg); }
      60%,100% { transform: translateX(220%) skewX(-20deg); }
    }
    @keyframes promoGlow {
      0%,100% { box-shadow: 0 0 0px rgba(34,197,94,.0); }
      50% { box-shadow: 0 0 14px rgba(34,197,94,.35); }
    }
        .promo-pill{
      position: relative;
      overflow: hidden;
      background: linear-gradient(90deg,${C.card},${C.card}) padding-box,
                  linear-gradient(90deg,#22C55E,#818CF8,#22C55E) border-box;
      background-size: 200% 100%, 200% 100%;
      border: 1px solid transparent !important;
      animation: promoBorderSpin 4s linear infinite, promoGlow 2.4s ease-in-out infinite;
    }
    .promo-pill::after{
      content:"";
      position:absolute; top:0; left:0; width:40%; height:100%;
      background: linear-gradient(120deg, transparent, rgba(255,255,255,.35), transparent);
      animation: promoShine 3.2s ease-in-out infinite;
      pointer-events:none;
    }
    @media(max-width:420px){
      .r1{grid-template-columns:1fr!important}
      .modal-grid3{grid-template-columns:1fr!important}
    }
  
    .imp-btn{display:inline-flex;align-items:center;gap:7px;padding:7px 12px;border-radius:8px;font-size:12px;font-weight:600;white-space:nowrap;font-family:inherit;color:#22C55E;background:linear-gradient(${C.card},${C.card}) padding-box,linear-gradient(135deg,rgba(34,197,94,.7),rgba(129,140,248,.7)) border-box;border:1px solid transparent;transition:.2s}
    .imp-btn:hover{transform:translateY(-1px);box-shadow:0 0 16px rgba(34,197,94,.3)}
    .imp-tag{font-size:9px;font-weight:700;color:#818CF8;background:rgba(129,140,248,.12);border-radius:5px;padding:2px 6px}
    .sum-btn{color:#A78BFA}
    @media(max-width:640px){.imp-tag{display:none}}
  `;


  const sevColor = { success: "#22C55E", danger: "#EF4444", warning: "#F59E0B", info: "#818CF8" };
  const sevBg = { success: "rgba(34,197,94,.08)", danger: "rgba(239,68,68,.08)", warning: "rgba(245,158,11,.08)", info: "rgba(129,140,248,.08)" };
  const sevBorder = { success: "rgba(34,197,94,.2)", danger: "rgba(239,68,68,.2)", warning: "rgba(245,158,11,.2)", info: "rgba(129,140,248,.2)" };

  return (<>
    <style>{dynamicCSS}</style>
    {typeof window !== "undefined" && !("ontouchstart" in window) && navigator.maxTouchPoints === 0 && (
      <>
                <div id="cur" style={{ position: "fixed", width: 12, height: 12, borderRadius: "50%", background: "#22C55E", pointerEvents: "none", zIndex: 9999, top: 0, left: 0, willChange: "transform", opacity: 0, transition: "opacity .25s", boxShadow: "0 0 10px #22C55E,0 0 20px rgba(34,197,94,.5)" }} />
        <div id="cur2" style={{ position: "fixed", width: 32, height: 32, borderRadius: "50%", border: "1px solid rgba(34,197,94,.4)", pointerEvents: "none", zIndex: 9998, top: 0, left: 0, willChange: "transform", opacity: 0, transition: "opacity .25s" }} />
      </>
    )}

    <div style={{ display: "flex", minHeight: "100vh" }}>
      {/* SIDEBAR */}
      <aside className="app-sidebar" onMouseEnter={() => !sbPinned && setSbHover(true)} onMouseLeave={() => !sbPinned && setSbHover(false)} style={{ width: 220, minHeight: "100vh", background: C.card, borderRight: `1px solid ${C.border}`, display: "flex", flexDirection: "column", position: "fixed", left: 0, top: 0, bottom: 0, zIndex: 100, transform: sbOpen ? "none" : "translateX(-100%)", transition: "transform .25s ease", boxShadow: sbOpen && !sbPinned ? `4px 0 30px rgba(0,0,0,${C.isLight ? ".15" : ".5"})` : "none" }}>
        <div style={{ padding: "16px 14px", display: "flex", alignItems: "center", justifyContent: "center", borderBottom: `1px solid ${C.border}`, cursor: "pointer", overflow: "hidden", width: "100%" }} onClick={() => { setTab("dashboard"); if (!sbPinned) setSbHover(false); }}>
          <img src="/obsidex.svg" alt="Obsidex" style={{ width: 130, height: "auto", filter: C.isLight ? "invert(0)" : "invert(1)", display: "block" }} />
        </div>
        <nav style={{ flex: 1, padding: "8px 7px", overflowY: "auto" }}>
          <div style={{ fontSize: 10, fontWeight: 500, textTransform: "uppercase", letterSpacing: ".12em", color: C.subtext, padding: "0 8px", margin: "10px 0 4px" }}>Navigation</div>
          {navs.map(([id, lb]) => <div key={id} onClick={() => { setTab(id); if (!sbPinned) setSbHover(false); if (typeof window !== "undefined" && window.innerWidth < 640) setSbPinned(false); }} style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 9px", borderRadius: 7, cursor: "pointer", fontSize: 13, color: tab === id ? "#22C55E" : C.subtext, background: tab === id ? "rgba(34,197,94,.1)" : "transparent", fontWeight: tab === id ? 500 : 400, marginBottom: 1 }}>
            <span style={{ width: 18, display: "flex", justifyContent: "center", color: tab === id ? "#22C55E" : C.subtext }}>{IC[id] || IC.rules}</span>{lb}
          </div>)}
          <div style={{ fontSize: 10, fontWeight: 500, textTransform: "uppercase", letterSpacing: ".12em", color: C.subtext, padding: "0 8px", margin: "16px 0 4px" }}>Settings</div>
          <div onClick={() => setShowSettings(true)} style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 9px", borderRadius: 7, cursor: "pointer", fontSize: 13, color: C.subtext, background: "transparent", transition: "background .15s" }}
  onMouseEnter={e => e.currentTarget.style.background = C.insetBg}
  onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
  <span style={{ width: 18, textAlign: "center" }}>⚙️</span>Settings & Account
</div>
          <div onClick={onLogout} style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 9px", borderRadius: 7, cursor: "pointer", fontSize: 13, color: "#EF4444", marginTop: 2 }}><span style={{ width: 18, display: "flex", justifyContent: "center" }}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" /></svg></span>Logout</div>
        </nav>
        <div style={{ padding: "10px 9px", borderTop: `1px solid ${C.border}` }}>
          <ProfilePanel
            C={C}
            userName={userName}
            startBal={startBal}
            netPnl={A.netPnl}
            allAccounts={allAccounts}
            currentAccountId={currentAccountId}
            onSwitchAccount={onSwitchAccount}
            onAddAccount={onAddAccount}
          />
        </div>
      </aside>
      {sbPinned && <div className="sidebar-backdrop" onClick={() => setSbPinned(false)} />}

      <main className="app-main" style={{ marginLeft: sbPinned ? 220 : 0, flex: 1, display: "flex", flexDirection: "column", transition: "margin-left .25s ease" }}>
        {/* TOPBAR */}
        <div className="app-topbar" style={{ padding: "10px 20px", display: "flex", alignItems: "center", borderBottom: `1px solid ${C.border}`, background: C.card, position: "sticky", top: 0, zIndex: 50 }}>
          <button onClick={() => setSbPinned(p => !p)} style={{ background: sbPinned ? "rgba(34,197,94,.1)" : "transparent", border: `1px solid ${sbPinned ? "rgba(34,197,94,.3)" : C.border}`, borderRadius: 6, padding: "5px 7px", cursor: "pointer", marginRight: 12, display: "flex", alignItems: "center", justifyContent: "center", color: sbPinned ? "#22C55E" : C.subtext }}>{IC.menu}</button>
          <div className="topbar-title" style={{ flex: "0 0 auto", minWidth: 100 }}>
  <div style={{ fontSize: 15, fontWeight: 600, color: C.text }}>{navs.find(n => n[0] === tab)?.[1]}</div>
  <div style={{ fontSize: 10, color: C.subtext, marginTop: 1 }}>{new Date().toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}</div>
</div>

                    <div className="topbar-username" style={{ flex: 1, display: "flex", justifyContent: "center", paddingRight: 40 }}>{userName && <span style={{ fontSize: 14, fontWeight: 700, letterSpacing: ".04em", background: "linear-gradient(135deg,#A78BFA,#818CF8,#6366F1)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", filter: "drop-shadow(0 0 8px rgba(139,92,246,.3))" }}>{userName}</span>}</div>
          <div
  className="promo-pill"
  style={{
    display: "inline-flex", alignItems: "center", gap: 8, flexShrink: 0,
    borderRadius: 999, padding: "6px 8px 6px 14px",
    marginRight: 10, transition: "transform .15s", cursor: "pointer"
  }}
  onClick={() => window.open("https://apextraderfunding.com/member/aff/go/naineetpujar", "_blank", "noopener,noreferrer")}
  onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-1px)"; }}
  onMouseLeave={e => { e.currentTarget.style.transform = "none"; }}>
  <span style={{ fontSize: 13 }}>🎁</span>
  <span style={{ fontSize: 11, fontWeight: 700, color: "#22C55E", whiteSpace: "nowrap" }}>90% OFF Apex</span>
  <button
    onClick={e => { e.stopPropagation(); navigator.clipboard.writeText("NAINEET"); e.currentTarget.textContent = "Copied ✓"; setTimeout(() => { e.currentTarget.textContent = "CODE: NAINEET"; }, 1500); }}
    style={{ fontSize: 10, fontWeight: 700, color: "#818CF8", background: "rgba(129,140,248,.12)", border: "1px solid rgba(129,140,248,.3)", borderRadius: 6, padding: "3px 8px", cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap" }}>
    CODE: NAINEET
  </button>
</div>
      
<div
  className="topbar-new"
  style={{
    flex: "0 0 auto",
    display: "flex",
    gap: 6,
    position: "relative",
    zIndex: 100
  }}
>
  <button className="imp-btn" onClick={() => setShowImport(true)}>
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 16V4M7 9l5-5 5 5M4 20h16"/></svg>
  Import<span className="imp-tag">MT5 · Tradovate</span>
</button>

  <button
    onClick={() => {
      setEditT(null);
      setModal(true);
    }}
    style={{
      background: "transparent",
      border: `1px solid ${C.border}`,
      borderRadius: 7,
      padding: "8px 12px",
      color: C.text,
      fontSize: 12,
      fontWeight: 600,
      cursor: "pointer",
      whiteSpace: "nowrap"
    }}
  >
    + New Trade
  </button>
</div>
        </div>

        <div className="app-content" style={{ padding: "16px 18px", display: "flex", flexDirection: "column", gap: 12 }}>

          {/* ─── DASHBOARD ─── */}
          {tab === "dashboard" && (
            <>{trades.length === 0 ?
              <div style={{ textAlign: "center", padding: "60px 20px", color: C.subtext }}>
                <div style={{ fontSize: 16, fontWeight: 600, color: C.text, marginBottom: 6 }}>Welcome to Obsidex Journal</div>
                <div style={{ fontSize: 13, marginBottom: 16 }}>Add your first trade.</div>
                <button onClick={() => { setEditT(null); setModal(true); }} style={{ background: "#22C55E", border: "none", borderRadius: 7, padding: "9px 20px", color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>+ Add First Trade</button>
              </div> : <>
                <div className="r1" style={{ display: "grid", gridTemplateColumns: "repeat(5,1fr)", gap: 10 }}>
                  <MC label={`Net P&L · ${trades.length}t`} value={fp(A.netPnl)} color={A.netPnl >= 0 ? "#22C55E" : "#EF4444"} sub={<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 9, color: C.subtext, paddingTop: 5, borderTop: `1px solid ${C.border}` }}><span>+${A.totalProfit.toFixed(0)}</span><span style={{ fontWeight: 700, color: pctChange >= 0 ? "#22C55E" : "#EF4444", fontSize: 10 }}>{pctChange >= 0 ? "+" : ""}{pctChange.toFixed(2)}%</span><span>-${A.totalLoss.toFixed(0)}</span></div>} />
                  <MC label="Trade Win %" value={`${((A.winRate || 0) * 100).toFixed(1)}%`} color={A.winRate >= .5 ? "#22C55E" : "#EF4444"}><SemiGauge wc={A.wc} lc={A.lc} be={A.be} size={64} /></MC>
                  <MC label="Profit Factor" value={(A.profitFactor || 0).toFixed(2)} color={(A.profitFactor || 0) >= 1.5 ? "#22C55E" : (A.profitFactor || 0) >= 1 ? "#F59E0B" : "#EF4444"}><CircleGauge value={Math.min(A.profitFactor || 0, 4)} max={4} size={50} strokeW={5} trackColor="#EF4444" /></MC>
                  <div style={{ ...cP, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, minHeight: 88 }}>
                    <StreakWidget trades={trades} dayWR={dayWR} C={C} />
                    <CircleGauge value={dayWR * 100} max={100} size={50} strokeW={5} trackColor={dayWR === 0 ? C.border : "#EF4444"} />
                  </div>
                  <MC label="Avg Win/Loss" value={A.avgLoss > 0 ? `${(A.avgWin / A.avgLoss).toFixed(2)}` : "∞"} color={C.text} sub={<WinLossBar avgWin={A.avgWin || 0} avgLoss={A.avgLoss || 0} />} />
                </div>
                <div className="r2" style={{ display: "grid", gridTemplateColumns: "300px 1fr 1fr", gap: 10 }}>
                  <div style={{ ...cP, display: "flex", flexDirection: "column", minHeight: 340 }}>
                    <div style={{ fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".1em", color: C.subtext, marginBottom: 4 }}>⚡ Thunder Score</div>
                    <ThunderScore data={A} />
                  </div>
                  <div style={cS}>
                    <div style={{ padding: "11px 14px 9px", display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: `1px solid ${C.border}` }}><span style={{ fontSize: 12, fontWeight: 600, color: C.text }}>Cumulative P&L</span><div style={{ display: "flex", alignItems: "center", gap: 8 }}><span style={{ fontSize: 11, fontWeight: 700, color: pctChange >= 0 ? "#22C55E" : "#EF4444", background: pctChange >= 0 ? "rgba(34,197,94,.08)" : "rgba(239,68,68,.08)", padding: "2px 7px", borderRadius: 5, border: `1px solid ${pctChange >= 0 ? "rgba(34,197,94,.25)" : "rgba(239,68,68,.25)"}` }}>{pctChange >= 0 ? "↑" : "↓"}{Math.abs(pctChange).toFixed(2)}%</span><span style={{ fontSize: 12, fontWeight: 600, color: A.netPnl >= 0 ? "#22C55E" : "#EF4444" }}>{fp(A.netPnl)}</span></div></div>
                    <div style={{ padding: "6px 10px 10px" }}><ResponsiveContainer width="100%" height={210}><AreaChart data={ddD}><defs><linearGradient id="cg" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#22C55E" stopOpacity={.2} /><stop offset="100%" stopColor="#22C55E" stopOpacity={0} /></linearGradient></defs><CartesianGrid stroke={C.chartGrid} vertical={false} /><XAxis dataKey="name" tick={{ fill: ac, fontSize: 9 }} axisLine={false} tickLine={false} /><YAxis tick={{ fill: ac, fontSize: 9 }} axisLine={false} tickLine={false} tickFormatter={v => `$${v}`} width={46} /><Tooltip content={<CTip />} /><Area type="monotone" dataKey="cum" name="Cumulative" stroke="#22C55E" strokeWidth={2} fill="url(#cg)" dot={false} /></AreaChart></ResponsiveContainer></div>
                  </div>
                  <div style={cS}>
                    <div style={{ padding: "11px 14px 9px", display: "flex", justifyContent: "space-between", borderBottom: `1px solid ${C.border}` }}><span style={{ fontSize: 12, fontWeight: 600, color: C.text }}>Daily P&L</span><span style={{ fontSize: 11, fontWeight: 600, color: "#818CF8" }}>{ddD.filter(d => d.daily > 0).length}/{ddD.length}</span></div>
                    <div style={{ padding: "6px 10px 10px" }}><ResponsiveContainer width="100%" height={210}><BarChart data={ddD} barSize={10}><CartesianGrid stroke={C.chartGrid} vertical={false} /><XAxis dataKey="name" tick={{ fill: ac, fontSize: 9 }} axisLine={false} tickLine={false} /><YAxis tick={{ fill: ac, fontSize: 9 }} axisLine={false} tickLine={false} tickFormatter={v => `$${v}`} width={46} /><Tooltip content={<CTip />} /><Bar dataKey="daily" name="Daily" radius={[2, 2, 0, 0]}>{ddD.map((d, i) => <Cell key={i} fill={d.daily >= 0 ? "#22C55E" : "#EF4444"} opacity={.85} />)}</Bar></BarChart></ResponsiveContainer></div>
                  </div>
                </div>
                <CalView showTabs={false} />
              </>
            }</>
          )}

          {/* ─── TRADE LOG ─── */}
          {tab === "trades" && (
  <>
    <div style={{ display: "flex", gap: 7, marginBottom: 10, flexWrap: "wrap", alignItems: "center" }}>
      <input value={sq} onChange={e => setSq(e.target.value)} placeholder="Search…" style={{ flex: 1, minWidth: 140, background: C.card, border: `1px solid ${C.border}`, borderRadius: 7, padding: "7px 11px", color: C.text, fontSize: 12, outline: "none", fontFamily: "inherit" }} />
      {[[fA, setFA, ["All", ...new Set(trades.map(t => t.asset))]], [fS, setFS, ["All", ...SESSIONS]], [fD, setFD, ["All", "buy", "sell"]]].map(([v, fn, opts], i) => <select key={i} value={v} onChange={e => fn(e.target.value)} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 7, padding: "7px 9px", color: C.subtext, fontSize: 11, outline: "none", cursor: "pointer", fontFamily: "inherit" }}>{opts.map(o => <option key={o}>{o}</option>)}</select>)}
      <div style={{ display: "flex", gap: 4 }}>
        {["date","pnl","asset"].map(col => (
          <button key={col} onClick={() => doSort(col)} style={{ background: sc === col ? "rgba(34,197,94,.1)" : C.card, border: `1px solid ${sc === col ? "rgba(34,197,94,.3)" : C.border}`, borderRadius: 6, padding: "5px 10px", fontSize: 10, fontWeight: 600, color: sc === col ? "#22C55E" : C.subtext, cursor: "pointer", fontFamily: "inherit" }}>
            {col === "date" ? "Date" : col === "pnl" ? "P&L" : "Asset"} {sc === col ? (sd === "asc" ? "↑" : "↓") : ""}
          </button>
        ))}
      </div>
    </div>

    {/* Summary Bar */}
    {ft.length > 0 && (
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 8, marginBottom: 10 }}>
        {(() => {
          const fw = ft.filter(t => t.win), fl = ft.filter(t => !t.win && !t.isBE);
          const fnet = ft.reduce((a,t) => a+t.pnl, 0);
          const fwr = (fw.length+fl.length) ? fw.length/(fw.length+fl.length)*100 : 0;
          return [
            ["Showing", `${ft.length} trades`, "#818CF8"],
            ["Net P&L", `${fnet>=0?"+":""}$${fnet.toFixed(0)}`, fnet>=0?"#22C55E":"#EF4444"],
            ["Win Rate", `${fwr.toFixed(0)}%`, fwr>=50?"#22C55E":"#EF4444"],
            ["W/L/BE", `${fw.length}/${fl.length}/${ft.filter(t=>t.isBE).length}`, C.subtext],
          ].map(([l,v,c]) => (
            <div key={l} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, padding: "8px 12px", textAlign: "center" }}>
              <div style={{ fontSize: 9, color: C.subtext, textTransform: "uppercase", marginBottom: 3 }}>{l}</div>
              <div style={{ fontSize: 15, fontWeight: 700, color: c }}>{v}</div>
            </div>
          ));
        })()}
      </div>
    )}

    {!ft.length ? (
      <div style={{ textAlign: "center", padding: 40, color: C.subtext }}>No trades match your filters.</div>
    ) : (
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {ft.map(t => {
          const imgs = t.chartImages || (t.chartImage ? [t.chartImage] : []);
          const isExpanded = expandedTradeLog.has(t.id);
          return (
            <div key={t.id} style={{ background: C.card, border: `1px solid ${t.isBE ? "rgba(59,130,246,.25)" : t.win ? "rgba(34,197,94,.2)" : "rgba(239,68,68,.2)"}`, borderRadius: 12, overflow: "hidden", transition: "box-shadow .2s" }}>
              {/* Main Row */}
              <div className="tradelog-row" onClick={() => setExpandedTradeLog(prev => { const s = new Set(prev); s.has(t.id) ? s.delete(t.id) : s.add(t.id); return s; })}
                style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 16px", cursor: "pointer" }}>
                {/* Color dot */}
                <div style={{ width: 10, height: 10, borderRadius: "50%", background: t.isBE ? "#3B82F6" : t.win ? "#22C55E" : "#EF4444", flexShrink: 0, boxShadow: `0 0 6px ${t.isBE ? "#3B82F6" : t.win ? "#22C55E" : "#EF4444"}60` }} />
                {/* Asset + direction */}
                <div style={{ minWidth: 90 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>{t.asset}</div>
                  <div style={{ display: "flex", gap: 4, marginTop: 2 }}>
                    <span style={{ fontSize: 9, padding: "1px 6px", borderRadius: 10, background: t.direction === "buy" ? "rgba(34,197,94,.12)" : "rgba(239,68,68,.12)", color: t.direction === "buy" ? "#22C55E" : "#EF4444", fontWeight: 600 }}>{t.direction.toUpperCase()}</span>
                    <span style={{ fontSize: 9, padding: "1px 6px", borderRadius: 10, background: C.border, color: C.subtext }}>{t.session}</span>
                  </div>
                </div>
                {/* Date */}
                <div style={{ minWidth: 72, fontSize: 11, color: C.subtext }}>
                  {new Date(t.date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "2-digit" })}
                </div>
                {/* P&L */}
                <div style={{ minWidth: 80, textAlign: "right" }}>
                  <div style={{ fontSize: 16, fontWeight: 800, color: t.isBE ? "#3B82F6" : t.pnl >= 0 ? "#22C55E" : "#EF4444" }}>
                    {t.isBE ? "B/E" : `${t.pnl >= 0 ? "+" : ""}$${t.pnl.toFixed(2)}`}
                  </div>
                  {t.rr > 0 && <div style={{ fontSize: 10, color: "#818CF8" }}>{t.rr.toFixed(2)}R</div>}
                </div>
                {/* Tags preview */}
                <div style={{ flex: 1, display: "flex", flexWrap: "wrap", gap: 3 }}>
                  {(t.tags || []).slice(0,3).map(tg => (
                    <span key={tg} style={{ fontSize: 9, padding: "2px 6px", borderRadius: 10, background: MISTAKE_TAGS.includes(tg) ? "rgba(239,68,68,.1)" : "rgba(139,92,246,.1)", color: MISTAKE_TAGS.includes(tg) ? "#EF4444" : "#A78BFA", border: `1px solid ${MISTAKE_TAGS.includes(tg) ? "rgba(239,68,68,.2)" : "rgba(139,92,246,.2)"}` }}>{tg}</span>
                  ))}
                  {(t.tags||[]).length > 3 && <span style={{ fontSize: 9, color: C.subtext }}>+{t.tags.length-3}</span>}
                </div>
                {/* Rating */}
                <div style={{ flexShrink: 0 }}><RatingWidget value={t.rating||0} type={ratingType} size={11} /></div>
                {/* Result badge */}
                <span style={{ fontSize: 10, fontWeight: 700, padding: "3px 10px", borderRadius: 10, background: t.isBE ? "rgba(59,130,246,.12)" : t.win ? "rgba(34,197,94,.12)" : "rgba(239,68,68,.12)", color: t.isBE ? "#3B82F6" : t.win ? "#22C55E" : "#EF4444", border: `1px solid ${t.isBE ? "rgba(59,130,246,.3)" : t.win ? "rgba(34,197,94,.3)" : "rgba(239,68,68,.3)"}`, flexShrink: 0 }}>
                  {t.isBE ? "B/E" : t.win ? "WIN" : "LOSS"}
                </span>
                {/* Action buttons — always visible */}
                <div style={{ display: "flex", gap: 5, flexShrink: 0 }} onClick={e => e.stopPropagation()}>
                  <button onClick={() => { setEditT(t); setModal(true); }}
                    style={{ background: "rgba(34,197,94,.1)", border: "1px solid rgba(34,197,94,.3)", borderRadius: 6, padding: "4px 10px", color: "#22C55E", fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
                    Edit
                  </button>
                  <button onClick={() => { if(window.confirm("Delete this trade?")) del(t.id); }}
                    style={{ background: "rgba(239,68,68,.1)", border: "1px solid rgba(239,68,68,.3)", borderRadius: 6, padding: "4px 10px", color: "#EF4444", fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
                    Del
                  </button>
                </div>
                <span style={{ fontSize: 10, color: C.subtext, marginLeft: 2 }}>{isExpanded ? "▲" : "▼"}</span>
              </div>

              {/* Expanded detail */}
              {isExpanded && (
                <div style={{ padding: "0 16px 14px", borderTop: `1px solid ${C.border}` }}>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 8, marginTop: 12, marginBottom: 12 }}>
                    {[
                      ["Date", new Date(t.date).toLocaleDateString("en-US", { weekday:"short", month:"short", day:"numeric" }), C.text],
                      ["Emotion Before", t.emotionBefore || "—", "#A78BFA"],
                      ["Emotion After", t.emotionAfter || "—", "#818CF8"],
                      ["Session", t.session, C.subtext],
                    ].map(([l,v,c]) => (
                      <div key={l} style={{ background: C.insetBg, border: `1px solid ${C.border}`, borderRadius: 7, padding: "8px 10px" }}>
                        <div style={{ fontSize: 9, color: C.subtext, textTransform: "uppercase", marginBottom: 3 }}>{l}</div>
                        <div style={{ fontSize: 12, fontWeight: 600, color: c }}>{v}</div>
                      </div>
                    ))}
                  </div>
                  {(t.mistakes||[]).length > 0 && (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 8 }}>
                      {t.mistakes.map(m => <span key={m} style={{ fontSize: 9, padding: "2px 8px", borderRadius: 10, background: "rgba(239,68,68,.1)", color: "#EF4444", border: "1px solid rgba(239,68,68,.25)" }}>⚠ {m}</span>)}
                    </div>
                  )}
                  {t.notes && (
                    <div style={{ fontSize: 11, color: C.subtext, lineHeight: 1.6, fontStyle: "italic", padding: "8px 12px", background: C.insetBg, border: `1px solid ${C.border}`, borderRadius: 8, marginBottom: 8 }}>
                      "{t.notes}"
                    </div>
                  )}
                  {imgs.length > 0 && (
                    <div style={{ display: "flex", gap: 6, overflowX: "auto" }}>
                      {imgs.map((img, i) => (
                        <div key={i} style={{ flexShrink: 0, width: 140, height: 85, borderRadius: 8, overflow: "hidden", border: `1px solid ${C.border}` }}>
                          <img src={img} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    )}
  </>
)}

          {/* ─── ANALYTICS ─── */}
          {tab === "analytics" && (
            <>
              <PerfStrip A={A} total={trades.length} C={C} />
<TagBoard A={A} trades={trades} C={C} />
              {/* Session Performance */}
              <div style={cP}>
                <div style={{ fontSize: 12, fontWeight: 700, color: C.text, marginBottom: 16 }}>🕐 Session Performance</div>
                {Object.keys(A.sessions || {}).length === 0
                  ? <div style={{ textAlign: "center", color: C.subtext, padding: 20 }}>No session data</div>
                  : (() => {
                      const sessEntries = Object.entries(A.sessions || {});
                      const maxAbsPnl = Math.max(...sessEntries.map(([, d]) => Math.abs(d.pnl)), 1);
                      const SESS_COLORS = { Asia: { stroke: "#818CF8", glow: "rgba(129,140,248,.4)", bg: "rgba(129,140,248,.08)" }, London: { stroke: "#3B82F6", glow: "rgba(59,130,246,.4)", bg: "rgba(59,130,246,.08)" }, "NY AM": { stroke: "#F59E0B", glow: "rgba(245,158,11,.4)", bg: "rgba(245,158,11,.08)" }, "NY PM": { stroke: "#A855F7", glow: "rgba(168,85,247,.4)", bg: "rgba(168,85,247,.08)" } };
                      return (
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(220px,1fr))", gap: 12 }}>
                          {sessEntries.map(([sess, d]) => {
                            const wr = d.count ? d.wins / d.count * 100 : 0;
                            const pnlPct = Math.abs(d.pnl) / maxAbsPnl;
                            const sc2 = SESS_COLORS[sess] || { stroke: "#22C55E", glow: "rgba(34,197,94,.4)", bg: "rgba(34,197,94,.08)" };
                            const pnlCol = d.pnl >= 0 ? "#22C55E" : "#EF4444";
                            const GAUGE_SIZE = 80, SW = 7, R = (GAUGE_SIZE - SW) / 2;
                            const wrPct = wr / 100;
                            const wrCol = wr >= 60 ? "#22C55E" : wr >= 45 ? "#F59E0B" : "#EF4444";
                            const axes = [
                              { label: "WR", score: Math.min(wr * 1.4, 100) },
                              { label: "PnL", score: Math.min(pnlPct * 100, 100) },
                              { label: "Vol", score: Math.min((d.count / Math.max(...sessEntries.map(([,x]) => x.count), 1)) * 100, 100) },
                              { label: "Avg", score: d.count ? Math.min(Math.max((d.pnl / d.count + 200) / 4, 0), 100) : 0 },
                            ];
                            const n = axes.length, cx2 = 40, cy2 = 40, mR2 = 28;
                            const pt2 = (i, r2) => { const a = -Math.PI / 2 + (2 * Math.PI * i) / n; return { x: cx2 + r2 * Math.cos(a), y: cy2 + r2 * Math.sin(a) }; };
                            const dp2 = axes.map((ax, i) => pt2(i, (ax.score / 100) * mR2));
                            const grids2 = [0.33, 0.66, 1].map(s => Array.from({ length: n }, (_, i) => pt2(i, mR2 * s)).map(p => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" "));
                            const totalScore = Math.round(axes.reduce((a, x) => a + x.score, 0) / axes.length);
                            const scoreCol = totalScore >= 70 ? "#22C55E" : totalScore >= 45 ? "#F59E0B" : "#EF4444";
                            return (
                              <div key={sess} style={{ background: sc2.bg, border: `1px solid ${sc2.stroke}40`, borderRadius: 14, padding: "16px 16px 14px", position: "relative", overflow: "hidden" }}>
                                <div style={{ position: "absolute", top: -20, right: -20, width: 80, height: 80, borderRadius: "50%", background: sc2.glow, filter: "blur(28px)", opacity: 0.5, pointerEvents: "none" }} />
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
                                  <div>
                                    <span style={bSt(sB(sess))}>{sess}</span>
                                    <div style={{ fontSize: 10, color: C.subtext, marginTop: 4 }}>{d.count} trades</div>
                                  </div>
                                  <svg width="80" height="80" viewBox="0 0 80 80">
                                    {grids2.map((g, gi) => <polygon key={gi} points={g} fill="none" stroke={sc2.stroke} strokeWidth={gi === 2 ? 0.8 : 0.4} strokeOpacity={0.3} />)}
                                    {Array.from({ length: n }, (_, i) => { const o = pt2(i, mR2); return <line key={i} x1={cx2} y1={cy2} x2={o.x} y2={o.y} stroke={sc2.stroke} strokeWidth={0.4} strokeOpacity={0.3} />; })}
                                    <polygon points={dp2.map(p => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ")} fill={sc2.stroke} fillOpacity={0.2} stroke={sc2.stroke} strokeWidth={1.5} />
                                    {dp2.map((p, i) => <circle key={i} cx={p.x} cy={p.y} r={2.5} fill={sc2.stroke} />)}
                                    {axes.map((ax, i) => { const lp = pt2(i, mR2 + 12); return <text key={i} x={lp.x} y={lp.y + 3} textAnchor="middle" fill={sc2.stroke} fontSize="7" fontWeight="700" opacity={0.8}>{ax.label}</text>; })}
                                  </svg>
                                </div>
                                <div style={{ fontSize: 22, fontWeight: 800, color: pnlCol, lineHeight: 1, marginBottom: 4, letterSpacing: "-0.5px" }}>
                                  {d.pnl >= 0 ? "+" : ""}<span style={{ fontSize: 13, fontWeight: 500, opacity: 0.7 }}>$</span>{Math.abs(d.pnl).toFixed(0)}
                                </div>
                                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                                  <div style={{ position: "relative", width: GAUGE_SIZE, height: GAUGE_SIZE / 2 + SW / 2, flexShrink: 0 }}>
                                    <svg width={GAUGE_SIZE} height={GAUGE_SIZE / 2 + SW / 2} viewBox={`0 0 ${GAUGE_SIZE} ${GAUGE_SIZE / 2 + SW / 2}`}>
                                      <path d={`M ${SW/2} ${GAUGE_SIZE/2} A ${R} ${R} 0 0 1 ${GAUGE_SIZE-SW/2} ${GAUGE_SIZE/2}`} fill="none" stroke={C.border} strokeWidth={SW} strokeLinecap="round" />
                                      <path d={`M ${SW/2} ${GAUGE_SIZE/2} A ${R} ${R} 0 0 1 ${GAUGE_SIZE-SW/2} ${GAUGE_SIZE/2}`} fill="none" stroke={wrCol} strokeWidth={SW} strokeLinecap="round"
                                        strokeDasharray={`${Math.PI * R}`} strokeDashoffset={`${Math.PI * R * (1 - wrPct)}`} style={{ transition: "stroke-dashoffset 1s", filter: `drop-shadow(0 0 4px ${wrCol}80)` }} />
                                    </svg>
                                    <div style={{ position: "absolute", bottom: 0, left: "50%", transform: "translateX(-50%)", textAlign: "center" }}>
                                      <div style={{ fontSize: 14, fontWeight: 800, color: wrCol, lineHeight: 1 }}>{wr.toFixed(0)}%</div>
                                      <div style={{ fontSize: 8, color: C.subtext }}>WR</div>
                                    </div>
                                  </div>
                                  <div style={{ flex: 1 }}>
                                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, marginBottom: 4 }}>
                                      <span style={{ color: "#22C55E" }}>✓ {d.wins}W</span>
                                      <span style={{ color: "#EF4444" }}>✗ {d.count - d.wins}L</span>
                                    </div>
                                    <div style={{ height: 5, background: C.border, borderRadius: 3, overflow: "hidden" }}>
                                      <div style={{ display: "flex", height: "100%" }}>
                                        <div style={{ width: `${wr}%`, background: "#22C55E", borderRadius: "3px 0 0 3px" }} />
                                        <div style={{ width: `${100 - wr}%`, background: "#EF4444", borderRadius: "0 3px 3px 0" }} />
                                      </div>
                                    </div>
                                    <div style={{ fontSize: 9, color: C.subtext, marginTop: 4 }}>
                                      Avg: <span style={{ color: d.pnl / d.count >= 0 ? "#22C55E" : "#EF4444", fontWeight: 600 }}>{d.pnl / d.count >= 0 ? "+" : ""}${(d.pnl / d.count).toFixed(0)}/trade</span>
                                    </div>
                                  </div>
                                </div>
                                <div style={{ height: 3, background: "linear-gradient(90deg,#8B1A1A,#E85D2A 30%,#F59E0B 60%,#22C55E)", borderRadius: 2, position: "relative", marginTop: 4 }}>
                                  <div style={{ position: "absolute", top: "50%", left: `${totalScore}%`, transform: "translate(-50%,-50%)", width: 9, height: 9, borderRadius: "50%", background: scoreCol, border: "2px solid #fff", boxShadow: `0 0 6px ${scoreCol}` }} />
                                </div>
                                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 8, color: C.subtext, marginTop: 3 }}>
                                  <span>Score</span><span style={{ color: scoreCol, fontWeight: 700 }}>{totalScore}/100</span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()
                }
              </div>

              <ConsistencyPanel trades={trades} C={C} />

              <InstrumentBoard stats={instrumentStats} C={C} />

              <TagFilterBuilder trades={trades} savedTags={savedTags} ratingType={ratingType} beCap={beCap} onEditTrade={t => { setEditT(t); setModal(true); }} />

              <div style={{ ...cS, marginTop: 0 }}>
                <div style={{ padding: "12px 16px", borderBottom: `1px solid ${C.border}`, display: "flex", justifyContent: "space-between" }}><span style={{ fontSize: 13, fontWeight: 600, color: C.text }}>Equity</span><span style={{ fontSize: 11, fontWeight: 700, color: pctChange >= 0 ? "#22C55E" : "#EF4444" }}>{pctChange >= 0 ? "↑" : "↓"}{Math.abs(pctChange).toFixed(2)}%</span></div>
                <div style={{ padding: "10px 14px 14px" }}><ResponsiveContainer width="100%" height={220}><AreaChart data={eqD}><defs><linearGradient id="eq2" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#6366F1" stopOpacity={.15} /><stop offset="100%" stopColor="#6366F1" stopOpacity={0} /></linearGradient></defs><CartesianGrid stroke={C.chartGrid} vertical={false} /><XAxis dataKey="name" tick={{ fill: ac, fontSize: 9 }} axisLine={false} tickLine={false} /><YAxis tick={{ fill: ac, fontSize: 9 }} axisLine={false} tickLine={false} tickFormatter={v => `$${(v / 1000).toFixed(1)}k`} width={50} domain={["dataMin-500", "dataMax+500"]} /><Tooltip content={<CTip />} /><Area type="monotone" dataKey="balance" name="Balance" stroke="#6366F1" strokeWidth={2} fill="url(#eq2)" dot={false} /></AreaChart></ResponsiveContainer></div>
              </div>
            </>
          )}

          {/* ─── CALENDAR ─── */}
          {tab === "calendar" && <CalView showTabs={true} />}

          {/* ─── PSYCHOLOGY ─── */}
          {tab === "psychology" && (
            <div className="grid2" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <div style={cP}>
                <div style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", color: C.subtext, marginBottom: 12 }}>Win Rate by Emotion</div>
                {Object.entries(A.emotionMap || {}).sort((a, b) => b[1].count - a[1].count).map(([e, d]) => { const wr = d.count ? Math.round(d.wins / d.count * 100) : 0; const col = wr >= 60 ? "#22C55E" : wr >= 40 ? "#F59E0B" : "#EF4444"; return <div key={e} style={{ marginBottom: 12 }}><div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}><span style={{ fontSize: 12, fontWeight: 500, color: C.text }}>{e}</span><span style={{ fontSize: 12, fontWeight: 700, color: col }}>{wr}%</span></div><div style={{ height: 3, background: C.border, borderRadius: 2, overflow: "hidden" }}><div style={{ height: "100%", width: `${wr}%`, background: col }} /></div><div style={{ fontSize: 9, color: C.subtext, marginTop: 2 }}>{d.count} trades</div></div>; })}
              </div>
              <div style={cP}>
                <div style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", color: C.subtext, marginBottom: 12 }}>Mistakes</div>
                {Object.entries(A.tagMap || {}).filter(([t]) => MISTAKE_TAGS.includes(t)).sort((a, b) => b[1].count - a[1].count).map(([tag, d]) => { const wr = d.count ? Math.round(d.wins / d.count * 100) : 0; return <div key={tag} style={{ background: C.insetBg, border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 14px", marginBottom: 7 }}><div style={{ display: "flex", justifyContent: "space-between" }}><span style={{ fontSize: 12, fontWeight: 600, color: "#EF4444" }}>{tag}</span><span style={{ fontSize: 10, color: C.subtext }}>{d.count}t</span></div><div style={{ fontSize: 10, color: C.subtext, marginTop: 3 }}>{wr}% WR · ${d.pnl.toFixed(2)}</div></div>; })}
                {!Object.entries(A.tagMap || {}).some(([t]) => MISTAKE_TAGS.includes(t)) && <div style={{ color: C.subtext, textAlign: "center", padding: 20 }}>No mistakes</div>}
              </div>
            </div>
          )}

          {/* ─── INSIGHTS ─── */}
          {tab === "insights" && (
            <>
              {smartInsights.length === 0 && (
                <div style={{ textAlign: "center", padding: 40, color: C.subtext }}>
                  <div style={{ fontSize: 24, marginBottom: 8 }}>🧠</div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: C.text, marginBottom: 6 }}>Not enough data yet</div>
                  <div style={{ fontSize: 12 }}>Add at least 5 trades to unlock AI insights about your trading patterns.</div>
                </div>
              )}
              {Object.entries(A.tagMap || {}).filter(([t]) => !MISTAKE_TAGS.includes(t) && A.tagMap[t].count >= 2).length > 0 && (
                <div style={{ ...cP, marginBottom: 4 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: C.text, marginBottom: 10 }}>🔑 Best Confluences & Key Levels</div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(180px,1fr))", gap: 8 }}>
                    {Object.entries(A.tagMap || {}).filter(([t]) => !MISTAKE_TAGS.includes(t) && A.tagMap[t].count >= 2).sort((a, b) => b[1].pnl - a[1].pnl).slice(0, 6).map(([tag, d]) => (
                      <div key={tag} style={{ background: C.insetBg, border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 12px" }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: C.text, marginBottom: 4 }}>{tag}</div>
                        <div style={{ fontSize: 11, color: d.pnl >= 0 ? "#22C55E" : "#EF4444", fontWeight: 700 }}>{d.pnl >= 0 ? "+" : ""}${d.pnl.toFixed(0)}</div>
                        <div style={{ fontSize: 10, color: C.subtext }}>{(d.wins / d.count * 100).toFixed(0)}% WR · {d.count}t</div>
                        <div style={{ height: 3, background: C.border, borderRadius: 2, marginTop: 6, overflow: "hidden" }}><div style={{ height: "100%", width: `${(d.wins / d.count) * 100}%`, background: "#22C55E" }} /></div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {instrumentStats.length > 0 && (
                <div style={{ ...cP, marginBottom: 4 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: C.text, marginBottom: 10 }}>⭐ Best Instruments by Win Rate</div>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    {instrumentStats.filter(i => i.count >= 2).slice(0, 6).map(inst => (
                      <div key={inst.asset} style={{ background: C.insetBg, border: `1px solid ${inst.pnl >= 0 ? "rgba(34,197,94,.2)" : "rgba(239,68,68,.2)"}`, borderRadius: 8, padding: "10px 14px", minWidth: 120 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{inst.asset}</div>
                        <div style={{ fontSize: 16, fontWeight: 700, color: inst.wr >= 50 ? "#22C55E" : "#EF4444", marginTop: 2 }}>{inst.wr.toFixed(0)}%</div>
                        <div style={{ fontSize: 10, color: C.subtext }}>{inst.count} trades · {inst.pnl >= 0 ? "+" : ""}${inst.pnl.toFixed(0)}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="grid2" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                {smartInsights.map((ins, i) => (
                  <div key={i} style={{ background: sevBg[ins.severity], border: `1px solid ${sevBorder[ins.severity]}`, borderRadius: 10, padding: "14px 16px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                      <span style={{ fontSize: 20 }}>{ins.icon}</span>
                      <span style={{ fontSize: 12, fontWeight: 700, color: sevColor[ins.severity] }}>{ins.title}</span>
                    </div>
                    <div style={{ fontSize: 12, color: C.subtext, lineHeight: 1.6 }}>{ins.body}</div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* ─── NEWS ─── */}
          {tab === "calendar2" && <EconomicCalendar C={C} />}

          {/* ─── RULES ─── */}
          {tab === "rules" && <RulesPage C={C} propRules={propRules || []} propHistory={propRulesHistory || {}} onSaveRules={onSaveRules} onSaveRulesHistory={onSaveRulesHistory} />}

        </div>
      </main>

      {modal && <TradeModal initial={editT} onClose={() => { setModal(false); setEditT(null); }} onSave={save} savedTags={savedTags} onSaveTags={onSaveTags} beCap={beCap} ratingType={ratingType} propRulesInModal={propRules || []} />}
            {showImport && <ImportModal existing={trades} onSaveTrade={onSaveTrade} onClose={() => setShowImport(false)} onDone={n => { setShowImport(false); setTab("trades"); alert(`Imported ${n} trades`); }} />}
      {showSummary && <SummaryModal trades={trades} startKey={`${calM.getFullYear()}-${String(calM.getMonth() + 1).padStart(2, "0")}`} onClose={() => setShowSummary(false)} />}
      {showSettings && <SettingsModal startBal={startBal} userName={userName} beCap={beCap} ratingType={ratingType} theme={propSettings?.theme} onSave={(newSettings) => { onSaveSettings(newSettings); }} onClose={() => setShowSettings(false)} />}
      {calPop && <CalDayPopover  day={calPop} trades={calPop.trades} onClose={() => setCalPop(null)} onEdit={t => { setCalPop(null); setEditT(t); setModal(true); }} onDelete={id => { del(id); setCalPop(prev => { const updated = prev.trades.filter(t => t.id !== id); return updated.length ? { ...prev, trades: updated } : null; }); }} />}
    </div>
  </>);
}